import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './App.css';
import FuzzyText from './components/FuzzyText';
import LaserFlow from './LaserFlow';
import FlowingMenu from './components/FlowingMenu';
import HamburgerMenu from './components/HamburgerMenu';
import AnimatedTestimonials from './components/AnimatedTestimonials';
import FAQ from './components/FAQ';
import SiteFooter from './components/SiteFooter';
import config from './config';
import DropInFlow from './components/DropInFlow';
import TournamentFlow from './components/TournamentFlow';
import OVRPlus from './components/OVRPlus';
import PaymentSuccess from './components/PaymentSuccess';
import AdminMfaGate from './components/AdminMfaGate';
import DropInRosterExport from './components/DropInRosterExport';
import BlitzInfoModal from './components/BlitzInfoModal';
import OvrCupInfoModal, { CUP_INFO } from './components/OvrCupInfoModal';
import Reveal from './components/Reveal';
import Scoreboard from './components/Scoreboard';
import { useAuth } from './AuthContext';
import { useAuthModal } from './AuthModalContext';
import { useCart } from './CartContext';
import { supabase } from './supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';

function merchOrderCode(id) {
  return 'OVR-M' + String(id).replace(/-/g, '').slice(0, 6).toUpperCase();
}

function hasFaDraft() {
  try {
    const d = JSON.parse(localStorage.getItem('ovrRegDraft') || 'null');
    return !!(d && d.step !== 'done' && (d.team?.players || []).some(p => p.fa_id));
  } catch (e) { return false; }
}

function FaWarnModal({ onStay, onLeave }) {
  return ReactDOM.createPortal(
    <div className="fa-warn-overlay" onClick={onStay}>
      <div className="fa-warn-card" onClick={e => e.stopPropagation()}>
        <div className="fa-warn-eyebrow">FREE AGENT NOT CONFIRMED</div>
        <div className="fa-warn-title">HOLD UP</div>
        <p className="fa-warn-body">
          You have a free agent on your roster. They are not confirmed until you complete registration and pay. Leaving now means their spot is not secured.
        </p>
        <div className="fa-warn-actions">
          <button className="fa-warn-primary" onClick={onStay}>STAY &amp; COMPLETE →</button>
          <button className="fa-warn-secondary" onClick={onLeave}>LEAVE ANYWAY</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ReceiptModal({ item, onClose, onRequestCancel }) {
  const levelLabel = { adv: 'Advanced', int: 'Intermediate', beg: 'Beginner' };
  const typeFullLabel = { dropin: 'Drop-In Session', team: 'Team Registration', fa: 'Free Agent Sign-Up', merch: 'Merch Order' };

  const [cancelledFaIds, setCancelledFaIds] = React.useState(new Set());

  // Force repaint after print dialog closes to clear any white-bar residue
  React.useEffect(() => {
    const flush = () => {
      document.body.style.display = 'none';
      // eslint-disable-next-line no-unused-expressions
      document.body.offsetHeight;
      document.body.style.display = '';
    };
    window.addEventListener('afterprint', flush);
    return () => window.removeEventListener('afterprint', flush);
  }, []);

  // Filter cancelled FAs from the player list on receipt open
  React.useEffect(() => {
    if (item._type !== 'team') return;
    const faIds = (Array.isArray(item.players) ? item.players : [])
      .filter(p => p && p.fa_id)
      .map(p => p.fa_id);
    if (faIds.length === 0) return;
    supabase
      .from('tournament_free_agents')
      .select('id, payment_status')
      .in('id', faIds)
      .then(({ data }) => {
        if (!data) return;
        const cancelled = new Set(
          data
            .filter(f => f.payment_status === 'user_cancelled' || f.payment_status === 'admin_removed')
            .map(f => f.id)
        );
        if (cancelled.size > 0) setCancelledFaIds(cancelled);
      });
  }, [item]);

  const paid = item._type === 'merch'
    ? (item.status === 'paid' || item.status === 'ready')
    : (item.payment_status === 'paid' || item.paid === true);

  const title = item._type === 'dropin' ? (item.session_label || 'Drop-In Session')
              : item._type === 'team'   ? (item.team_name || item.captain_name || 'Tournament Team')
              : item._type === 'merch'  ? 'Merch Order'
              : (item.tournament || 'Free Agent');

  const rows = [];
  if (item._type === 'dropin') {
    rows.push(['Registered Name', item.user_name || '-']);
    rows.push(['Email',           item.user_email || '-']);
    rows.push(['Session',         item.session_label || '-']);
    rows.push(['Date / Time',     item.session_time || '-']);
    rows.push(['Location',        item.session_location || '-']);
    rows.push(['Level',           levelLabel[item.level] || item.level || '-']);
    rows.push(['Position',        item.position || '-']);
  } else if (item._type === 'team') {
    rows.push(['Team Name',       item.team_name || '-']);
    rows.push(['Captain',         item.captain_name || '-']);
    rows.push(['Captain Email',   item.captain_email || '-']);
    rows.push(['Phone',           item.phone || '-']);
    rows.push(['Tournament',      item.season || '-']);
    rows.push(['Division',        item.division || '-']);
    const activePlayers = (Array.isArray(item.players) ? item.players : [])
      .filter(p => !p.fa_id || !cancelledFaIds.has(p.fa_id));
    rows.push(['Players', activePlayers.length ? `${activePlayers.length} player${activePlayers.length !== 1 ? 's' : ''}` : '-']);
    activePlayers.forEach((p, i) => {
      const name = typeof p === 'string' ? p : [p.firstName, p.lastName].filter(Boolean).join(' ') || p.name || '-';
      const pos  = typeof p === 'object' ? (p.position || '') : '';
      rows.push([`  ${i + 1}.`, pos ? `${name}  (${pos})` : name]);
    });
  } else if (item._type === 'merch') {
    rows.push(['Name',            item.user_name || '-']);
    rows.push(['Email',           item.user_email || '-']);
    rows.push(['Status', item.status === 'ready' ? 'Ready for pickup' : item.status === 'paid' ? 'Paid — awaiting pickup' : (item.status || '-')]);
    rows.push(['Pickup', 'Plant Recreation Centre · during league play']);
  } else {
    rows.push(['Name',            item.name || '-']);
    rows.push(['Email',           item.email || '-']);
    rows.push(['Tournament',      item.tournament || '-']);
    rows.push(['Position',        item.position || '-']);
    rows.push(['Gender',          item.gender || '-']);
  }
  rows.push([item._type === 'merch' ? 'Ordered' : 'Registered',  new Date(item.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })]);
  if (item.amount_paid_cents) rows.push(['Total Paid', `$${(item.amount_paid_cents / 100).toFixed(2)} CAD`]);
  rows.push(['Payment',     paid ? 'Paid via Stripe' : 'Pending']);

  return ReactDOM.createPortal(
    <div className="receipt-overlay" onClick={onClose}>
      <div className="receipt-panel" onClick={e => e.stopPropagation()}>

        {/* Sticky action bar */}
        <div className="receipt-action-bar no-print">
          <button className="receipt-action-bar__print" onClick={() => window.print()}>
            ↓ SAVE AS PDF / PRINT
          </button>
          {onRequestCancel && (item._type === 'team' || item._type === 'fa' || item._type === 'dropin') && item.payment_status === 'paid' && (
            <button
              onClick={() => onRequestCancel(item)}
              style={{
                background: 'transparent', border: '1px solid rgba(157,21,21,0.45)',
                color: 'rgba(224,85,85,0.85)', fontFamily: "'Bebas Neue','Oswald',sans-serif",
                fontSize: 12, letterSpacing: '0.14em', padding: '8px 14px', cursor: 'pointer',
                transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#9D1515'; e.currentTarget.style.color = '#e05555'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(157,21,21,0.45)'; e.currentTarget.style.color = 'rgba(224,85,85,0.85)'; }}
            >
              CANCEL REGISTRATION
            </button>
          )}
          <button className="receipt-action-bar__close" onClick={onClose}>✕ CLOSE</button>
        </div>

        <div className="receipt-doc" id="receipt-printable">
          {/* Header */}
          <div className="receipt-doc__header">
            <div className="receipt-doc__header-top">
              <img
                src={'/ovr-logo.png'}
                alt="OVR"
                className="receipt-doc__logo"
              />
              <div>
                <div className="receipt-doc__org">Ottawa Volleyball Revival</div>
                <div className="receipt-doc__title">{item._type === 'merch' ? 'ORDER RECEIPT' : 'REGISTRATION RECEIPT'}</div>
              </div>
            </div>
          </div>

          {/* Conf # hero */}
          <div className="receipt-doc__conf-block">
            <div className="receipt-doc__conf-label">{item._type === 'merch' ? 'ORDER NUMBER' : 'CONFIRMATION NUMBER'}</div>
            <div className="receipt-doc__conf-num">{item.confirmation_number || 'NOT ASSIGNED'}</div>
            <div className="receipt-doc__conf-hint">{item._type === 'merch' ? 'Give this number to pick up your order' : 'Present this number at check-in'}</div>
          </div>

          {/* Type + title */}
          <div className="receipt-doc__event-row">
            <span className="receipt-doc__event-type">{typeFullLabel[item._type]}</span>
            <span className="receipt-doc__event-name">{title}</span>
          </div>

          {/* Merch item photos */}
          {item._type === 'merch' && Array.isArray(item.items) && item.items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 18px' }}>
              {item.items.map((it, i) => {
                const img = merchItemImage(it.id);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#131313', border: '1px solid #2a2a2a', padding: '10px 14px' }}>
                    {img ? (
                      <img src={img} alt={it.name || it.id} style={{ width: 56, height: 56, objectFit: 'cover', background: '#1a1a1a', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 56, height: 56, background: '#1a1a1a', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>{it.name || it.id}</div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#8a8a8a', letterSpacing: '0.06em', marginTop: 2 }}>
                        {it.size ? `SIZE ${String(it.size).toUpperCase()}` : ''}{it.qty ? ` · QTY ${it.qty}` : ''}
                      </div>
                    </div>
                    {it.price != null && <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 18, color: '#fff' }}>${Number(it.price).toFixed(2)}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Detail rows */}
          <div className="receipt-doc__rows">
            {rows.map(([label, val], i) => (
              <div key={i} className={`receipt-doc__row${label.startsWith('  ') ? ' receipt-doc__row--indent' : ''}`}>
                <span className="receipt-doc__row-label">{label.trim()}</span>
                <span className="receipt-doc__row-val">{val}</span>
              </div>
            ))}
          </div>

          {/* FA receipt note — clarify this is the FA's own purchase, not the team's */}
          {item._type === 'fa' && (
            <div style={{ margin: '16px 0 0', padding: '10px 14px', background: 'rgba(107,214,163,0.05)', borderLeft: '2px solid rgba(107,214,163,0.35)', fontSize: 11, color: '#6bd6a3', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.06em', lineHeight: 1.6 }}>
              This is your personal free agent registration receipt. Your team captain's payment is separate. You are not responsible for it.
            </div>
          )}

          {/* Stripe receipt links */}
          {item._type === 'fa' && item.stripe_receipt_url && (
            <div className="receipt-doc__stripe-link no-print">
              <a href={item.stripe_receipt_url} target="_blank" rel="noopener noreferrer">
                VIEW YOUR FA PAYMENT RECEIPT →
              </a>
            </div>
          )}
          {item._type === 'team' && item.stripe_receipt_url && (
            <div className="receipt-doc__stripe-link no-print">
              <a href={item.stripe_receipt_url} target="_blank" rel="noopener noreferrer">
                RECEIPT 1 · TEAM REGISTRATION →
              </a>
            </div>
          )}
          {item._type === 'team' && Array.isArray(item.stripe_roster_receipt_urls) && item.stripe_roster_receipt_urls.map((url, i) => (
            <div key={i} className="receipt-doc__stripe-link no-print">
              <a href={url} target="_blank" rel="noopener noreferrer">
                RECEIPT {i + 2} · ADDITIONAL PLAYERS →
              </a>
            </div>
          ))}
          {item._type === 'dropin' && item.stripe_receipt_url && (
            <div className="receipt-doc__stripe-link no-print">
              <a href={item.stripe_receipt_url} target="_blank" rel="noopener noreferrer">
                VIEW STRIPE PAYMENT RECEIPT →
              </a>
            </div>
          )}

          {/* Footer */}
          <div className="receipt-doc__footer">
            <div>OVR League · Ottawa, ON</div>
            <div>@ovrleague · @horizoneventsofficial</div>
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
}

const ABOUT_STATS = [
  { target: 8,    suffix: '+',  label: 'Events Hosted',         prefix: '' },
  { target: 1000, suffix: '+',  label: 'Players Connected',     prefix: '' },
  { target: 25,   suffix: '',   label: 'Sponsors & Partners',   prefix: '' },
  { target: 5000, suffix: '+',  label: 'in Prizes & Giveaways', prefix: '$' },
];

function AboutStats({ scrollRef }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [counts, setCounts] = useState(ABOUT_STATS.map(() => 0));

  useEffect(() => {
    const el = ref.current;
    const root = scrollRef?.current ?? null;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.05, root }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollRef]);

  useEffect(() => {
    if (!started) return;
    const duration = 1600;
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setCounts(ABOUT_STATS.map(s => Math.round(s.target * ease)));
      if (t < 1) { raf = requestAnimationFrame(tick); }
      else { setCounts(ABOUT_STATS.map(s => s.target)); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started]);

  return (
    <div ref={ref} className="about-stats">
      {ABOUT_STATS.map((s, i) => (
        <div key={i} className="about-stat-row">
          <span className="about-stat-num">{s.prefix}{counts[i].toLocaleString()}{s.suffix}</span>
          <span className="about-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

const TOURNAMENT_REGISTRATIONS_OPEN = true;
const DROPIN_REGISTRATIONS_OPEN = true;
const STATUS_COLORS = { open: '#6bd6a3', draft: '#555', closed: '#d8a64a', completed: '#9D1515' };
const INPUT_STYLE = { background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#e8e8e8', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 10px', outline: 'none', width: '100%', boxSizing: 'border-box' };
const BTN_SM = (extra = {}) => ({ background: 'transparent', border: '1px solid #2a2a2a', color: '#e8e8e8', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em', padding: '6px 14px', cursor: 'pointer', ...extra });

// Defined outside AdminScoreboardTab so React doesn't remount it on every keystroke
function SbScoreField({ label, value, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#555', letterSpacing: '0.14em', marginBottom: 4, textTransform: 'uppercase' }}>{label}</div>
      <input
        style={{ background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#e8e8e8', outline: 'none', width: '100%', boxSizing: 'border-box', fontSize: 28, textAlign: 'center', fontFamily: "'Bebas Neue','Oswald',sans-serif", padding: '10px 8px', letterSpacing: '0.05em' }}
        type="number" min="0" value={value} onChange={onChange}
      />
    </div>
  );
}

function AdminEventsTab({ sessions, seasons, onRefresh, supabase }) {
  const [section, setSection] = React.useState('sessions');
  const [saving, setSaving] = React.useState(false);
  const [editSession, setEditSession] = React.useState(null);
  const [newSession, setNewSession] = React.useState(null);
  const [editSeason, setEditSeason] = React.useState(null);
  const [newSeason, setNewSeason] = React.useState(null);

  // Convert stored ISO → datetime-local value ("YYYY-MM-DDTHH:MM" in browser local time)
  const toLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  // Convert datetime-local → ISO string for storage
  const fromLocal = (v) => v ? new Date(v).toISOString() : null;

  const fmtLocal = (iso) => iso ? new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '';

  const OvrError = ({ msg }) => msg ? (
    <div style={{ background: 'rgba(157,21,21,0.12)', border: '1px solid rgba(157,21,21,0.4)', borderLeft: '3px solid #9D1515', padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#9D1515', letterSpacing: '0.14em', fontWeight: 700 }}>ERROR</span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#e8e8e8', letterSpacing: '0.06em' }}>{msg}</span>
    </div>
  ) : null;

  const Field = ({ label, children, full }) => (
    <div style={full ? { gridColumn: '1/-1' } : {}}>
      <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#555', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );

  const saveSession = async (sess, setErr) => {
    if (!sess.starts_at) { setErr('DATE & TIME is required.'); return; }
    if (!sess.location?.trim()) { setErr('LOCATION is required.'); return; }
    setSaving(true);
    setErr('');

    const isoStartsAt = sess.starts_at.length === 16 ? fromLocal(sess.starts_at) : sess.starts_at;
    const d = new Date(isoStartsAt);
    const pad = n => String(n).padStart(2, '0');
    const DAY_NAMES = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
    const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    const h = d.getHours(), m = pad(d.getMinutes());
    const h12 = h % 12 || 12, ampm = h < 12 ? 'AM' : 'PM';
    const duration = parseInt(sess.duration_hours) || 2;
    const endD = new Date(d.getTime() + duration * 3600000);
    const eH = endD.getHours() % 12 || 12, eM = pad(endD.getMinutes()), eAmpm = endD.getHours() < 12 ? 'AM' : 'PM';
    const timeDisplay = `${h12}:${m} ${ampm} – ${eH}:${eM} ${eAmpm}`;

    const autoId = `${sess.level || 'beg'}-${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}-${pad(h)}`;
    const sessionId = sess.id?.trim() || autoId;

    const priceCents = Math.round((parseFloat(sess.price_cad) || 12) * 100);

    const payload = {
      id: sessionId,
      level: sess.level || 'beg',
      starts_at: isoStartsAt,
      location: sess.location.trim(),
      address: sess.address?.trim() || '',
      day: DAY_NAMES[d.getDay()],
      date: `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`,
      month: `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`,
      time: timeDisplay,
      price_cents: Math.max(500, Math.min(50000, priceCents)),
      status: sess.status || 'open',
    };

    const isExisting = sess.id?.trim() && sessions.some(s => s.id === sess.id.trim());
    const { id, ...fields } = payload;
    const { error } = isExisting
      ? await supabase.from('drop_in_sessions').update(fields).eq('id', id)
      : await supabase.from('drop_in_sessions').insert({ id, ...fields });
    if (error) { setErr('Save failed: ' + error.message); setSaving(false); return; }
    await onRefresh();
    setEditSession(null);
    setNewSession(null);
    setSaving(false);
  };

  const FORMAT_OPTIONS = {
    '4-teams':  '4 Teams · 4-Hour Block',
    '8-teams':  '8 Teams · 8-Hour Block',
    '10-teams': '10 Teams · 8-Hour Block',
  };
  const formatKeyFromValue = (v) => Object.entries(FORMAT_OPTIONS).find(([, lbl]) => lbl === v)?.[0] || '4-teams';

  const saveSeason = async (sea, setErr) => {
    if (!sea.id?.trim()) { setErr('SEASON ID is required (e.g. szn8).'); return; }
    if (!sea.name?.trim()) { setErr('NAME is required.'); return; }
    const payload = {
      id: sea.id.trim(),
      name: sea.name.trim(),
      event_date: sea.event_date ? (sea.event_date.length === 16 ? fromLocal(sea.event_date) : sea.event_date) : null,
      format: FORMAT_OPTIONS[sea.format_key || '4-teams'] || FORMAT_OPTIONS['4-teams'],
      description: sea.description?.trim() || '',
      per_player_cents: Math.round((parseFloat(sea.per_player_cad) || 25) * 100),
      fa_fee_cents: Math.round((parseFloat(sea.fa_fee_cad) || 25) * 100),
      team_refund_cutoff_days: 5,
      fa_refund_cutoff_days: 2,
      status: sea.status || 'draft',
      coming_soon: sea.coming_soon ?? (sea.status !== 'open'),
    };
    setSaving(true);
    setErr('');
    const { id, ...fields } = payload;
    const { error } = seasons.some(s => s.id === id)
      ? await supabase.from('seasons').update(fields).eq('id', id)
      : await supabase.from('seasons').insert({ id, ...fields });
    if (error) { setErr('Save failed: ' + error.message); setSaving(false); return; }
    await onRefresh();
    setEditSeason(null);
    setNewSeason(null);
    setSaving(false);
  };

  const toggleSessionStatus = async (sess) => {
    const next = sess.status === 'open' ? 'closed' : 'open';
    await supabase.from('drop_in_sessions').update({ status: next }).eq('id', sess.id);
    onRefresh();
  };

  const toggleSeasonStatus = async (sea) => {
    const next = sea.status === 'open' ? 'closed' : 'open';
    await supabase.from('seasons').update({ status: next, coming_soon: next !== 'open' }).eq('id', sea.id);
    onRefresh();
  };

  const SessionForm = ({ data, onSave, onCancel }) => {
    const isNew = !data.id;
    const [f, setF] = React.useState({
      ...data,
      starts_at: toLocal(data.starts_at),
      duration_hours: data.duration_hours || 2,
      price_cad: data.price_cents ? String(Math.round(data.price_cents / 100)) : '12',
    });
    const [err, setErr] = React.useState('');
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    return (
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderTop: '2px solid #9D1515', padding: 20, marginBottom: 16 }}>
        <OvrError msg={err} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <Field label="Level">
            <select style={INPUT_STYLE} value={f.level || 'beg'} onChange={e => set('level', e.target.value)}>
              <option value="beg">Beginner</option><option value="int">Intermediate</option><option value="adv">Advanced</option>
            </select>
          </Field>
          <Field label="Status">
            <select style={INPUT_STYLE} value={f.status || 'open'} onChange={e => set('status', e.target.value)}>
              <option value="draft">Draft (hidden)</option><option value="open">Open</option><option value="closed">Closed</option><option value="completed">Completed</option>
            </select>
          </Field>
          <Field label="Date & Time *">
            <input type="datetime-local" style={{ ...INPUT_STYLE, colorScheme: 'dark', ...(err && !f.starts_at ? { borderColor: '#9D1515' } : {}) }} value={f.starts_at || ''} onChange={e => set('starts_at', e.target.value)} />
          </Field>
          <Field label="Duration">
            <select style={INPUT_STYLE} value={f.duration_hours || 2} onChange={e => set('duration_hours', e.target.value)}>
              <option value="1">1 hour</option><option value="2">2 hours</option><option value="3">3 hours</option><option value="4">4 hours</option>
            </select>
          </Field>
          <Field label="Location *" full><input style={{ ...INPUT_STYLE, ...(err && !f.location ? { borderColor: '#9D1515' } : {}) }} value={f.location || ''} onChange={e => set('location', e.target.value)} placeholder="Plant Recreation Centre" /></Field>
          <Field label="Address" full><input style={INPUT_STYLE} value={f.address || ''} onChange={e => set('address', e.target.value)} placeholder="930 Somerset St W · Court A" /></Field>
          <Field label="Price (CAD $)"><input style={INPUT_STYLE} type="number" min="5" max="100" value={f.price_cad || '12'} onChange={e => set('price_cad', e.target.value)} /></Field>
          {!isNew && <Field label="Session ID (auto)"><div style={{ ...INPUT_STYLE, color: '#555', userSelect: 'all' }}>{f.id}</div></Field>}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} disabled={saving} onClick={() => onSave(f, setErr)}>{saving ? 'SAVING…' : 'SAVE SESSION'}</button>
          <button style={BTN_SM()} onClick={onCancel}>CANCEL</button>
        </div>
      </div>
    );
  };

  const SeasonForm = ({ data, onSave, onCancel }) => {
    const isNew = !data.id;
    const [f, setF] = React.useState({
      ...data,
      event_date: toLocal(data.event_date),
      format_key: formatKeyFromValue(data.format) || '4-teams',
      per_player_cad: data.per_player_cents ? String(Math.round(data.per_player_cents / 100)) : '25',
      fa_fee_cad: data.fa_fee_cents ? String(Math.round(data.fa_fee_cents / 100)) : '25',
    });
    const [err, setErr] = React.useState('');
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    return (
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderTop: '2px solid #d8a64a', padding: 20, marginBottom: 16 }}>
        <OvrError msg={err} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <Field label="Season ID * (no spaces)">
            <input style={{ ...INPUT_STYLE, ...(err && !f.id ? { borderColor: '#9D1515' } : {}) }} value={f.id || ''} onChange={e => set('id', e.target.value)} placeholder="szn8" disabled={!isNew} />
          </Field>
          <Field label="Name *">
            <input style={{ ...INPUT_STYLE, ...(err && !f.name ? { borderColor: '#9D1515' } : {}) }} value={f.name || ''} onChange={e => set('name', e.target.value)} placeholder="OVR CUP SZN VIII" />
          </Field>
          <Field label="Event Date & Time (blank = TBD)" full>
            <input type="datetime-local" style={{ ...INPUT_STYLE, colorScheme: 'dark' }} value={f.event_date || ''} onChange={e => set('event_date', e.target.value || '')} />
          </Field>
          <Field label="Format">
            <select style={INPUT_STYLE} value={f.format_key || '4-teams'} onChange={e => set('format_key', e.target.value)}>
              <option value="4-teams">4 Teams · 4 Hours</option>
              <option value="8-teams">8 Teams · 8 Hours</option>
              <option value="10-teams">10 Teams · 8 Hours</option>
            </select>
          </Field>
          <Field label="Status">
            <select style={INPUT_STYLE} value={f.status || 'draft'} onChange={e => set('status', e.target.value)}>
              <option value="draft">Draft (hidden)</option>
              <option value="open">Open (registration live)</option>
              <option value="closed">Closed</option>
              <option value="completed">Completed</option>
            </select>
          </Field>
          <Field label="Price / Player (CAD $)">
            <input style={INPUT_STYLE} type="number" min="1" value={f.per_player_cad || '25'} onChange={e => set('per_player_cad', e.target.value)} />
          </Field>
          <Field label="FA Fee (CAD $)">
            <input style={INPUT_STYLE} type="number" min="1" value={f.fa_fee_cad || '25'} onChange={e => set('fa_fee_cad', e.target.value)} />
          </Field>
          <Field label="Description" full>
            <input style={INPUT_STYLE} value={f.description || ''} onChange={e => set('description', e.target.value)} placeholder="Full-day cup tournament. Bracket play, awards ceremony." />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} disabled={saving} onClick={() => onSave(f, setErr)}>{saving ? 'SAVING…' : 'SAVE SEASON'}</button>
          <button style={BTN_SM()} onClick={onCancel}>CANCEL</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #1e1e1e', marginBottom: 24 }}>
        {[['sessions', 'DROP-IN SESSIONS'], ['seasons', 'TOURNAMENT SEASONS']].map(([key, label]) => (
          <button key={key} onClick={() => setSection(key)} style={{ background: 'transparent', border: 'none', borderBottom: section === key ? '2px solid #9D1515' : '2px solid transparent', color: section === key ? '#fff' : '#555', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 16, letterSpacing: '0.08em', padding: '8px 18px', cursor: 'pointer' }}>{label}</button>
        ))}
      </div>

      {section === 'sessions' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, letterSpacing: '0.08em', color: '#fff' }}>DROP-IN SESSIONS ({sessions.length})</div>
            <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} onClick={() => { setNewSession({ id: '', level: 'beg', starts_at: '', duration_hours: 2, price_cad: '12', location: '', address: '', status: 'open' }); setEditSession(null); }}>+ NEW SESSION</button>
          </div>
          {newSession && <SessionForm data={newSession} onSave={saveSession} onCancel={() => setNewSession(null)} />}
          {sessions.map(s => (
            <div key={s.id}>
              {editSession?.id === s.id
                ? <SessionForm data={editSession} onSave={saveSession} onCancel={() => setEditSession(null)} />
                : (
                  <div style={{ background: '#111', border: '1px solid #1e1e1e', padding: '14px 18px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#555', minWidth: 44 }}>{s.id}</span>
                    <span style={{ background: STATUS_COLORS[s.status] || '#555', color: '#000', fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', padding: '2px 7px', borderRadius: 2 }}>{(s.status || 'open').toUpperCase()}</span>
                    <span style={{ fontSize: 12, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: 70 }}>{s.level}</span>
                    <span style={{ fontSize: 12, color: '#e8e8e8', flex: 1 }}>{s.month} · {s.time}</span>
                    <span style={{ fontSize: 12, color: '#8a8a8a' }}>{s.location}</span>
                    <span style={{ fontSize: 11, color: '#555', fontFamily: "'JetBrains Mono',monospace" }}>{fmtLocal(s.starts_at)}</span>
                    <button style={BTN_SM()} onClick={() => toggleSessionStatus(s)}>{s.status === 'open' ? 'CLOSE' : 'OPEN'}</button>
                    <button style={BTN_SM()} onClick={() => { setEditSession({ ...s }); setNewSession(null); }}>EDIT</button>
                  </div>
                )
              }
            </div>
          ))}
          {sessions.length === 0 && !newSession && <div style={{ color: '#333', padding: 40, textAlign: 'center', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22 }}>NO SESSIONS · ADD ONE ABOVE</div>}
        </div>
      )}

      {section === 'seasons' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, letterSpacing: '0.08em', color: '#fff' }}>TOURNAMENT SEASONS ({seasons.length})</div>
            <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} onClick={() => { setNewSeason({ id: '', name: '', event_date: null, format: '', description: '', status: 'draft', coming_soon: true }); setEditSeason(null); }}>+ NEW SEASON</button>
          </div>
          {newSeason && <SeasonForm data={newSeason} onSave={saveSeason} onCancel={() => setNewSeason(null)} />}
          {seasons.map(s => (
            <div key={s.id}>
              {editSeason?.id === s.id
                ? <SeasonForm data={editSeason} onSave={saveSeason} onCancel={() => setEditSeason(null)} />
                : (
                  <div style={{ background: '#111', border: '1px solid #1e1e1e', padding: '14px 18px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#555', minWidth: 44 }}>{s.id}</span>
                    <span style={{ background: STATUS_COLORS[s.status] || '#555', color: '#000', fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', padding: '2px 7px', borderRadius: 2 }}>{(s.status || 'draft').toUpperCase()}</span>
                    <span style={{ fontSize: 13, color: '#e8e8e8', flex: 1 }}>{s.name}</span>
                    <span style={{ fontSize: 12, color: '#8a8a8a' }}>${(s.per_player_cents / 100).toFixed(0)}/player · FA ${(s.fa_fee_cents / 100).toFixed(0)}</span>
                    <span style={{ fontSize: 11, color: '#555', fontFamily: "'JetBrains Mono',monospace" }}>{s.event_date ? fmtLocal(s.event_date) + ' · team ' + s.team_refund_cutoff_days + 'd / FA ' + s.fa_refund_cutoff_days + 'd' : 'DATE TBD'}</span>
                    <button style={BTN_SM()} onClick={() => toggleSeasonStatus(s)}>{s.status === 'open' ? 'CLOSE' : 'OPEN'}</button>
                    <button style={BTN_SM()} onClick={() => { setEditSeason({ ...s }); setNewSeason(null); }}>EDIT</button>
                  </div>
                )
              }
            </div>
          ))}
          {seasons.length === 0 && !newSeason && <div style={{ color: '#333', padding: 40, textAlign: 'center', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22 }}>NO SEASONS · ADD ONE ABOVE</div>}
        </div>
      )}
    </div>
  );
}

function AdminScoreboardTab({ seasons, supabase, onRefresh }) {
  const [tournId, setTournId] = React.useState('');
  const [tournData, setTournData] = React.useState(null);
  const [registrations, setRegistrations] = React.useState([]);
  const [matches, setMatches] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [archiveMode, setArchiveMode] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [scoreInputs, setScoreInputs] = React.useState({ sets: [], status: 'completed' });
  const [err, setErr] = React.useState('');
  const [sending, setSending] = React.useState('');

  const STAGE_LABELS = { pool: 'Pool Play', quarter: 'Quarter Finals', semi: 'Semi Finals', bronze: 'Bronze Match', final: 'Championship' };
  const SETS_FOR_STAGE = { pool: 1, quarter: 1, semi: 3, bronze: 3, final: 3 };
  const STAGE_ORDER = ['pool', 'quarter', 'semi', 'bronze', 'final'];

  const loadData = React.useCallback(async (tid) => {
    if (!tid) return;
    setLoading(true);
    const [{ data: seas }, { data: regs }, { data: ms }] = await Promise.all([
      supabase.from('seasons').select('*').eq('id', tid).maybeSingle(),
      supabase.from('tournament_registrations').select('id, team_name, captain_name, num_players').eq('season', tid).eq('payment_status', 'paid'),
      supabase.from('tournament_matches').select('*').eq('tournament_id', tid).order('match_number'),
    ]);
    setTournData(seas);
    setRegistrations(regs || []);
    setMatches(ms || []);
    setLoading(false);
  }, [supabase]);

  React.useEffect(() => {
    if (tournId) { loadData(tournId); }
    else { setTournData(null); setRegistrations([]); setMatches([]); }
    setEditId(null); setErr('');
  }, [tournId, loadData]);

  const reload = React.useCallback(() => loadData(tournId), [tournId, loadData]);

  // --- Email the game-day schedule to captains (or a test to yourself) ---
  const sendSchedule = async (testOnly) => {
    let testTeam;
    if (testOnly) {
      const t = window.prompt('Which team do you want to preview? (e.g. Alpha Q)', 'Alpha Q');
      if (t === null) return;
      testTeam = t.trim() || undefined;
    }
    if (!testOnly && !window.confirm(`Send the game-day schedule email to all ${registrations.length} captains for "${tournData?.name || tournId}"? This emails real people and cannot be undone.`)) return;
    setSending(testOnly ? 'test' : 'all'); setErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-schedule-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}`, apikey: process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({ tournamentId: tournId, testEmail: testOnly ? (session?.user?.email || '') : undefined, testTeam }),
      });
      const j = await res.json();
      if (!res.ok || j.error) throw new Error(j.error || `Failed (${res.status})`);
      window.alert(testOnly
        ? `Test email sent to ${j.testEmail} (sample team: ${j.sampleTeam}). Check it, then use "Email all captains" when ready.`
        : `Done — sent to ${j.sent} of ${j.total} captains${j.failed ? `, ${j.failed} failed` : ''}.`);
    } catch (e) {
      setErr('Schedule email failed: ' + e.message);
    } finally { setSending(''); }
  };

  // Compute standings from completed pool matches (for playoff seeding)
  const computeAdminStandings = (ms) => {
    const pools = {};
    for (const m of ms) {
      if (m.stage !== 'pool' || m.status !== 'completed') continue;
      const pool = m.pool || 'ALL';
      if (!pools[pool]) pools[pool] = {};
      for (const name of [m.team1_name, m.team2_name]) {
        if (name && name !== 'TBD' && !pools[pool][name])
          pools[pool][name] = { name, pts: 0, gp: 0, w: 0, l: 0, diff: 0 };
      }
      const s = (m.sets || [])[0]; if (!s) continue;
      const t1 = pools[pool][m.team1_name], t2 = pools[pool][m.team2_name];
      if (!t1 || !t2) continue;
      t1.gp++; t2.gp++;
      t1.diff += (s.t1 || 0) - (s.t2 || 0); t2.diff += (s.t2 || 0) - (s.t1 || 0);
      if (s.t1 > s.t2) { t1.pts += 2; t1.w++; t2.l++; }
      else if (s.t2 > s.t1) { t2.pts += 2; t2.w++; t1.l++; }
      else { t1.pts++; t2.pts++; }
    }
    const result = {};
    for (const [pool, teams] of Object.entries(pools))
      result[pool] = Object.values(teams).sort((a, b) => b.pts - a.pts || b.w - a.w || b.diff - a.diff);
    return result;
  };

  const matchWinner = (match, sets) => {
    const toWin = Math.ceil((SETS_FOR_STAGE[match.stage] || 1) / 2);
    let t1 = 0, t2 = 0;
    for (const s of sets) { if (s.t1 > s.t2) t1++; else if (s.t2 > s.t1) t2++; }
    if (t1 >= toWin) return match.team1_name;
    if (t2 >= toWin) return match.team2_name;
    return null;
  };

  // --- Generate Schedule ---
  const generateSchedule = async () => {
    if (matches.length > 0 && !window.confirm('This will delete all existing matches and regenerate. Continue?')) return;
    setGenerating(true); setErr('');
    // Always wipe this tournament's matches first (not just when local state shows
    // some) so rapid/repeat clicks can't pile up duplicate schedules.
    await supabase.from('tournament_matches').delete().eq('tournament_id', tournId);

    const format = tournData?.format || '';
    const isBlitz = tournId === 'blitz';
    let numPools = registrations.length <= 5 ? 1 : 2;
    if (format.includes('4 Teams')) numPools = 1;
    else if (format.includes('8 Teams') || format.includes('10 Teams')) numPools = 2;

    // Shuffle teams
    const teamNames = [...registrations].sort(() => Math.random() - 0.5)
      .map(r => r.team_name?.trim() || r.captain_name?.trim() || `Team ${r.id.slice(0,4).toUpperCase()}`);

    // Distribute evenly across pools
    const pools = Array.from({ length: numPools }, (_, pi) => teamNames.filter((_, j) => j % numPools === pi));

    const toInsert = [];
    let matchNum = 1;

    // Round-robin within each pool, greedy-ordered so no team plays back-to-back,
    // then INTERLEAVED across pools (A, B, A, B…) since both courts run in parallel.
    const poolOrdered = pools.map((poolTeams, pi) => {
      const poolLabel = isBlitz ? null : String.fromCharCode(65 + pi); // null for Blitz, A/B/C for cup
      const pairs = [];
      for (let i = 0; i < poolTeams.length; i++)
        for (let j = i + 1; j < poolTeams.length; j++)
          pairs.push([poolTeams[i], poolTeams[j]]);
      // Greedy reorder: each next game avoids both teams from the previous game.
      const ordered = [];
      const remaining = [...pairs];
      let last = new Set();
      while (remaining.length) {
        const prev = last;
        let idx = remaining.findIndex(p => !prev.has(p[0]) && !prev.has(p[1]));
        if (idx === -1) idx = 0; // unavoidable near the end
        ordered.push(remaining.splice(idx, 1)[0]);
        last = new Set(ordered[ordered.length - 1]);
      }
      return { poolLabel, ordered };
    });
    // Interleave by time slot: round 1 = Pool A game 1 + Pool B game 1, etc.
    const maxRounds = Math.max(0, ...poolOrdered.map(p => p.ordered.length));
    for (let slot = 0; slot < maxRounds; slot++) {
      for (const { poolLabel, ordered } of poolOrdered) {
        if (slot >= ordered.length) continue;
        const [t1, t2] = ordered[slot];
        toInsert.push({ tournament_id: tournId, match_number: matchNum++, stage: 'pool', pool: poolLabel, team1_name: t1, team2_name: t2, sets: [], status: 'scheduled' });
      }
    }

    // Playoff stubs — QF only if 8+ teams with 2 pools of 4+
    const needsQF = numPools >= 2 && pools.every(p => p.length >= 4) && teamNames.length >= 8;
    if (needsQF) {
      for (let i = 0; i < 4; i++)
        toInsert.push({ tournament_id: tournId, match_number: 100 + i, stage: 'quarter', pool: null, team1_name: 'TBD', team2_name: 'TBD', sets: [], status: 'scheduled' });
    }
    toInsert.push({ tournament_id: tournId, match_number: 200, stage: 'semi', pool: null, team1_name: 'TBD', team2_name: 'TBD', sets: [], status: 'scheduled' });
    toInsert.push({ tournament_id: tournId, match_number: 201, stage: 'semi', pool: null, team1_name: 'TBD', team2_name: 'TBD', sets: [], status: 'scheduled' });
    toInsert.push({ tournament_id: tournId, match_number: 300, stage: 'bronze', pool: null, team1_name: 'TBD', team2_name: 'TBD', sets: [], status: 'scheduled' });
    toInsert.push({ tournament_id: tournId, match_number: 301, stage: 'final', pool: null, team1_name: 'TBD', team2_name: 'TBD', sets: [], status: 'scheduled' });

    const { error } = await supabase.from('tournament_matches').insert(toInsert);
    if (error) { setErr('Generate failed: ' + error.message); setGenerating(false); return; }
    await reload();
    setGenerating(false);
  };

  // --- Fill playoff bracket from pool standings ---
  const fillPlayoffs = async () => {
    const standings = computeAdminStandings(matches);
    const poolKeys = Object.keys(standings).sort();
    const hasQF = matches.some(m => m.stage === 'quarter');
    setSaving(true);

    const updateByNum = async (num, t1, t2) => {
      if (t1 || t2) await supabase.from('tournament_matches').update({ team1_name: t1 || 'TBD', team2_name: t2 || 'TBD' }).eq('tournament_id', tournId).eq('match_number', num);
    };
    const updateByStage = async (stage, t1, t2) => {
      const m = matches.find(x => x.stage === stage);
      if (m) await supabase.from('tournament_matches').update({ team1_name: t1 || 'TBD', team2_name: t2 || 'TBD' }).eq('id', m.id);
    };

    if (poolKeys.length >= 2) {
      const A = standings[poolKeys[0]] || [];
      const B = standings[poolKeys[1]] || [];
      if (hasQF) {
        // Cross-seeding: 1A vs 4B, 2A vs 3B, 1B vs 4A, 2B vs 3A
        await updateByNum(100, A[0]?.name, B[3]?.name || 'TBD');
        await updateByNum(101, A[1]?.name || 'TBD', B[2]?.name || 'TBD');
        await updateByNum(102, B[0]?.name, A[3]?.name || 'TBD');
        await updateByNum(103, B[1]?.name || 'TBD', A[2]?.name || 'TBD');
      } else {
        // No QF: 1A vs 2B, 1B vs 2A in semis
        await updateByNum(200, A[0]?.name, B[1]?.name);
        await updateByNum(201, B[0]?.name, A[1]?.name);
      }
    } else {
      // Single pool (Blitz): 1st vs 2nd in Final, 3rd vs 4th in Bronze
      const pool = standings[poolKeys[0]] || [];
      await updateByStage('final',  pool[0]?.name || 'TBD', pool[1]?.name || 'TBD');
      await updateByStage('bronze', pool[2]?.name || 'TBD', pool[3]?.name || 'TBD');
    }

    await reload();
    setSaving(false);
  };

  // --- End Tournament ---
  const endTournament = async () => {
    if (!window.confirm(`End "${tournData?.name || tournId}"?\n\nThis will close registrations and mark the season as completed. All registration and match data stays saved. You can send review emails separately after.`)) return;
    setSaving(true); setErr('');
    const { error } = await supabase.from('seasons').update({ status: 'completed', coming_soon: true }).eq('id', tournId);
    if (error) { setErr('End tournament failed: ' + error.message); setSaving(false); return; }
    await reload();
    if (onRefresh) await onRefresh();
    setSaving(false);
  };

  // --- Send review request emails to captains ---
  const sendReviewEmails = async (testOnly) => {
    if (!testOnly && !window.confirm(`Send review request emails to all ${registrations.length} captains for "${tournData?.name || tournId}"? This emails real people.`)) return;
    setSending(testOnly ? 'review-test' : 'review-all'); setErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-review-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}`, apikey: process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({ tournamentId: tournId, testEmail: testOnly ? (session?.user?.email || '') : undefined }),
      });
      const j = await res.json();
      if (!res.ok || j.error) throw new Error(j.error || `Failed (${res.status})`);
      window.alert(testOnly
        ? `Test review email sent to ${j.testEmail}.`
        : `Done — review emails sent to ${j.sent} of ${j.total} captains${j.failed ? `, ${j.failed} failed` : ''}.`);
    } catch (e) {
      setErr('Review email failed: ' + e.message);
    } finally { setSending(''); }
  };

  // --- Save scores + auto-advance playoff winners ---
  const saveScore = async (match) => {
    setSaving(true); setErr('');
    const sets = scoreInputs.sets
      .filter(s => s.t1 !== '' || s.t2 !== '')
      .map(s => ({ t1: parseInt(s.t1) || 0, t2: parseInt(s.t2) || 0 }));
    const status = scoreInputs.status;
    const { error } = await supabase.from('tournament_matches').update({ sets, status }).eq('id', match.id);
    if (error) { setErr('Save failed: ' + error.message); setSaving(false); return; }

    // Auto-advance: fill next round's TBD slots with winner/loser
    if (status === 'completed' && sets.length > 0) {
      const winner = matchWinner(match, sets);
      const loser = winner ? (winner === match.team1_name ? match.team2_name : match.team1_name) : null;
      if (match.stage === 'quarter' && winner) {
        // Cross-bracket SF routing (avoids pool rematches):
        // QF100 (1A/4B) → SF200 t1 | QF103 (2B/3A) → SF200 t2
        // QF101 (2A/3B) → SF201 t1 | QF102 (1B/4A) → SF201 t2
        const sfMap = { 100: [200, 'team1_name'], 101: [201, 'team1_name'], 102: [201, 'team2_name'], 103: [200, 'team2_name'] };
        const [sfNum, pos] = sfMap[match.match_number] || [];
        if (sfNum) await supabase.from('tournament_matches').update({ [pos]: winner }).eq('tournament_id', tournId).eq('match_number', sfNum);
      } else if (match.stage === 'semi' && winner && loser) {
        const pos = match.match_number === 200 ? 'team1_name' : 'team2_name';
        await supabase.from('tournament_matches').update({ [pos]: winner }).eq('tournament_id', tournId).eq('match_number', 301);
        await supabase.from('tournament_matches').update({ [pos]: loser }).eq('tournament_id', tournId).eq('match_number', 300);
      }
    }

    await reload();
    setEditId(null);
    setSaving(false);
  };

  const startEdit = (m) => {
    setEditId(m.id);
    const numSets = SETS_FOR_STAGE[m.stage] || 1;
    const existingSets = m.sets || [];
    const sets = Array.from({ length: numSets }, (_, i) => ({ t1: existingSets[i]?.t1 ?? '', t2: existingSets[i]?.t2 ?? '' }));
    setScoreInputs({ sets, status: m.status === 'scheduled' ? 'completed' : (m.status || 'completed') });
  };

  const markLive = async (id) => { await supabase.from('tournament_matches').update({ status: 'live' }).eq('id', id); await reload(); };
  const deleteMatch = async (id) => { if (!window.confirm('Delete this match?')) return; await supabase.from('tournament_matches').delete().eq('id', id); await reload(); };

  // Derived state
  const poolMatches = matches.filter(m => m.stage === 'pool');
  const allPoolDone = poolMatches.length > 0 && poolMatches.every(m => m.status === 'completed');
  const playoffHasTBD = matches.some(m => m.stage !== 'pool' && (m.team1_name === 'TBD' || m.team2_name === 'TBD'));
  const matchesByStage = STAGE_ORDER.reduce((acc, s) => { const ms = matches.filter(m => m.stage === s); if (ms.length) acc[s] = ms; return acc; }, {});

  const scoreboardUrl = tournId ? `${window.location.origin}/scoreboard/${tournId}` : '';
  const qrUrl = tournId ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(scoreboardUrl)}&color=e8e8e8&bgcolor=0a0a0a` : '';
  const qrPrintUrl = tournId ? `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(scoreboardUrl)}&color=000000&bgcolor=ffffff` : '';

  const printQR = () => {
    const tournName = tournData?.name || tournId.toUpperCase();
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>OVR Scoreboard QR – ${tournName}</title><style>
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=JetBrains+Mono:wght@400;700&display=swap');
      * { margin:0; padding:0; box-sizing:border-box; }
      body { background:#fff; color:#000; font-family:'JetBrains Mono',monospace; }
      @page { size: letter; margin: 0; }
      .page { width:8.5in; min-height:11in; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:0.6in; position:relative; }
      .top-bar { position:absolute; top:0; left:0; right:0; height:0.55in; background:#000; display:flex; align-items:center; padding:0 0.6in; gap:16px; }
      .top-bar-logo { font-family:'Bebas Neue',sans-serif; font-size:32px; color:#fff; letter-spacing:0.12em; }
      .top-bar-line { width:1px; height:28px; background:#333; }
      .top-bar-text { font-family:'JetBrains Mono',monospace; font-size:10px; color:#ccc; letter-spacing:0.22em; text-transform:uppercase; }
      .bottom-bar { position:absolute; bottom:0; left:0; right:0; height:0.4in; background:#000; display:flex; align-items:center; justify-content:center; }
      .bottom-text { font-family:'JetBrains Mono',monospace; font-size:9px; color:#aaa; letter-spacing:0.2em; text-transform:uppercase; }
      .eyebrow { font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.28em; color:#111; text-transform:uppercase; margin-bottom:12px; font-weight:700; }
      .title { font-family:'Bebas Neue',sans-serif; font-size:72px; letter-spacing:0.06em; color:#000; line-height:0.9; margin-bottom:6px; }
      .subtitle { font-family:'JetBrains Mono',monospace; font-size:11px; letter-spacing:0.14em; color:#222; margin-bottom:40px; }
      .qr-wrap { border:3px solid #000; padding:18px; background:#fff; margin-bottom:32px; }
      .qr-wrap img { display:block; }
      .divider { width:60px; height:3px; background:#000; margin-bottom:28px; }
      .scan-label { font-family:'JetBrains Mono',monospace; font-size:11px; letter-spacing:0.28em; color:#000; font-weight:700; text-transform:uppercase; margin-bottom:10px; }
      .url-text { font-family:'JetBrains Mono',monospace; font-size:13px; color:#000; letter-spacing:0.04em; }
      .accent { color:#9D1515; }
    </style></head><body>
      <div class="page">
        <div class="top-bar">
          <div class="top-bar-logo">OVR</div>
          <div class="top-bar-line"></div>
          <div class="top-bar-text">Ottawa Volleyball Revival</div>
        </div>
        <div class="eyebrow">Live Tournament Scoreboard</div>
        <div class="title">${tournName}</div>
        <div class="subtitle">Scan to follow the action in real time</div>
        <div class="qr-wrap">
          <img src="${qrPrintUrl}" width="420" height="420" alt="QR Code" />
        </div>
        <div class="divider"></div>
        <div class="scan-label">↑ Scan with your phone camera</div>
        <div class="url-text">${scoreboardUrl}</div>
        <div class="bottom-bar">
          <div class="bottom-text">ovrleague.com &nbsp;·&nbsp; @ovrleague &nbsp;·&nbsp; @horizoneventsofficial</div>
        </div>
      </div>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  return (
    <div>
      {/* Tournament selector */}
      <div style={{ marginBottom: 24 }}>
        {!archiveMode ? (
          <>
            <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#555', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 6 }}>SELECT TOURNAMENT</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select style={{ ...INPUT_STYLE, maxWidth: 320 }} value={tournId} onChange={e => setTournId(e.target.value)}>
                <option value="">choose...</option>
                {seasons.filter(s => s.status !== 'completed').map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
              </select>
              <button style={BTN_SM({ borderColor: '#555', color: '#888' })} onClick={() => { setArchiveMode(true); setTournId(''); }}>📁 ARCHIVE</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#d8a64a', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 6 }}>ARCHIVED TOURNAMENTS</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select style={{ ...INPUT_STYLE, maxWidth: 320, borderColor: '#d8a64a44' }} value={tournId} onChange={e => setTournId(e.target.value)}>
                <option value="">choose season...</option>
                {seasons.filter(s => s.status === 'completed').map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
              </select>
              <button style={BTN_SM()} onClick={() => { setArchiveMode(false); setTournId(''); }}>← BACK</button>
            </div>
          </>
        )}
      </div>

      {!tournId && <div style={{ color: '#333', padding: '40px 0', textAlign: 'center', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22 }}>{archiveMode ? 'SELECT A SEASON ABOVE' : 'SELECT A TOURNAMENT ABOVE'}</div>}

      {tournId && (
        <>
          {/* QR + URL */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap' }}>
            <div style={{ background: '#0a0a0a', border: '1px solid #1e1e1e', padding: 12 }}>
              <img src={qrUrl} alt="QR" width={160} height={160} style={{ display: 'block' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#555', letterSpacing: '0.18em', marginBottom: 6 }}>PUBLIC SCOREBOARD</div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#6bd6a3', marginBottom: 12, wordBreak: 'break-all' }}>{scoreboardUrl}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={BTN_SM()} onClick={() => navigator.clipboard?.writeText(scoreboardUrl)}>COPY LINK</button>
                <a href={scoreboardUrl} target="_blank" rel="noopener noreferrer" style={{ ...BTN_SM(), textDecoration: 'none', display: 'inline-block' }}>OPEN ↗</a>
                <button style={BTN_SM({ borderColor: '#d8a64a', color: '#d8a64a' })} onClick={printQR}>PRINT QR</button>
              </div>
            </div>
          </div>

          {/* Registered teams */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#555', letterSpacing: '0.18em', marginBottom: 8 }}>REGISTERED TEAMS ({registrations.length})</div>
            {registrations.length === 0
              ? <div style={{ color: '#333', fontSize: 12, fontFamily: "'JetBrains Mono',monospace" }}>No paid team registrations yet.</div>
              : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {registrations.map(r => (
                    <div key={r.id} style={{ background: '#111', border: '1px solid #1e1e1e', padding: '5px 12px', fontSize: 12, color: '#e8e8e8', fontFamily: "'JetBrains Mono',monospace" }}>
                      {r.team_name || r.captain_name || r.id.slice(0, 8)}
                    </div>
                  ))}
                </div>
            }
          </div>

          {/* Error */}
          {err && <div style={{ background: 'rgba(157,21,21,0.12)', border: '1px solid rgba(157,21,21,0.4)', borderLeft: '3px solid #9D1515', padding: '10px 14px', marginBottom: 14, fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#e05555' }}>{err}</div>}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} disabled={generating || registrations.length < 2} onClick={generateSchedule}>
              {generating ? 'GENERATING…' : matches.length > 0 ? '↻ REGENERATE SCHEDULE' : '⚡ GENERATE SCHEDULE'}
            </button>
            {allPoolDone && playoffHasTBD && (
              <button style={BTN_SM({ borderColor: '#d8a64a', color: '#d8a64a' })} disabled={saving} onClick={fillPlayoffs}>
                {saving ? 'FILLING…' : '🏆 FILL PLAYOFF BRACKET'}
              </button>
            )}
            {matches.length > 0 && <div style={{ fontSize: 11, color: '#555', fontFamily: "'JetBrains Mono',monospace" }}>{matches.filter(m => m.stage === 'pool').length} pool · {matches.filter(m => m.stage !== 'pool').length} playoff matches</div>}
          </div>

          {/* Email the schedule to captains */}
          {matches.some(m => m.stage === 'pool') && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center', paddingTop: 14, borderTop: '1px dashed #1e1e1e' }}>
              <button style={BTN_SM({ borderColor: '#6bd6a3', color: '#6bd6a3' })} disabled={!!sending} onClick={() => sendSchedule(true)}>
                {sending === 'test' ? 'SENDING…' : '✉ EMAIL TEST TO ME'}
              </button>
              <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} disabled={!!sending || registrations.length === 0} onClick={() => sendSchedule(false)}>
                {sending === 'all' ? 'SENDING…' : `✉ EMAIL SCHEDULE TO ${registrations.length} CAPTAINS`}
              </button>
              <div style={{ fontSize: 10, color: '#555', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.06em' }}>Personalized game-day email · first game highlighted</div>
            </div>
          )}

          {/* End tournament + review emails */}
          {tournData && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center', paddingTop: 14, borderTop: '1px dashed #1e1e1e' }}>
              {tournData.status !== 'completed' ? (
                <button style={BTN_SM({ borderColor: '#d8a64a', color: '#d8a64a' })} disabled={saving} onClick={endTournament}>
                  {saving ? 'SAVING…' : '⏹ END TOURNAMENT'}
                </button>
              ) : (
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#6bd6a3', letterSpacing: '0.12em' }}>✓ TOURNAMENT ENDED</span>
              )}
              {tournData.status === 'completed' && (
                <>
                  <button style={BTN_SM({ borderColor: '#6bd6a3', color: '#6bd6a3' })} disabled={!!sending} onClick={() => sendReviewEmails(true)}>
                    {sending === 'review-test' ? 'SENDING…' : '✉ TEST REVIEW EMAIL'}
                  </button>
                  <button style={BTN_SM({ background: '#1a3a2a', borderColor: '#6bd6a3', color: '#6bd6a3' })} disabled={!!sending || registrations.length === 0} onClick={() => sendReviewEmails(false)}>
                    {sending === 'review-all' ? 'SENDING…' : `✉ SEND REVIEW EMAILS TO ${registrations.length} CAPTAINS`}
                  </button>
                </>
              )}
              <div style={{ fontSize: 10, color: '#555', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.06em' }}>
                {tournData.status === 'completed' ? 'Registrations archived · review emails ready to send' : 'Closes registrations · keeps all data · unlocks review emails'}
              </div>
            </div>
          )}

          {loading && <div style={{ color: '#444', padding: 40, textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.14em' }}>LOADING…</div>}

          {/* Matches grouped by stage */}
          {!loading && Object.entries(matchesByStage).map(([stage, stageMatches]) => (
            <div key={stage} style={{ marginBottom: 28 }}>
              {/* Stage header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #1a1a1a' }}>
                <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 16, letterSpacing: '0.12em', color: stage === 'pool' ? '#e8e8e8' : '#d8a64a' }}>{STAGE_LABELS[stage] || stage.toUpperCase()}</div>
                <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono',monospace", color: '#555' }}>
                  {stageMatches.filter(m => m.status === 'completed').length}/{stageMatches.length} complete
                </div>
              </div>

              {stageMatches.map(m => (
                <div key={m.id} style={{ marginBottom: 6 }}>
                  {editId === m.id ? (
                    /* Score entry panel */
                    <div style={{ background: '#111', border: '1px solid #2a2a2a', borderTop: `2px solid ${stage === 'pool' ? '#9D1515' : '#d8a64a'}`, padding: 16, marginBottom: 8 }}>
                      <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 13, color: '#aaa', letterSpacing: '0.1em', marginBottom: 14 }}>
                        GAME {m.match_number}{stage === 'pool' && m.pool ? ` · POOL ${m.pool}` : ''}
                      </div>

                      {scoreInputs.sets.map((s, i) => (
                        <div key={i} style={{ marginBottom: 12 }}>
                          {scoreInputs.sets.length > 1 && <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#555', letterSpacing: '0.18em', marginBottom: 8 }}>SET {i + 1}</div>}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'end' }}>
                            <SbScoreField label={m.team1_name || 'TEAM 1'} value={s.t1} onChange={e => setScoreInputs(p => { const sets = [...p.sets]; sets[i] = { ...sets[i], t1: e.target.value }; return { ...p, sets }; })} />
                            <div style={{ fontSize: 11, color: '#333', fontFamily: "'Bebas Neue',sans-serif", paddingBottom: 14, textAlign: 'center' }}>VS</div>
                            <SbScoreField label={m.team2_name || 'TEAM 2'} value={s.t2} onChange={e => setScoreInputs(p => { const sets = [...p.sets]; sets[i] = { ...sets[i], t2: e.target.value }; return { ...p, sets }; })} />
                          </div>
                        </div>
                      ))}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
                        <select style={{ ...INPUT_STYLE, maxWidth: 160 }} value={scoreInputs.status} onChange={e => setScoreInputs(p => ({ ...p, status: e.target.value }))}>
                          <option value="live">Live (in progress)</option>
                          <option value="completed">Completed</option>
                          <option value="scheduled">Scheduled</option>
                        </select>
                        <button style={BTN_SM({ background: '#9D1515', borderColor: '#9D1515', color: '#fff' })} disabled={saving} onClick={() => saveScore(m)}>{saving ? 'SAVING…' : 'SAVE'}</button>
                        <button style={BTN_SM()} onClick={() => setEditId(null)}>CANCEL</button>
                      </div>
                    </div>
                  ) : (
                    /* Match row */
                    <div style={{ background: '#111', border: '1px solid #1a1a1a', borderLeft: m.status === 'live' ? '2px solid #9D1515' : '1px solid #1a1a1a', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => startEdit(m)}>
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#555', minWidth: 24 }}>#{m.match_number}</span>
                      {stage === 'pool' && m.pool && <span style={{ fontSize: 9, color: '#555', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.1em' }}>POOL {m.pool}</span>}
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', padding: '2px 6px', borderRadius: 2, background: m.status === 'live' ? '#9D1515' : m.status === 'completed' ? '#0d2018' : '#0d0d1a', color: m.status === 'live' ? '#fff' : m.status === 'completed' ? '#6bd6a3' : '#6666aa' }}>{(m.status || 'scheduled').toUpperCase()}</span>
                      <span style={{ flex: 1, fontSize: 13, color: '#e8e8e8', minWidth: 180 }}>
                        {m.team1_name || 'TBD'} <span style={{ color: '#444' }}>vs</span> {m.team2_name || 'TBD'}
                      </span>
                      {(m.sets || []).length > 0 && (
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: '#6bd6a3', fontWeight: 700 }}>
                          {m.sets.map(s => `${s.t1}–${s.t2}`).join('  ')}
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                        {m.status !== 'live' && m.status !== 'completed' && (
                          <button style={BTN_SM({ fontSize: 10, padding: '3px 8px', borderColor: '#9D1515', color: '#9D1515' })} onClick={() => markLive(m.id)}>● LIVE</button>
                        )}
                        <button style={BTN_SM({ fontSize: 10, padding: '3px 8px' })} onClick={() => startEdit(m)}>SCORE</button>
                        <button style={BTN_SM({ fontSize: 10, padding: '3px 8px', borderColor: 'rgba(157,21,21,0.4)', color: 'rgba(224,85,85,0.7)', letterSpacing: '0.12em' })} onClick={() => deleteMatch(m.id)}>REMOVE</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {!loading && matches.length === 0 && (
            <div style={{ color: '#333', padding: '60px 0', textAlign: 'center', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22 }}>
              {registrations.length >= 2 ? 'HIT "GENERATE SCHEDULE" TO AUTO-CREATE ALL MATCHES' : 'NEED AT LEAST 2 PAID TEAMS TO GENERATE A SCHEDULE'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Shirt 360° viewer ────────────────────────────────────────────────────────
function Shirt3D({ front, back }) {
  const hasBack = !!back; // front-print-only shirts have no back design and don't rotate
  const [angle, setAngle] = React.useState(0);
  const [smooth, setSmooth] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [zoomPos, setZoomPos] = React.useState(null); // { x, y } 0–1 over the shirt card
  const cardRef = React.useRef(null);
  const isTouch = 'ontouchstart' in window;

  React.useEffect(() => {
    const srcs = [front, back].filter(Boolean);
    let done = 0;
    setLoaded(false);
    setAngle(0);
    srcs.forEach(src => {
      const img = new Image();
      img.onload = img.onerror = () => { done++; if (done >= srcs.length) setLoaded(true); };
      img.src = src;
    });
  }, [front, back]);

  const onSlide = (e) => {
    setSmooth(false);
    setAngle(Number(e.target.value));
  };

  const norm = ((angle % 360) + 360) % 360;
  const showingBack = hasBack && norm > 90 && norm < 270;
  const sliderVal = norm;
  const face = showingBack ? back : front;

  const zoom = 2.6;
  const lensPct = 100 / zoom;
  const toPos = (clientX, clientY) => {
    const r = cardRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (clientY - r.top) / r.height)),
    };
  };

  const shadow = 'drop-shadow(0 22px 26px rgba(0,0,0,0.55))';

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'radial-gradient(circle at 50% 38%, #1e1e1e 0%, #0b0b0b 75%)', userSelect: 'none' }}>
      {!loaded && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.2em', color: '#555' }}>LOADING</span>
        </div>
      )}

      {loaded && (
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', perspective: 1400 }}>
          <div ref={cardRef} style={{ position: 'relative', width: '82%', maxWidth: 460, aspectRatio: '1' }}>
            {/* 3D card: front + back mounted back-to-back, rotating on Y axis */}
            <div style={{
              position: 'absolute', inset: 0,
              transformStyle: 'preserve-3d',
              transform: `rotateY(${angle}deg)`,
              transition: smooth ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
            }}>
              <img src={front} alt="" draggable={false} style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
                backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
                filter: shadow, pointerEvents: 'none',
              }} />
              {hasBack && (
                <img src={back} alt="" draggable={false} style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
                  transform: 'rotateY(180deg)',
                  backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
                  filter: shadow, pointerEvents: 'none',
                }} />
              )}
            </div>

            {/* Zoom hit layer (static, above the rotating card) */}
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 3, cursor: zoomPos ? 'crosshair' : 'zoom-in', touchAction: isTouch ? 'none' : 'auto' }}
              onMouseMove={isTouch ? undefined : (e) => setZoomPos(toPos(e.clientX, e.clientY))}
              onMouseLeave={isTouch ? undefined : () => setZoomPos(null)}
              onTouchStart={(e) => setZoomPos(toPos(e.touches[0].clientX, e.touches[0].clientY))}
              onTouchMove={(e) => setZoomPos(toPos(e.touches[0].clientX, e.touches[0].clientY))}
              onTouchEnd={() => setZoomPos(null)}
            >
              {zoomPos && !isTouch && (
                <div style={{
                  position: 'absolute', pointerEvents: 'none',
                  width: `${lensPct}%`, height: `${lensPct}%`,
                  left: `${Math.max(0, Math.min(100 - lensPct, zoomPos.x * 100 - lensPct / 2))}%`,
                  top: `${Math.max(0, Math.min(100 - lensPct, zoomPos.y * 100 - lensPct / 2))}%`,
                  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.5)',
                }} />
              )}
              {zoomPos && isTouch && (
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: '#141414',
                  backgroundImage: `url("${face}")`, backgroundRepeat: 'no-repeat',
                  backgroundSize: `${zoom * 100}% ${zoom * 100}%`,
                  backgroundPosition: `${zoomPos.x * 100}% ${zoomPos.y * 100}%`,
                }} />
              )}
            </div>
          </div>

          {hasBack && <div style={{ position: 'absolute', top: 14, left: 16, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }}>↻ 360° VIEW</div>}
          {hasBack && <div style={{ position: 'absolute', top: 14, right: 16, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.45)', pointerEvents: 'none' }}>{showingBack ? 'BACK' : 'FRONT'}</div>}
          {!zoomPos && (
            <div style={{ position: 'absolute', bottom: 12, left: 16, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none' }}>
              {isTouch ? '⊕ TOUCH SHIRT TO ZOOM' : '⊕ HOVER SHIRT TO ZOOM'}
            </div>
          )}
        </div>
      )}

      {/* Magnified panel — slides over the info column, square so the shirt isn't stretched */}
      {loaded && zoomPos && !isTouch && (
        <div style={{
          position: 'absolute', left: '100%', top: 0, height: '100%', aspectRatio: '1',
          zIndex: 6, pointerEvents: 'none', marginLeft: 2,
          background: '#141414', border: '1px solid #2a2a2a', boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          backgroundImage: `url("${face}")`, backgroundRepeat: 'no-repeat',
          backgroundSize: `${zoom * 100}% ${zoom * 100}%`,
          backgroundPosition: `${zoomPos.x * 100}% ${zoomPos.y * 100}%`,
        }} />
      )}

      {loaded && hasBack && (
        <div style={{ padding: '10px 28px 18px' }}>
          <input
            type="range" min={0} max={360} step={1} value={sliderVal}
            onChange={onSlide}
            style={{ width: '100%', accentColor: '#9D1515', cursor: 'ew-resize', display: 'block' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '0.12em', color: '#555' }}>
            <span>FRONT</span><span>BACK</span><span>FRONT</span>
          </div>
        </div>
      )}
    </div>
  );
}

const MERCH_SIZE_GUIDE = {
  cols: ['SIZE', 'LENGTH', 'SHOULDER', 'CHEST', 'SLEEVE'],
  cm: [
    ['S', 71, 53, 57, 22],
    ['M', 73, 56, 60, 23],
    ['L', 75, 59, 63, 24],
    ['XL', 77, 62, 66, 25],
    ['2XL', 79, 65, 69, 26],
  ],
  in: [
    ['S', 27.95, 20.87, 22.44, 8.66],
    ['M', 28.74, 22.05, 23.62, 9.06],
    ['L', 29.53, 23.23, 24.80, 9.45],
    ['XL', 30.31, 24.41, 25.98, 9.84],
    ['2XL', 31.10, 25.59, 27.17, 10.24],
  ],
};

function SizeGuide() {
  const [unit, setUnit] = React.useState('in');
  const rows = MERCH_SIZE_GUIDE[unit];
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
        {['in', 'cm'].map(u => (
          <button key={u} onClick={() => setUnit(u)}
            style={{ background: 'transparent', border: 'none', borderBottom: `2px solid ${unit === u ? '#111' : 'transparent'}`, color: unit === u ? '#111' : '#aaa', fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.14em', padding: '2px 0 6px', cursor: 'pointer' }}>
            {u === 'in' ? 'INCHES' : 'CENTIMETERS'}
          </button>
        ))}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {MERCH_SIZE_GUIDE.cols.map(c => (
              <th key={c} style={{ background: '#f5f5f5', border: '1px solid #e5e5e5', padding: '7px 4px', fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '0.12em', color: '#666', fontWeight: 500 }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r[0]}>
              {r.map((cell, i) => (
                <td key={i} style={{ border: '1px solid #e5e5e5', padding: '7px 4px', textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: i === 0 ? '#111' : '#555', fontWeight: i === 0 ? 600 : 400 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 8, fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '0.1em', color: '#bbb' }}>1–3 CM SIZING DIFFERENCES MAY OCCUR</div>
    </div>
  );
}

// Amazon-style zoom: hover shows a lens + magnified panel beside the image;
// on touch devices, dragging magnifies in place.
function ZoomImage({ src, alt, zoom = 2.6 }) {
  const [pos, setPos] = React.useState(null); // { x, y } in 0–1, null = not zooming
  const ref = React.useRef(null);
  const isTouch = 'ontouchstart' in window;

  const toPos = (clientX, clientY) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (clientY - r.top) / r.height)),
    };
  };

  const lensPct = 100 / zoom; // lens box size as % of image

  return (
    <div
      ref={ref}
      style={{ position: 'relative', width: '100%', height: '100%', cursor: pos ? 'crosshair' : 'zoom-in', touchAction: 'none' }}
      onMouseMove={isTouch ? undefined : (e) => setPos(toPos(e.clientX, e.clientY))}
      onMouseLeave={isTouch ? undefined : () => setPos(null)}
      onTouchStart={(e) => setPos(toPos(e.touches[0].clientX, e.touches[0].clientY))}
      onTouchMove={(e) => { e.preventDefault(); setPos(toPos(e.touches[0].clientX, e.touches[0].clientY)); }}
      onTouchEnd={() => setPos(null)}
    >
      <img src={src} alt={alt} draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#0b0b0b' }} />

      {/* Touch: magnify in place */}
      {pos && isTouch && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none',
          backgroundImage: `url("${src}")`, backgroundRepeat: 'no-repeat',
          backgroundSize: `${zoom * 100}% ${zoom * 100}%`,
          backgroundPosition: `${pos.x * 100}% ${pos.y * 100}%`,
        }} />
      )}

      {/* Desktop: lens + side panel */}
      {pos && !isTouch && (
        <>
          <div style={{
            position: 'absolute', zIndex: 3, pointerEvents: 'none',
            width: `${lensPct}%`, height: `${lensPct}%`,
            left: `${Math.max(0, Math.min(100 - lensPct, pos.x * 100 - lensPct / 2))}%`,
            top: `${Math.max(0, Math.min(100 - lensPct, pos.y * 100 - lensPct / 2))}%`,
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.7)',
            backdropFilter: 'brightness(1.2)',
          }} />
          <div style={{
            position: 'absolute', zIndex: 6, pointerEvents: 'none',
            left: '100%', top: 0, width: '92%', height: '100%',
            marginLeft: 2, background: '#0b0b0b', border: '1px solid #2a2a2a',
            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
            backgroundImage: `url("${src}")`, backgroundRepeat: 'no-repeat',
            backgroundSize: `${zoom * 100}% ${zoom * 100}%`,
            backgroundPosition: `${pos.x * 100}% ${pos.y * 100}%`,
          }} />
        </>
      )}

      {!pos && (
        <div style={{ position: 'absolute', bottom: 10, left: 14, zIndex: 3, pointerEvents: 'none', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.5)' }}>
          {isTouch ? '⊕ TOUCH TO ZOOM' : '⊕ HOVER TO ZOOM'}
        </div>
      )}
    </div>
  );
}

function ProductModal({ shirt, cart, setCart, onClose }) {
  const [selectedSize, setSelectedSize] = React.useState(null);
  const [added, setAdded] = React.useState(false);
  const [showGuide, setShowGuide] = React.useState(false);
  const [view, setView] = React.useState(0); // 0 = 3D spin, 1+ = photos
  const inCart = cart.find(i => i.id === shirt.id && i.size === selectedSize);
  const isMobile = window.innerWidth < 700;
  const photos = shirt.photos || [];

  const thumbStyle = (active) => ({
    width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, flexShrink: 0,
    border: `1px solid ${active ? '#9D1515' : '#2a2a2a'}`, opacity: active ? 1 : 0.55,
    cursor: 'pointer', overflow: 'hidden', background: '#111', padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
    transition: 'opacity 0.15s, border-color 0.15s',
  });

  const addToCart = () => {
    if (!selectedSize || added) return;
    setCart(prev => {
      const existing = prev.find(i => i.id === shirt.id && i.size === selectedSize);
      if (existing) return prev.map(i => i.id === shirt.id && i.size === selectedSize ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { id: shirt.id, name: `${shirt.name} — ${shirt.edition}`, size: selectedSize, price: shirt.price, qty: 1 }];
    });
    setAdded(true);
    setTimeout(() => { setAdded(false); onClose(); }, 700);
  };

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}
      onClick={onClose}>
      <div style={{ background: '#fff', width: '100%', maxWidth: 920, maxHeight: isMobile ? '100dvh' : '90dvh', display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden', position: 'relative' }}
        onClick={e => e.stopPropagation()}>

        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, zIndex: 10, background: '#fff', border: '1px solid #eee', cursor: 'pointer', fontSize: 18, color: '#888', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', padding: 0 }}>×</button>

        {/* Viewer: thumbnails on the left, main view beside them */}
        <div style={{ flex: isMobile ? '0 0 auto' : '0 0 58%', height: isMobile ? '52dvh' : 'auto', minHeight: isMobile ? 0 : 520, display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', background: '#0b0b0b' }}>
          {/* Thumbnail strip */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: 8, padding: isMobile ? '10px 12px' : '14px 10px 14px 14px', overflowX: isMobile ? 'auto' : 'visible', overflowY: isMobile ? 'visible' : 'auto', alignItems: 'center' }}>
            <button onClick={() => setView(0)} style={thumbStyle(view === 0)}>
              <img src={shirt.front} alt="" draggable={false} style={{ width: '88%', height: '88%', objectFit: 'contain', pointerEvents: 'none' }} />
              {shirt.back && <span style={{ position: 'absolute', bottom: 2, left: 0, right: 0, textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 7, letterSpacing: '0.08em', color: '#9D1515' }}>360°</span>}
            </button>
            {photos.map((src, i) => (
              <button key={src} onClick={() => setView(i + 1)} style={thumbStyle(view === i + 1)}>
                <img src={src} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
              </button>
            ))}
          </div>

          {/* Main view — arrows cycle through the gallery (360 view + photos) */}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}>
            {view === 0 ? (
              <Shirt3D front={shirt.front} back={shirt.back} />
            ) : (
              <ZoomImage src={photos[view - 1]} alt={shirt.name} />
            )}
            {photos.length > 0 && (
              <>
                <button onClick={() => setView(v => (v - 1 + photos.length + 1) % (photos.length + 1))}
                  style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)', width: 40, height: 40, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, borderRadius: '50%', backdropFilter: 'blur(6px)', zIndex: 2, padding: 0 }}>‹</button>
                <button onClick={() => setView(v => (v + 1) % (photos.length + 1))}
                  style={{ position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)', width: 40, height: 40, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, borderRadius: '50%', backdropFilter: 'blur(6px)', zIndex: 2, padding: 0 }}>›</button>
                {view > 0 && (
                  <div style={{ position: 'absolute', bottom: 10, right: 14, fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.5)', pointerEvents: 'none' }}>
                    {view + 1} / {photos.length + 1}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Info */}
        <div style={{ flex: 1, padding: isMobile ? '20px 20px 28px' : '44px 36px 36px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', background: '#fff' }}>
          {shirt.tag && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, background: '#9D1515', borderRadius: '50%', display: 'inline-block' }} />
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.22em', color: '#9D1515' }}>{shirt.tag}</span>
            </div>
          )}
          <div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.2em', color: '#aaa', marginBottom: 6 }}>{shirt.edition}</div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 36 : 44, letterSpacing: '0.02em', lineHeight: 1, color: '#111' }}>{shirt.name}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, color: '#111' }}>${shirt.price}</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#aaa', letterSpacing: '0.1em' }}>CAD · PRE-ORDER</span>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.2em', color: '#999' }}>SELECT SIZE</div>
              <button onClick={() => setShowGuide(v => !v)}
                style={{ background: 'transparent', border: 'none', color: '#9D1515', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.14em', cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                {showGuide ? 'HIDE SIZE GUIDE' : 'SIZE GUIDE'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {MERCH_SIZES.map(sz => (
                <button key={sz} onClick={() => setSelectedSize(sz)}
                  style={{ background: selectedSize === sz ? '#111' : 'transparent', border: `1px solid ${selectedSize === sz ? '#111' : '#ddd'}`, color: selectedSize === sz ? '#fff' : '#555', fontFamily: "'Bebas Neue',sans-serif", fontSize: 13, letterSpacing: '0.1em', padding: '9px 16px', cursor: 'pointer', transition: 'all 0.1s' }}>
                  {sz}
                </button>
              ))}
            </div>
          </div>
          {showGuide && <SizeGuide />}
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.12em', color: '#ccc', lineHeight: 1.7 }}>
            LIMITED RUN · SHIPS 4–6 WEEKS AFTER ORDER WINDOW CLOSES
          </div>
          <button onClick={addToCart} disabled={!selectedSize || added}
            style={{ marginTop: 'auto', width: '100%', background: added ? '#2a7a4b' : selectedSize ? '#111' : '#f0f0f0', border: 'none', color: selectedSize ? '#fff' : '#bbb', fontFamily: "'Bebas Neue',sans-serif", fontSize: 15, letterSpacing: '0.18em', padding: '18px', cursor: selectedSize && !added ? 'pointer' : 'not-allowed', transition: 'background 0.2s' }}>
            {added ? 'ADDED ✓' : !selectedSize ? 'SELECT A SIZE' : inCart ? `ADD ANOTHER (${inCart.qty} IN CART)` : 'ADD TO CART'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Merch page ───────────────────────────────────────────────────────────────
const S = (p) => `/SHIRT%20DESIGNS/Shirts/${p}`;

const MERCH_SHIRTS = [
  {
    id: 'black-eye',
    name: 'THE WITNESS',
    edition: 'BLACK',
    price: 35,
    tag: 'EXCLUSIVE',
    front: '/merch/black-eye-front.png',
    back: '/merch/black-eye-back.png',
    photos: [
      S('Black%20eye/018296ac-85cb-4ece-b503-8f33a34a56f7.png'),
      S('Black%20eye/05867337-4440-4da7-b422-d8146111e9fe.png'),
      S('Black%20eye/169f807d-5858-4088-89a6-0ea76b685846.png'),
      S('Black%20eye/24a89e64-0ec1-4f1e-84d3-a4e35e623dd7.png'),
      S('Black%20eye/24bb7254-0400-4d8d-94ef-fba5f6f82284.png'),
      S('Black%20eye/3dcb2030-13be-4e5e-a7f5-a89bfe17bdd0.png'),
      S('Black%20eye/a3422a18-f567-48c3-ae3b-9e6f97f6532a.png'),
      S('Black%20eye/cab7a880-2dd0-4fb7-a705-eba7b1a4c8ba.png'),
    ],
  },
  {
    id: 'white-eye',
    name: 'THE WITNESS',
    edition: 'WHITE',
    price: 35,
    tag: 'EXCLUSIVE',
    front: '/merch/white-eye-front.png',
    back: '/merch/white-eye-back.png',
    photos: [
      S('White%20eye/3b6f0a0c-9553-4bdf-bfda-aa5058353528.png'),
      S('White%20eye/3d7e4289-e464-4cf2-9524-3793d772d6f3.png'),
      S('White%20eye/3d88b5a4-2da8-4f0a-b4b6-5284896830ce.png'),
      S('White%20eye/93d2dcee-77bf-46e7-b4b1-c9be9cc10e62.png'),
      S('White%20eye/b9ee49b1-68d4-49d5-820f-8e632e62aa16.png'),
      S('White%20eye/d3750dc4-78f1-414a-8587-99c87d37bf71.png'),
      S('White%20eye/d50e5565-053b-4117-856b-6f521d3b1b85.png'),
      S('White%20eye/e0239fe9-8095-4fcd-b8b4-fc36f5f13703.png'),
    ],
  },
  {
    id: 'black-classic',
    name: 'AIRBORNE',
    edition: 'BLACK',
    price: 30,
    tag: null,
    front: '/merch/black-classic-front.png',
    back: null,
    photos: [
      S('Blackv/51229804-bb62-4177-ae6e-040aaf908ae2.png'),
      S('Blackv/7b4c41c1-2a83-46f0-811a-272d119d9869.png'),
      S('Blackv/8d64a0a3-4fe9-415b-934b-10c9efc4f91d.png'),
      S('Blackv/95baa52a-26bf-473e-a579-8ee5bf2233a5.png'),
    ],
  },
  {
    id: 'white-classic',
    name: 'AIRBORNE',
    edition: 'WHITE',
    price: 30,
    tag: null,
    front: '/merch/white-classic-front.png',
    back: null,
    photos: [
      S('Whitev/3d6490e9-6573-4020-be5c-697c694753bf.png'),
      S('Whitev/65341f6c-b9aa-4198-bcb5-0c39d04eab85.png'),
      S('Whitev/88d832d7-2122-4fe5-bfb4-c8b838f2daa7.png'),
      S('Whitev/f721c33f-44fe-4325-a396-de61668b33e1.png'),
    ],
  },
];

function merchItemImage(id) {
  return (MERCH_SHIRTS.find(s => s.id === id) || {}).front || null;
}

const MERCH_SIZES = ['S', 'M', 'L', 'XL', '2XL'];

// The drop goes live July 20, 2026 at 12:00 AM ET (midnight going into July 20)
const MERCH_DROP_TIME = new Date('2026-07-20T00:00:00-04:00').getTime();

function DropCountdown({ diff }) {
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  const pad = (n) => String(n).padStart(2, '0');

  const cell = (val, lbl) => (
    <div key={lbl} style={{ textAlign: 'center', minWidth: 62 }}>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(44px,8vw,76px)', lineHeight: 1, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{val}</div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.24em', color: 'rgba(0,0,0,0.45)', marginTop: 6 }}>{lbl}</div>
    </div>
  );
  const colon = (k) => (
    <div key={k} style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(34px,6vw,56px)', color: 'rgba(0,0,0,0.28)', lineHeight: 1.25 }}>:</div>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 'clamp(10px,3.5vw,30px)' }}>
      {cell(pad(d), 'DAYS')}{colon(1)}{cell(pad(h), 'HOURS')}{colon(2)}{cell(pad(m), 'MINUTES')}{colon(3)}{cell(pad(s), 'SECONDS')}
    </div>
  );
}

function MerchPage({ onBack, supabase }) {
  const [cart, setCart] = React.useState([]);
  const [openShirt, setOpenShirt] = React.useState(null);
  const [cartOpen, setCartOpen] = React.useState(false);
  const [checkingOut, setCheckingOut] = React.useState(false);
  const [successMsg, setSuccessMsg] = React.useState(false);

  // Drop gate — ?preview=1 bypasses so the store can be checked before launch
  const preview = React.useMemo(() => new URLSearchParams(window.location.search).has('preview'), []);
  const [now, setNow] = React.useState(Date.now());
  const dropped = preview || now >= MERCH_DROP_TIME;
  React.useEffect(() => {
    if (dropped) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [dropped]);

  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('payment_success') === '1' && p.get('type') === 'merch') {
      setSuccessMsg(true);
      window.history.replaceState({}, '', '/merch');
    }
    if (p.get('payment_cancelled') === '1') {
      window.history.replaceState({}, '', '/merch');
    }
  }, []);

  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const updateQty = (id, size, delta) => {
    setCart(prev => prev.map(i => i.id === id && i.size === size ? { ...i, qty: Math.max(0, i.qty + delta) } : i).filter(i => i.qty > 0));
  };

  const checkout = async () => {
    if (cart.length === 0) return;
    setCheckingOut(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-merch-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY, 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          items: cart.map(i => ({ id: i.id, size: i.size, qty: i.qty })),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (e) {
      alert('Checkout error: ' + e.message);
      setCheckingOut(false);
    }
  };

  if (successMsg) {
    return (
      <div style={{ minHeight: '100dvh', background: '#0a0a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.2em', color: '#6bd6a3', marginBottom: 16 }}>ORDER CONFIRMED</div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 52, color: '#fff', lineHeight: 1, marginBottom: 16 }}>YOU'RE IN.</div>
          <div style={{ fontSize: 14, color: '#888', lineHeight: 1.7, marginBottom: 32 }}>
            Your pre-order is locked in. We'll reach out with shipping details when your order is ready — 4–6 weeks after the pre-order window closes.
          </div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 32, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.08em' }}>
            QUESTIONS? <a href="https://www.instagram.com/ovrleague" style={{ color: '#9D1515' }}>@OVRLEAGUE</a>
          </div>
          <button onClick={onBack} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontFamily: "'Bebas Neue',sans-serif", fontSize: 13, letterSpacing: '0.18em', padding: '14px 32px', cursor: 'pointer' }}>
            BACK TO HOME
          </button>
        </div>
      </div>
    );
  }

  // ── Pre-drop: full-page countdown on the artwork ──
  if (!dropped) {
    return (
      <div style={{ minHeight: '100dvh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ position: 'fixed', inset: 0, backgroundImage: 'url("/merch/timer-bg.jpg")', backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
        <button onClick={onBack} style={{ position: 'fixed', top: 24, left: 24, zIndex: 2, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.15)', color: '#333', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer', padding: '8px 14px', backdropFilter: 'blur(4px)' }}>
          ← BACK
        </button>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <img src={'/ovr-logo.png'} alt="OVR" style={{ height: 52, marginBottom: 20 }} />
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.3em', color: '#9D1515', marginBottom: 10 }}>FIRST SERVE — DROP 01</div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(40px,7vw,68px)', lineHeight: 0.95, letterSpacing: '0.02em', color: '#111', marginBottom: 28 }}>DROPS IN</div>
          <DropCountdown diff={Math.max(0, MERCH_DROP_TIME - now)} />
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.2em', color: 'rgba(0,0,0,0.5)', marginTop: 28 }}>
            JULY 20 · 12:00 AM ET
          </div>
        </div>
      </div>
    );
  }

  // ── Live store — same artwork as page background ──
  return (
    <div style={{ minHeight: '100dvh', color: '#111', fontFamily: 'Inter, sans-serif', paddingBottom: cartCount > 0 ? 100 : 0, position: 'relative' }}>
      <div style={{ position: 'fixed', inset: 0, backgroundImage: 'url("/merch/timer-bg.jpg")', backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 20px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#888', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}>
            ← BACK
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={'/ovr-logo.png'} alt="OVR" style={{ height: 34 }} />
            <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, letterSpacing: '0.24em', color: '#fff' }}>MERCH</span>
          </div>
          <div style={{ width: 60 }} />
        </div>
      </div>

      {/* Hero strip */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 20px 32px', textAlign: 'center' }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.24em', color: '#9D1515', marginBottom: 12 }}>DROP 01 · PRE-ORDER NOW OPEN</div>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(36px,6vw,64px)', lineHeight: 0.95, letterSpacing: '0.02em', marginBottom: 16, color: '#111' }}>FIRST SERVE</div>
        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.5)', letterSpacing: '0.06em', fontFamily: "'JetBrains Mono',monospace" }}>LIMITED RUN · SHIPS 4–6 WEEKS AFTER ORDER CLOSES</div>
      </div>

      {/* Product grid — 2 × 2 */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 20px 60px', display: 'grid', gridTemplateColumns: window.innerWidth < 640 ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 24 }}>
        {MERCH_SHIRTS.map(shirt => (
          <div key={shirt.id} onClick={() => setOpenShirt(shirt)} style={{ cursor: 'pointer' }}>
            <div style={{ position: 'relative', aspectRatio: '1', background: 'radial-gradient(circle at 50% 38%, #1e1e1e 0%, #0b0b0b 75%)', border: '1px solid #1e1e1e', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={shirt.front} alt={shirt.name} style={{ width: '86%', height: '86%', objectFit: 'contain', display: 'block', transition: 'transform 0.3s ease', filter: 'drop-shadow(0 22px 26px rgba(0,0,0,0.55))' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
              {shirt.tag && (
                <div style={{ position: 'absolute', top: 12, left: 12, background: '#9D1515', color: '#fff', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.18em', padding: '4px 10px' }}>{shirt.tag}</div>
              )}
              {shirt.back && <div style={{ position: 'absolute', bottom: 12, right: 12, fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.35)' }}>↻ 360° VIEW</div>}
            </div>
            <div style={{ padding: '14px 2px 4px' }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '0.2em', color: 'rgba(0,0,0,0.5)', marginBottom: 5 }}>{shirt.edition}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: '0.03em', lineHeight: 1, color: '#111' }}>{shirt.name}</div>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, lineHeight: 1, color: '#111', whiteSpace: 'nowrap' }}>
                  ${shirt.price} <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, color: 'rgba(0,0,0,0.5)', letterSpacing: '0.1em' }}>CAD</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {openShirt && (
        <ProductModal
          key={openShirt.id}
          shirt={openShirt}
          cart={cart}
          setCart={(fn) => { setCart(fn); setCartOpen(true); }}
          onClose={() => setOpenShirt(null)}
        />
      )}

      {/* Cart drawer */}
      {cartCount > 0 && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#0f0f0f', borderTop: '1px solid #9D1515' }}>
          {/* Collapsed bar */}
          {!cartOpen && (
            <button onClick={() => setCartOpen(true)} style={{ width: '100%', background: 'transparent', border: 'none', color: '#fff', padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 14, letterSpacing: '0.14em' }}>CART ({cartCount}) · ${cartTotal.toFixed(2)} CAD</span>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 12, color: '#9D1515', letterSpacing: '0.1em' }}>VIEW ↑</span>
            </button>
          )}
          {/* Expanded drawer */}
          {cartOpen && (
            <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, letterSpacing: '0.14em' }}>CART</div>
                <button onClick={() => setCartOpen(false)} style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: 18, padding: 0 }}>↓</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, maxHeight: 220, overflowY: 'auto' }}>
                {cart.map(item => (
                  <div key={`${item.id}-${item.size}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: '#ccc', fontWeight: 600 }}>{item.name}</div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#555', letterSpacing: '0.1em' }}>SIZE {item.size}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button onClick={() => updateQty(item.id, item.size, -1)} style={{ width: 26, height: 26, background: 'transparent', border: '1px solid #333', color: '#ccc', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, width: 20, textAlign: 'center' }}>{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.size, 1)} style={{ width: 26, height: 26, background: 'transparent', border: '1px solid #333', color: '#ccc', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                    </div>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, color: '#fff', minWidth: 48, textAlign: 'right' }}>${(item.price * item.qty).toFixed(0)}</div>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#555', letterSpacing: '0.12em' }}>TOTAL</div>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color: '#fff' }}>${cartTotal.toFixed(2)} <span style={{ fontSize: 12, color: '#555' }}>CAD</span></div>
                </div>
                <button onClick={checkout} disabled={checkingOut}
                  style={{ background: '#9D1515', border: 'none', color: '#fff', fontFamily: "'Bebas Neue',sans-serif", fontSize: 14, letterSpacing: '0.18em', padding: '16px 32px', cursor: checkingOut ? 'not-allowed' : 'pointer', opacity: checkingOut ? 0.6 : 1 }}>
                  {checkingOut ? 'REDIRECTING...' : 'PRE-ORDER NOW →'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

function App() {
  const { user, signOut } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { removeItem: cartRemoveItem } = useCart();
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPage, setShowAdminPage] = useState(() => window.location.pathname === '/admin');
  const [adminRegistrations, setAdminRegistrations] = useState([]);
  const [adminTournaments, setAdminTournaments] = useState([]);
  const [adminFreeAgents, setAdminFreeAgents] = useState([]);
  const [adminBlitz, setAdminBlitz] = useState([]);
  const [adminMerch, setAdminMerch] = useState([]);
  const [merchNotify, setMerchNotify] = useState({ confirm: false, busy: false, result: null });
  const [adminWaitlist, setAdminWaitlist] = useState([]);
  const [adminSessions, setAdminSessions] = useState([]);
  const [adminSeasons, setAdminSeasons] = useState([]);
  const [adminTab, setAdminTab] = useState('dropins');
  const [tournArchiveView, setTournArchiveView] = useState(false);

  // Public tournaments page data
  const [pubSeasons, setPubSeasons] = useState([]); // eslint-disable-line no-unused-vars
  const [pubLiveTournIds, setPubLiveTournIds] = useState([]);
  const [pubChampions, setPubChampions] = useState({}); // eslint-disable-line no-unused-vars
  const [liveToastInfo, setLiveToastInfo] = useState(null); // { id, name } for global live toast
  const [liveToastDismissed, setLiveToastDismissed] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminAvatarMap, setAdminAvatarMap] = useState({});
  const [adminSaving, setAdminSaving] = useState({});
  const [adminFaPickerTeam, setAdminFaPickerTeam] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminFilterLevel, setAdminFilterLevel] = useState('');
  const [adminFilterSession, setAdminFilterSession] = useState('');
  const [adminConfirm, setAdminConfirm] = useState(null);
  const [cancelDropinConfirm, setCancelDropinConfirm] = useState(null); // { ev, within24, paid }
  const [cancelRegConfirm, setCancelRegConfirm] = useState(null); // { ev, paid } for team/FA
  const [ovrAlert, setOvrAlert] = useState(null); // { title?, message } replaces native alert()
  const [adminRefunding, setAdminRefunding] = useState(null); // tournament key being refunded
  const [rosterEditTeam, setRosterEditTeam] = useState(null);
  const [rosterDraft, setRosterDraft] = useState([]);
  const [rosterSaving, setRosterSaving] = useState(false);
  const [rosterFaPool, setRosterFaPool] = useState(null);
  const [rosterFaLoading, setRosterFaLoading] = useState(false);
  const [rosterShowFa, setRosterShowFa] = useState(false);
  const [rosterAddForm, setRosterAddForm] = useState(null);
  const [rosterAddForm2, setRosterAddForm2] = useState(null);
  const [rosterClaimingFa, setRosterClaimingFa] = useState(null);
  const [rosterCheckingOut, setRosterCheckingOut] = useState(false);
  const [email, setEmail] = useState('');
  const [showMenuPage, setShowMenuPage] = useState(() => {
    const p = window.location.pathname;
    return !(p.startsWith('/tournaments') || p.startsWith('/drop-in') || ['/events','/about','/gallery','/sponsors','/admin'].includes(p));
  });
  const [showUpcomingEvents, setShowUpcomingEvents] = useState(() => window.location.pathname === '/events');
  const [showBlitzInfo, setShowBlitzInfo] = useState(false);
  const [showCupInfo, setShowCupInfo] = useState(false);
  const [showAboutUs, setShowAboutUs] = useState(() => window.location.pathname === '/about');
  const [showGallery, setShowGallery] = useState(() => window.location.pathname === '/gallery');
  const [showTournamentsPage, setShowTournamentsPage] = useState(() => {
    const p = window.location.pathname;
    return p.startsWith('/tournaments') && !p.startsWith('/tournaments/register');
  });
  const [showTournamentSignup, setShowTournamentSignup] = useState(false);
  const [showTournamentDetails, setShowTournamentDetails] = useState(false);
  const [showTournamentTeamForm, setShowTournamentTeamForm] = useState(() => window.location.pathname.startsWith('/tournaments/register'));
  const [showTournamentAcknowledgments, setShowTournamentAcknowledgments] = useState(false);
  const [showTournamentThankYou, setShowTournamentThankYou] = useState(false);
  const [tournamentTeamFormStep, setTournamentTeamFormStep] = useState(1);
  const [tournamentAcknowledgments, setTournamentAcknowledgments] = useState({
    mediaRelease: false,
    paymentConfirmation: false
  });
  const [tournamentStage, setTournamentStage] = useState('pool'); // 'pool', 'quarters', 'semis', 'finals'
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showDropInFlow, setShowDropInFlow] = useState(() => window.location.pathname.startsWith('/drop-in'));
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [tournamentTeamData, setTournamentTeamData] = useState({
    numPlayers: '',
    teamName: '',
    captainName: '',
    captainEmail: '',
    phoneNumber: '',
    players: []
  });
  const [selectedTournament, setSelectedTournament] = useState('szn4'); // 'szn4' | 'szn5'
  // eslint-disable-next-line no-unused-vars
  const [tournamentSignupOnlySeason, setTournamentSignupOnlySeason] = useState(null);
  const [showPlayerType, setShowPlayerType] = useState(false);
  const [featuredSeason, setFeaturedSeason] = useState('szn5');
  const [showSponsors, setShowSponsors] = useState(() => window.location.pathname === '/sponsors');
  const [showOVRPlus, setShowOVRPlus] = useState(() => window.location.pathname === '/ovr-plus');
  const [showMerch, setShowMerch] = useState(() => window.location.pathname.replace(/\/$/, '') === '/merch');
  const [showMyEvents, setShowMyEvents] = useState(() => window.location.pathname === '/my-events');
  const [showScoreboard, setShowScoreboard] = useState(() => window.location.pathname.startsWith('/scoreboard'));
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentSuccessSessionId, setPaymentSuccessSessionId] = useState(null);
  const [userAvatar, setUserAvatar] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [activeSponsor, setActiveSponsor] = useState(null);
  const [showAllSponsorsList, setShowAllSponsorsList] = useState(false);
  const aboutUsPageRef = useRef(null);
  const tournScrollRef = useRef(null);
  const trophyVideoRef = useRef(null);
  const trophySectionRef = useRef(null);
  const galleryScrollRef = useRef(null);
  const sectionVideoRef = useRef(null);
  const [sectionVideoMuted, setSectionVideoMuted] = useState(true);
  const trophyTargetRef = useRef(0);
  const trophyCurrentRef = useRef(0);
  const trophyRafRef = useRef(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamFormStep, setTeamFormStep] = useState(1);
  const [teamData, setTeamData] = useState({
    numPlayers: '',
    teamName: '',
    players: []
  });
  const [showIndividualForm, setShowIndividualForm] = useState(false);
  const [individualData, setIndividualData] = useState({
    name: '',
    email: ''
  });
  const [showDropInThankYou, setShowDropInThankYou] = useState(false);
  const [dropInThankYouAmount, setDropInThankYouAmount] = useState(0);
  const [dropInThankYouSlot, setDropInThankYouSlot] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  const isMobile = viewportWidth <= 768;

  const navigate = useNavigate();
  const location = useLocation();
  const [regDraft, setRegDraft] = useState(null);
  const [myEventsTournaments, setMyEventsTournaments] = useState(null);
  const [myEventsFa, setMyEventsFa] = useState(null);
  const [myEventsDropins, setMyEventsDropins] = useState(null);
  const [myEventsOrders, setMyEventsOrders] = useState(null);
  const [myEventsReceipt, setMyEventsReceipt] = useState(null);
  const [myEventsRefreshKey, setMyEventsRefreshKey] = useState(0);
  const [restoreDraft, setRestoreDraft] = useState(() => {
    if (!window.location.pathname.startsWith('/tournaments/register')) return false;
    try { const d = JSON.parse(localStorage.getItem('ovrRegDraft') || 'null'); return !!(d?.step && d.step !== 'done'); } catch (e) { return false; }
  });
  const [showFaWarning, setShowFaWarning] = useState(false);
  const faWarningPendingRef = useRef(null);
  const editCartItemIdRef = useRef(null);

  const currentRoute =
    showScoreboard ? window.location.pathname.startsWith('/scoreboard') ? window.location.pathname : '/scoreboard' :
    showAdminPage ? '/admin' :
    showMyEvents ? '/my-events' :
    showTournamentTeamForm ? '/tournaments/register' :
    (showTournamentsPage || showTournamentDetails) ? '/tournaments' :
    showDropInFlow ? '/drop-in' :
    showUpcomingEvents ? '/events' :
    showAboutUs ? '/about' :
    showGallery ? '/gallery' :
    showSponsors ? '/sponsors' :
    showOVRPlus ? '/ovr-plus' :
    showMerch ? '/merch' :
    '/';

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Refresh admin data whenever the admin page is visible and isAdmin is resolved.
  // isAdmin is in deps so a page-refresh at /admin still fetches once auth loads.
  useEffect(() => {
    if (showAdminPage && isAdmin) refreshAdminData();
  }, [showAdminPage, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch My Events data whenever the page becomes visible or the user changes.
  // Using a useEffect avoids stale-closure bugs in the event handler.
  useEffect(() => {
    if (!showMyEvents || !user) return;
    supabase.from('tournament_registrations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).then(async ({ data: t }) => {
      const [{ data: fa }, { data: di }, { data: mo }] = await Promise.all([
        supabase.from('tournament_free_agents').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('drop_in_registrations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        user.email
          ? supabase.from('merch_orders').select('*').ilike('user_email', user.email).order('created_at', { ascending: false })
          : Promise.resolve({ data: [] }),
      ]);
      const faData = fa || [];
      const assignedIds = [...new Set(faData.filter(f => f.assigned_team_id).map(f => f.assigned_team_id))];
      let teamMap = {};
      if (assignedIds.length > 0) {
        const { data: teams } = await supabase.from('tournament_registrations').select('id, team_name, captain_name, season, players').in('id', assignedIds);
        (teams || []).forEach(tm => { teamMap[tm.id] = tm; });
      }
      setMyEventsTournaments(t || []);
      setMyEventsFa(faData.map(f => f.assigned_team_id ? { ...f, _assigned_team: teamMap[f.assigned_team_id] || null } : f));
      setMyEventsDropins(di || []);
      setMyEventsOrders((mo || []).filter(o => o.status !== 'pending'));
    }).catch(() => {
      setMyEventsTournaments([]);
      setMyEventsFa([]);
      setMyEventsDropins([]);
      setMyEventsOrders([]);
    });
  }, [showMyEvents, user, myEventsRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for cart EDIT button — close all pages and open TournamentFlow at roster step
  useEffect(() => {
    const handler = () => {
      // Capture the cart item ID being edited before the draft gets cleared
      try {
        const d = JSON.parse(localStorage.getItem('ovrRegDraft') || 'null');
        editCartItemIdRef.current = d?._editCartItemId || null;
      } catch (e) { editCartItemIdRef.current = null; }
      setShowMenuPage(false);
      setShowTournamentsPage(false);
      setShowTournamentSignup(false);
      setShowTournamentDetails(false);
      setShowTournamentAcknowledgments(false);
      setShowTournamentThankYou(false);
      setRestoreDraft(true);
      setShowTournamentTeamForm(true);
    };
    window.addEventListener('ovr:edit-cart-item', handler);
    return () => window.removeEventListener('ovr:edit-cart-item', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore non-page form data from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('appState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        // Page state is now driven by URL - only restore form data
        if (state.email) setEmail(state.email);
        if (state.selectedSlot) setSelectedSlot(state.selectedSlot);
        if (state.teamFormStep) setTeamFormStep(state.teamFormStep);
        if (state.teamData) setTeamData(state.teamData);
        if (state.individualData) setIndividualData(state.individualData);
        if (state.selectedTournament) setSelectedTournament(state.selectedTournament);
        if (state.dropInThankYouAmount) setDropInThankYouAmount(state.dropInThankYouAmount);
        if (state.dropInThankYouSlot) setDropInThankYouSlot(state.dropInThankYouSlot);
      } catch (error) {
        console.error('Error restoring state:', error);
      }
    }

    // Stripe post-payment redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment_success') === '1') {
      setPaymentSuccessSessionId(urlParams.get('session_id'));
      setShowPaymentSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('payment_cancelled') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      // IMPORTANT: We intentionally do NOT delete the pending registration rows here.
      // A "cancel"/back can race with a payment that actually succeeded (Stripe charged
      // the card, but the webhook hadn't marked the row paid yet), and hard-deleting
      // the pending row caused PAID registrations to vanish — losing the roster while
      // keeping the customer's money. Leave the rows as pending; the webhook marks them
      // paid on success, and any genuinely-abandoned pending rows can be cleaned up by
      // an admin. Just clear the local marker.
      localStorage.removeItem('ovr_pending_payment');
      // Cart stays open so user can try again
    }

    // Legacy post-payment redirect
    const shouldShowTimeSlots = sessionStorage.getItem('showTimeSlots');
    const savedEmail = sessionStorage.getItem('userEmail');
    if (shouldShowTimeSlots === 'true') {
      setEmail(savedEmail || '');
      setShowTimeSlots(true);
      sessionStorage.removeItem('showTimeSlots');
      sessionStorage.removeItem('userEmail');
    }
  }, []);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = {
      email,
      showMenuPage,
      showUpcomingEvents,
      showAboutUs,
      showGallery,
      showTournamentsPage,
      showTournamentSignup,
      showTournamentDetails,
      showTournamentTeamForm,
      showTournamentAcknowledgments,
      showTournamentThankYou,
      tournamentTeamFormStep,
      tournamentAcknowledgments,
      selectedTournament,
      tournamentSignupOnlySeason,
      showTimeSlots,
      showPlayerType,
      showTeamForm,
      showIndividualForm,
      showDropInThankYou,
      dropInThankYouAmount,
      dropInThankYouSlot,
      selectedSlot,
      teamFormStep,
      teamData,
      individualData
    };
    sessionStorage.setItem('appState', JSON.stringify(state));
  }, [email, showMenuPage, showUpcomingEvents, showAboutUs, showGallery, showTournamentsPage, showTournamentSignup, showTournamentDetails, showTournamentTeamForm, showTournamentAcknowledgments, showTournamentThankYou, tournamentTeamFormStep, tournamentAcknowledgments, selectedTournament, tournamentSignupOnlySeason, showTimeSlots, showPlayerType, showTeamForm, showIndividualForm, showDropInThankYou, dropInThankYouAmount, dropInThankYouSlot, selectedSlot, teamFormStep, teamData, individualData]);

  // ── Sync URL with current page state (React Router) ──
  useEffect(() => {
    if (location.pathname !== currentRoute) navigate(currentRoute);
  }, [currentRoute]); // eslint-disable-line

  // ── Restore page state when URL changes (browser back/forward or direct link) ──
  useEffect(() => {
    const p = location.pathname;
    const wantAdmin      = p === '/admin';
    const wantMyEvents   = p === '/my-events';
    const wantReg        = p.startsWith('/tournaments/register');
    const wantTourn      = p.startsWith('/tournaments') && !wantReg;
    const wantDropIn     = p.startsWith('/drop-in');
    const wantEvents     = p === '/events';
    const wantAbout      = p === '/about';
    const wantGallery    = p === '/gallery';
    const wantSponsors   = p === '/sponsors';
    const wantOVRPlus    = p === '/ovr-plus';
    const wantScoreboard = p.startsWith('/scoreboard');
    const wantMenu       = !wantAdmin && !wantMyEvents && !wantReg && !wantTourn && !wantDropIn && !wantEvents && !wantAbout && !wantGallery && !wantSponsors && !wantOVRPlus && !wantScoreboard;

    // Already showing the right page - skip (My Events always re-fetches to pick up status changes)
    if (wantAdmin && showAdminPage) return;
    if (wantScoreboard && showScoreboard) return;
    // wantMyEvents intentionally NOT skipped so data re-fetches on each visit
    if (wantReg && showTournamentTeamForm) return;
    if (wantTourn && showTournamentsPage && !showTournamentTeamForm) return;
    if (wantDropIn && showDropInFlow) return;
    if (wantEvents && showUpcomingEvents) return;
    if (wantAbout && showAboutUs) return;
    if (wantGallery && showGallery) return;
    if (wantSponsors && showSponsors) return;
    if (wantOVRPlus && showOVRPlus) return;
    if (wantMenu && showMenuPage) return;

    setShowScoreboard(wantScoreboard);
    setShowAdminPage(wantAdmin);
    setShowMyEvents(wantMyEvents);
    setShowMenuPage(wantMenu);
    setShowUpcomingEvents(wantEvents);
    setShowAboutUs(wantAbout);
    setShowGallery(wantGallery);
    setShowSponsors(wantSponsors);
    setShowOVRPlus(wantOVRPlus);
    setShowTournamentsPage(wantTourn);
    setShowTournamentTeamForm(wantReg);
    setShowTournamentSignup(false);
    setShowTournamentDetails(false);
    setShowTournamentAcknowledgments(false);
    setShowTournamentThankYou(false);
    setShowDropInFlow(wantDropIn);
  }, [location.pathname]); // eslint-disable-line

  // Enable scrolling on scrollable pages - useLayoutEffect fires before paint so no white flash
  useLayoutEffect(() => {
    const scrollable = showTournamentsPage || showTournamentSignup || showTournamentDetails || showTournamentTeamForm || showTournamentAcknowledgments || showTournamentThankYou || showUpcomingEvents || showAboutUs || showGallery || showDropInFlow || showSponsors || showOVRPlus || showMerch || showAdminPage || showMyEvents || showScoreboard;
    const root = document.getElementById('root');
    if (scrollable) {
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.height = 'auto';
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
      if (root) { root.style.height = 'auto'; root.style.minHeight = '100vh'; }
    } else {
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      if (root) { root.style.height = '100vh'; root.style.minHeight = ''; }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    }
  }, [showTournamentsPage, showTournamentSignup, showTournamentDetails, showTournamentTeamForm, showTournamentAcknowledgments, showTournamentThankYou, showUpcomingEvents, showAboutUs, showGallery, showDropInFlow, showSponsors, showOVRPlus, showAdminPage, showMyEvents, showScoreboard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trophy lerp loop - runs until current time reaches target
  const trophyLerpLoop = () => {
    const v = trophyVideoRef.current;
    if (!v) { trophyRafRef.current = null; return; }

    const target = trophyTargetRef.current;
    const current = trophyCurrentRef.current;
    const diff = target - current;

    if (Math.abs(diff) < 0.001) {
      if (!v.seeking) v.currentTime = target;
      trophyCurrentRef.current = target;
      trophyRafRef.current = null;
      return;
    }

    const next = current + diff * 0.10;
    trophyCurrentRef.current = next;
    if (!v.seeking) v.currentTime = next;
    trophyRafRef.current = requestAnimationFrame(trophyLerpLoop);
  };

  // Trophy scroll scrub - updates target time, kicks off lerp loop
  // Also auto-mutes the section video when it scrolls out of view
  const scrubTrophyVideo = () => {
    const container = tournScrollRef.current;
    const video = trophyVideoRef.current;
    const section = trophySectionRef.current;

    // Auto-mute section video when scrolled out of view
    const secVideo = sectionVideoRef.current;
    if (secVideo && container && !secVideo.muted) {
      const { top, bottom } = secVideo.getBoundingClientRect();
      const { top: cTop, bottom: cBottom } = container.getBoundingClientRect();
      const outOfView = bottom <= cTop || top >= cBottom;
      if (outOfView) {
        secVideo.muted = true;
        setSectionVideoMuted(true);
      }
    }

    if (!container || !video || !section) return;

    // Ensure video stays paused (we drive it manually)
    if (!video.paused) video.pause();

    const dur = video.duration;
    if (!dur || isNaN(dur) || !isFinite(dur)) return;

    const scrollTop = container.scrollTop;
    const sectionTop = section.offsetTop;
    const scrollable = section.offsetHeight - container.clientHeight;
    const progress = scrollable > 0 ? Math.max(0, Math.min(1, (scrollTop - sectionTop) / scrollable)) : 0;
    trophyTargetRef.current = progress * dur;

    // Kick off lerp loop if not already running
    if (!trophyRafRef.current) {
      trophyRafRef.current = requestAnimationFrame(trophyLerpLoop);
    }
  };

  // Scroll to top when About Us page opens
  useEffect(() => {
    if (showAboutUs) {
      // .about-page is the scroll container (overflow-y: auto), so use the ref
      const reset = () => {
        if (aboutUsPageRef.current) {
          aboutUsPageRef.current.scrollTop = 0;
        }
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };
      reset();
      // Second pass after React finishes painting
      requestAnimationFrame(() => { reset(); setTimeout(reset, 80); });
    }
  }, [showAboutUs]);

  // Auto-show menu page if no page is active
  useEffect(() => {
    if (!showMenuPage && !showUpcomingEvents && !showAboutUs && !showGallery && !showTournamentsPage && !showTournamentSignup && !showTournamentDetails && !showTournamentTeamForm && !showTournamentAcknowledgments && !showTournamentThankYou && !showDropInThankYou && !showTimeSlots && !showPlayerType && !showTeamForm && !showIndividualForm && !showDropInFlow && !showSponsors && !showAdminPage) {
      setShowMenuPage(true);
    }
  }, [showMenuPage, showUpcomingEvents, showAboutUs, showGallery, showTournamentsPage, showTournamentSignup, showTournamentDetails, showTournamentTeamForm, showTournamentAcknowledgments, showTournamentThankYou, showDropInThankYou, showTimeSlots, showPlayerType, showTeamForm, showIndividualForm, showDropInFlow, showSponsors, showAdminPage]);


  // Check admin status + fetch avatar whenever user changes
  useEffect(() => {
    if (!user) { setIsAdmin(false); setUserAvatar(null); return; }
    let cancelled = false;
    supabase.from('profiles').select('role, avatar_url').eq('id', user.id).maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.warn('[profile fetch]', error.message); return; }
        setIsAdmin(data?.role === 'admin');
        setUserAvatar(data?.avatar_url || null);
      });
    return () => { cancelled = true; };
  }, [user]);

  const handleAvatarUpload = async (file) => {
    if (!file || !user) return;
    // MIME type allow-list - SVGs are excluded because they can carry inline scripts
    const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const EXT_FROM_MIME = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' };
    if (!ALLOWED_MIME.includes(file.type)) {
      setOvrAlert({ title: 'WRONG FILE TYPE', message: 'we only do JPG, PNG, WebP, or GIF. picky about files, not players.' });
      return;
    }
    // 5 MB cap
    if (file.size > 5 * 1024 * 1024) {
      setOvrAlert({ title: 'TOO LARGE', message: 'that photo is doing the most. keep it under 5 MB.' });
      return;
    }
    setAvatarUploading(true);
    const ext = EXT_FROM_MIME[file.type];
    const path = `${user.id}.${ext}`;

    // Remove previous avatars regardless of extension (jpg/png/webp/gif)
    const oldPaths = ['jpg', 'jpeg', 'png', 'webp', 'gif'].map(e => `${user.id}.${e}`);
    await supabase.storage.from('avatars').remove(oldPaths);

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      console.error('[Avatar] upload error:', uploadError);
      setOvrAlert({ title: 'UPLOAD FAILED', message: 'the server dropped the ball on that one. give it another go.' });
      setAvatarUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const url = `${urlData.publicUrl}?t=${Date.now()}`;

    const { error: dbError } = await supabase
      .from('profiles')
      .update({ avatar_url: url })
      .eq('id', user.id);

    if (dbError) {
      console.error('[Avatar] db update error:', dbError);
      setOvrAlert({ title: 'SO CLOSE', message: 'photo landed, profile did not. try again and it should stick.' });
    }

    setUserAvatar(url);
    setAvatarUploading(false);
  };

  // Check for unfinished registration draft when user signs in.
  // On production all tournaments are gated as COMING SOON, so we drop any draft
  // instead of letting the recovery banner bypass the gate.
  useEffect(() => {
    if (!user) { setRegDraft(null); return; }
    const isProd = !/^(localhost|127\.0\.0\.1)/.test(window.location.hostname);
    if (isProd) { localStorage.removeItem('ovrRegDraft'); setRegDraft(null); return; }
    try {
      const raw = localStorage.getItem('ovrRegDraft');
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && d.step && d.step !== 'tourn' && d.step !== 'done') setRegDraft(d);
    } catch (e) {}
  }, [user]);

  // On mount: check for any live tournaments for the global toast
  useEffect(() => {
    async function checkGlobalLive() {
      const { data: liveMatches } = await supabase.from('tournament_matches').select('tournament_id').eq('status', 'live');
      if (!liveMatches?.length) return;
      const ids = [...new Set(liveMatches.map(m => m.tournament_id))];
      const { data: seas } = await supabase.from('seasons').select('id, name').in('id', ids);
      if (seas?.length) {
        setPubLiveTournIds(ids);
        setLiveToastInfo({ id: seas[0].id, name: seas[0].name });
      }
    }
    checkGlobalLive();
  }, []); // eslint-disable-line

  // Fetch public scoreboard data whenever tournaments page opens
  useEffect(() => {
    if (!showTournamentsPage) return;
    supabase.from('seasons').select('id, name, event_date, status, format, coming_soon').order('event_date', { ascending: false })
      .then(({ data }) => setPubSeasons(data || []));
    supabase.from('tournament_matches').select('tournament_id').eq('status', 'live')
      .then(({ data }) => setPubLiveTournIds([...new Set((data || []).map(m => m.tournament_id))]));
    supabase.from('tournament_matches').select('tournament_id, team1_name, team2_name, sets, status').eq('stage', 'final').eq('status', 'completed')
      .then(({ data }) => {
        const champs = {};
        (data || []).forEach(m => {
          const sets = m.sets || [];
          let t1 = 0, t2 = 0;
          sets.forEach(s => { if ((s.t1||0) > (s.t2||0)) t1++; else if ((s.t2||0) > (s.t1||0)) t2++; });
          const toWin = Math.ceil(3 / 2);
          if (t1 >= toWin) champs[m.tournament_id] = m.team1_name;
          else if (t2 >= toWin) champs[m.tournament_id] = m.team2_name;
          else if (sets.length === 1) champs[m.tournament_id] = (s => s.t1 > s.t2 ? m.team1_name : m.team2_name)(sets[0]);
        });
        setPubChampions(champs);
      });
  }, [showTournamentsPage]); // eslint-disable-line

  const handleLogoClick = () => {
    // Reset all scroll positions before unmounting scrollable pages so the
    // home layout paints at the top instead of inheriting stale scroll state.
    if (galleryScrollRef.current) galleryScrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    setShowMenuPage(true);
    setShowUpcomingEvents(false);
    setShowAboutUs(false);
    setShowGallery(false);
    setShowSponsors(false);
    setShowOVRPlus(false);
    setShowMyEvents(false);
    setShowAdminPage(false);
    setActiveSponsor(null);
    setShowTournamentsPage(false);
    setShowTournamentSignup(false);
    setShowTournamentDetails(false);
    setShowTournamentTeamForm(false);
    setShowTournamentAcknowledgments(false);
    setShowTournamentThankYou(false);
    setTournamentTeamFormStep(1);
    setTournamentAcknowledgments({ mediaRelease: false, paymentConfirmation: false });
    setShowDropInFlow(false);
    setShowTimeSlots(false);
    setShowPlayerType(false);
    setShowTeamForm(false);
    setShowIndividualForm(false);
    setShowDropInThankYou(false);
    setDropInThankYouAmount(0);
    setDropInThankYouSlot('');
    setSelectedSlot(null);
    setTeamFormStep(1);
    setTeamData({ numPlayers: '', teamName: '', players: [] });
    setIndividualData({ name: '', email: '' });
    
    // Update saved state
    sessionStorage.setItem('appState', JSON.stringify({
      email,
      showMenuPage: true,
      showUpcomingEvents: false,
      showAboutUs: false,
      showGallery: false,
      showTournamentsPage: false,
      showTournamentSignup: false,
      showTournamentDetails: false,
      showTournamentTeamForm: false,
      showTournamentAcknowledgments: false,
      showTournamentThankYou: false,
      selectedTournament: 'szn4',
      tournamentSignupOnlySeason: null,
      showTimeSlots: false,
      showPlayerType: false,
      showTeamForm: false,
      showIndividualForm: false,
      showDropInThankYou: false,
      dropInThankYouAmount: 0,
      dropInThankYouSlot: '',
      selectedSlot: null,
      teamFormStep: 1,
      teamData: { numPlayers: '', teamName: '', players: [] },
      individualData: { name: '', email: '' }
    }));
    
    window.scrollTo(0, 0);
  };

  const handleSignOutGuarded = (bypass = false) => {
    if (!bypass && hasFaDraft()) {
      faWarningPendingRef.current = () => handleSignOutGuarded(true);
      setShowFaWarning(true);
      return;
    }
    try {
      sessionStorage.removeItem('appState');
      localStorage.removeItem('ovrRegDraft');
      localStorage.removeItem('ovr_pending_payment');
      localStorage.removeItem('ovr_cart_v1');
    } catch (e) { /* ignore */ }
    signOut().then(() => { window.location.href = '/'; }).catch(() => { window.location.href = '/'; });
  };

  const refreshAdminData = async () => {
    setAdminLoading(true);
    const [{ data: di, error: edi }, { data: t, error: et }, { data: fa, error: efa }, { data: b, error: eb }, { data: m, error: em }, { data: wl, error: ewl }, { data: sess }, { data: seas }] = await Promise.all([
      supabase.from('drop_in_registrations').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('tournament_registrations').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('tournament_free_agents').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('blitz_registrations').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('merch_orders').select('*').order('created_at', { ascending: false }).limit(500),
      supabase.from('drop_in_waitlist').select('*').order('joined_at', { ascending: true }).limit(500),
      supabase.from('drop_in_sessions').select('*').order('starts_at'),
      supabase.from('seasons').select('*').order('created_at'),
    ]);
    if (!edi) setAdminRegistrations(di || []);
    if (!et)  setAdminTournaments(t || []);
    if (!efa) setAdminFreeAgents(fa || []);
    if (!eb)  setAdminBlitz(b || []);
    if (!em)  setAdminMerch(m || []);
    if (!ewl) setAdminWaitlist(wl || []);
    setAdminSessions(sess || []);
    setAdminSeasons(seas || []);
    // Batch-fetch avatars for all unique user_ids across all tables
    const allRows = [...(di||[]), ...(t||[]), ...(fa||[]), ...(b||[])];
    const uids = [...new Set(allRows.map(r => r.user_id).filter(Boolean))];
    if (uids.length > 0) {
      supabase.from('profiles').select('id, avatar_url').in('id', uids)
        .then(({ data: profs }) => {
          const map = {};
          (profs || []).forEach(p => { map[p.id] = p.avatar_url || null; });
          setAdminAvatarMap(map);
        });
    }
    setAdminLoading(false);
  };

  const handleHamburgerNav = (section, _bypass = false) => {
    if (!_bypass && showTournamentTeamForm && hasFaDraft()) {
      faWarningPendingRef.current = () => handleHamburgerNav(section, true);
      setShowFaWarning(true);
      return;
    }
    // Reset all scroll positions before switching pages so every page paints
    // at the top instead of inheriting stale scroll state from the previous one.
    if (galleryScrollRef.current) galleryScrollRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Close all pages first
    setShowMenuPage(false);
    setShowUpcomingEvents(false);
    setShowAboutUs(false);
    setShowGallery(false);
    setShowTournamentsPage(false);
    setShowTournamentSignup(false);
    setShowTournamentDetails(false);
    setShowTournamentTeamForm(false);
    setShowTournamentAcknowledgments(false);
    setShowTournamentThankYou(false);
    setShowDropInFlow(false);
    setShowTimeSlots(false);
    setShowPlayerType(false);
    setShowTeamForm(false);
    setShowIndividualForm(false);
    setShowDropInThankYou(false);
    setShowSponsors(false);
    setShowOVRPlus(false);
    setShowMerch(false);
    setShowAdminPage(false);
    setShowMyEvents(false);
    setActiveSponsor(null);

    if (section === 'DROP INS') {
      if (!DROPIN_REGISTRATIONS_OPEN) { setOvrAlert({ title: 'REGISTRATIONS CLOSED', message: 'drop-in sign-ups are not available right now. check back soon.' }); return; }
      setShowDropInFlow(true);
    }
    else if (section === 'UPCOMING EVENTS') { setShowUpcomingEvents(true); }
    else if (section === 'TOURNAMENTS') { setShowTournamentsPage(true); }
    else if (section === 'TOURNAMENT_REGISTER') {
      if (!TOURNAMENT_REGISTRATIONS_OPEN) { setOvrAlert({ title: 'REGISTRATIONS CLOSED', message: 'tournament sign-ups are not available right now. check back soon.' }); return; }
      setShowTournamentTeamForm(true);
    }
    else if (section === 'ABOUT US')   { setShowAboutUs(true); }
    else if (section === 'GALLERY')    { setShowGallery(true); }
    else if (section === 'SPONSORS')   { setShowSponsors(true); }
    else if (section === 'OVR+')       { setShowOVRPlus(true); }
    else if (section === 'MERCH')      { setShowMerch(true); }
    else if (section === 'MY EVENTS') {
      // Reset data so loading bar shows, then bump key to trigger the fetch useEffect.
      setMyEventsTournaments(null);
      setMyEventsFa(null);
      setMyEventsDropins(null);
      setMyEventsRefreshKey(k => k + 1);
      setShowMyEvents(true);
    }
    else if (section === 'ADMIN') {
      setAdminTab('dropins');
      setAdminLoading(true);
      refreshAdminData();
      setShowAdminPage(true);
    }
    else                               { setShowMenuPage(true); }
  };

  const handleMenuClick = (section) => {
    if (section === 'DROP INS') {
      if (!DROPIN_REGISTRATIONS_OPEN) { setOvrAlert({ title: 'REGISTRATIONS CLOSED', message: 'drop-in sign-ups are not available right now. check back soon.' }); return; }
      setShowMenuPage(false); setShowDropInFlow(true);
    } else if (section === 'UPCOMING EVENTS') {
      setShowMenuPage(false);
      setShowUpcomingEvents(true);
    } else if (section === 'TOURNAMENTS') {
      setShowMenuPage(false); setShowTournamentsPage(true);
    } else if (section === 'ABOUT US') {
      setShowMenuPage(false);
      setShowAboutUs(true);
    } else if (section === 'GALLERY') {
      setShowMenuPage(false);
      setShowGallery(true);
    } else if (section === 'OVR+') {
      setShowMenuPage(false);
      setShowOVRPlus(true);
    } else if (section === 'MERCH') {
      setShowMenuPage(false);
      setShowMerch(true);
    }
  };

  // Tournament Team Form Handlers
  const handleTournamentNumPlayersSubmit = (e) => {
    e.preventDefault();
    const num = parseInt(tournamentTeamData.numPlayers);
    if (num >= 6 && num <= 8) {
      setTournamentTeamFormStep(2);
    } else {
      setOvrAlert({ title: 'ROSTER MATH IS OFF', message: '6 to 8 players. that is the sweet spot. adjust and try again.' });
    }
  };

  const handleTournamentTeamNameSubmit = (e) => {
    e.preventDefault();
    if (tournamentTeamData.teamName.trim()) {
      setTournamentTeamFormStep(3);
    }
  };

  const handleTournamentCaptainSubmit = (e) => {
    e.preventDefault();
    if (!tournamentTeamData.captainName.trim()) {
      setOvrAlert({ title: 'WHO IS LEADING?', message: 'we need a captain name. someone has to be in charge around here.' });
      return;
    }
    if (!tournamentTeamData.captainEmail.trim()) {
      setOvrAlert({ title: 'NO EMAIL NO GLORY', message: 'how do we reach you if you win? captain email, please.' });
      return;
    }
    if (!tournamentTeamData.phoneNumber.trim() || tournamentTeamData.phoneNumber.length !== 10) {
      setOvrAlert({ title: 'THAT IS NOT A NUMBER', message: 'give us a real 10-digit phone number. we promise not to spam you.' });
      return;
    }
    // Initialize players array (excluding captain - they're already entered)
    const numPlayers = parseInt(tournamentTeamData.numPlayers);
    const players = Array(numPlayers - 1).fill(null).map(() => ({
      name: '',
      email: ''
    }));
    setTournamentTeamData({ ...tournamentTeamData, players });
    setTournamentTeamFormStep(4);
  };

  const handleTournamentPlayerInfoChange = (index, field, value) => {
    const updatedPlayers = [...tournamentTeamData.players];
    updatedPlayers[index] = { ...updatedPlayers[index], [field]: value };
    setTournamentTeamData({ ...tournamentTeamData, players: updatedPlayers });
  };

  const handleTournamentFormComplete = async (e) => {
    e.preventDefault();
    // Validate all players have name and email
    const allValid = tournamentTeamData.players.every(p => p.name.trim() && p.email.trim());
    if (allValid) {
      // Navigate to acknowledgments page instead of showing alert
      setShowTournamentTeamForm(false);
      setShowTournamentAcknowledgments(true);
    } else {
      setOvrAlert({ title: 'MISSING PLAYERS', message: 'a few spots on the roster are blank. everyone needs a name and email to get in.' });
    }
  };

  const handleTournamentAcknowledgmentsSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate both checkboxes are checked
    if (!tournamentAcknowledgments.mediaRelease || !tournamentAcknowledgments.paymentConfirmation) {
      setOvrAlert({ title: 'TWO BOXES', message: 'both need a checkmark. we did not write the fine print for fun.' });
      return;
    }

    // Require auth at final registration step
    if (!user) {
      openAuthModal(() => handleTournamentAcknowledgmentsSubmit(e), 'signup');
      return;
    }

    // Save to Supabase FIRST so we don't lose registrations if user navigates away
    try {
      // 1. Save team (required for "all registrations" to show up)
      const saveTeamResponse = await fetch(`${config.apiUrl}/tournament-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team: {
            teamName: tournamentTeamData.teamName,
            numPlayers: parseInt(tournamentTeamData.numPlayers, 10),
            captainName: tournamentTeamData.captainName,
            captainEmail: tournamentTeamData.captainEmail,
            captainPhone: tournamentTeamData.phoneNumber
          },
          players: tournamentTeamData.players,
          acknowledgments: tournamentAcknowledgments,
          paid: false
        })
      });
      if (!saveTeamResponse.ok) {
        const errText = await saveTeamResponse.text();
        console.error('Failed to save tournament team:', errText);
        setOvrAlert({ title: 'REGISTRATION FAILED', message: 'that one did not land. try again or email ovrleague@gmail.com and we will get you in.' });
        return;
      }

      // 2. Collect emails (captain + all players) so they appear in emails list
      await fetch(`${config.apiUrl}/collect-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: tournamentTeamData.captainEmail,
          source: 'tournament-team-captain'
        })
      });
      await Promise.all(
        tournamentTeamData.players.map(player =>
          fetch(`${config.apiUrl}/collect-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: player.email,
              source: 'tournament-team'
            })
          })
        )
      );
    } catch (error) {
      console.error('Error saving tournament registration:', error);
      setOvrAlert({ title: 'CONNECTION ISSUE', message: 'something dropped on the way. check your connection and try again.' });
      return;
    }

    // Also save to Supabase for admin panel
    await supabase.from('tournament_registrations').insert({
      user_id: user.id,
      season: selectedTournament,
      team_name: tournamentTeamData.teamName,
      num_players: parseInt(tournamentTeamData.numPlayers, 10),
      captain_name: tournamentTeamData.captainName,
      captain_email: tournamentTeamData.captainEmail,
      phone: tournamentTeamData.phoneNumber,
      players: tournamentTeamData.players,
    });

    // Only show thank you after saves succeeded
    setShowTournamentAcknowledgments(false);
    setShowTournamentThankYou(true);
  };

  // Admin handlers
  const toggleTeamPaid = async (team) => {
    const newPaid = !team.paid;
    const newStatus = newPaid ? 'paid' : 'pending';
    setAdminSaving(s => ({ ...s, [team.id]: true }));
    const { data: updated, error } = await supabase
      .from('tournament_registrations')
      .update({ paid: newPaid, payment_status: newStatus })
      .eq('id', team.id)
      .select('id');
    if (error || !updated?.length) {
      alert(error ? `Update failed: ${error.message}` : 'Update was blocked - check RLS policies.');
      setAdminSaving(s => ({ ...s, [team.id]: false }));
      return;
    }
    setAdminTournaments(ts => ts.map(t => t.id === team.id ? { ...t, paid: newPaid, payment_status: newStatus } : t));
    setAdminSaving(s => ({ ...s, [team.id]: false }));
  };
  const removePlayerFromTeam = async (team, idx) => {
    // Prevent removing the captain (index 0) - would leave team headless
    if (idx === 0) { alert('Cannot remove the captain. To replace the captain, edit the team manually.'); return; }
    const removedPlayer = (team.players || [])[idx];
    const newPlayers = (team.players || []).filter((_, i) => i !== idx);
    const { data: updated, error } = await supabase
      .from('tournament_registrations')
      .update({ players: newPlayers, num_players: newPlayers.length })
      .eq('id', team.id)
      .select('id');
    if (error || !updated?.length) {
      alert(error ? `Update failed: ${error.message}` : 'Update was blocked - check RLS policies.');
      return;
    }
    // If this was a FA, unassign them so they return to the pool
    if (removedPlayer?.fa_id) {
      await supabase.from('tournament_free_agents').update({ assigned_team_id: null }).eq('id', removedPlayer.fa_id);
      setAdminFreeAgents(prev => prev.map(a => a.id === removedPlayer.fa_id ? { ...a, assigned_team_id: null } : a));
    }
    setAdminTournaments(ts => ts.map(t => t.id === team.id ? { ...t, players: newPlayers, num_players: newPlayers.length } : t));
  };
  const addFaToAdminTeam = async (team, fa) => {
    // Enforce max roster cap
    if ((team.players || []).length >= 8) {
      setOvrAlert({ title: 'TEAM IS FULL', message: 'max 8 per team. remove someone before adding a new one.' });
      return;
    }
    const parts = (fa.name || '').split(' ');
    const newPlayer = { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '', gender: fa.gender, position: fa.position, fa_id: fa.id };
    const newPlayers = [...(team.players || []), newPlayer];
    const { data: updated, error } = await supabase
      .from('tournament_registrations')
      .update({ players: newPlayers, num_players: newPlayers.length })
      .eq('id', team.id)
      .select('id');
    if (error || !updated?.length) {
      alert(error ? `Update failed: ${error.message}` : 'Update was blocked - check RLS policies.');
      return;
    }
    // Mark FA as assigned so no other team can claim them
    await supabase.from('tournament_free_agents').update({ assigned_team_id: team.id }).eq('id', fa.id);
    setAdminTournaments(ts => ts.map(t => t.id === team.id ? { ...t, players: newPlayers, num_players: newPlayers.length } : t));
    setAdminFreeAgents(prev => prev.map(a => a.id === fa.id ? { ...a, assigned_team_id: team.id } : a));
    setAdminFaPickerTeam(null);
  };

  const adminRemoveWithRefund = async (table, id) => {
    const { data: { session: s } } = await supabase.auth.getSession();
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-refund`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${s?.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ table, id }),
      }
    );
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Remove failed');
  };

  // Notify the first waiting person on a drop-in waitlist that a spot opened.
  // Fires and forgets — errors are logged but don't block the admin action.
  const notifyWaitlist = async (session_id) => {
    if (!session_id) return;
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-waitlist`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${s?.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ session_id }),
        }
      );
    } catch (e) {
      console.warn('[notifyWaitlist] failed silently:', e);
    }
  };

  const removeFreeAgent = async (fa) => {
    const isPaid = fa.payment_status === 'paid' || fa.paid === true;
    setAdminConfirm({ type: 'fa', label: fa.name, isPaid, onConfirm: async () => {
      try {
        if (isPaid) {
          await adminRemoveWithRefund('tournament_free_agents', fa.id);
        } else {
          const { data: updated, error } = await supabase
            .from('tournament_free_agents')
            .update({ payment_status: 'admin_removed', assigned_team_id: null })
            .eq('id', fa.id)
            .select('id');
          if (error || !updated?.length) throw new Error(error?.message || 'Remove was blocked - check RLS policies.');
        }
      } catch (err) {
        alert(err.message);
        setAdminConfirm(null);
        return;
      }
      setAdminConfirm(null);
      await refreshAdminData();
    }});
  };

  const deleteTeam = (team) => {
    const isPaid = team.payment_status === 'paid' || team.paid === true;
    setAdminConfirm({ type: 'team', label: team.team_name || team.captain_name || 'this team', isPaid, onConfirm: async () => {
      // Unassign FAs FIRST so they're never left pointing at a removed team
      const faIds = (team.players || []).filter(p => p.fa_id).map(p => p.fa_id);
      if (faIds.length) {
        await supabase.from('tournament_free_agents').update({ assigned_team_id: null }).in('id', faIds);
      }
      try {
        if (isPaid) {
          await adminRemoveWithRefund('tournament_registrations', team.id);
        } else {
          const { data: updated, error } = await supabase
            .from('tournament_registrations')
            .update({ payment_status: 'admin_removed' })
            .eq('id', team.id)
            .select('id');
          if (error || !updated?.length) {
            if (faIds.length) await supabase.from('tournament_free_agents').update({ assigned_team_id: team.id }).in('id', faIds);
            throw new Error(error?.message || 'Remove was blocked - check RLS policies.');
          }
        }
      } catch (err) {
        alert(err.message);
        setAdminConfirm(null);
        return;
      }
      setAdminConfirm(null);
      await refreshAdminData();
    }});
  };

  const removePlayerWithConfirm = (team, idx) => {
    const p = (team.players || [])[idx];
    const name = p ? ([p.firstName, p.lastName].filter(Boolean).join(' ') || p.name || `Player ${idx + 1}`) : `Player ${idx + 1}`;
    setAdminConfirm({ type: 'player', label: name, onConfirm: async () => {
      await removePlayerFromTeam(team, idx);
      setAdminConfirm(null);
      await refreshAdminData();
    }});
  };

  // Public scoreboard — no auth required
  if (showScoreboard) {
    return <Scoreboard onBack={() => { setShowScoreboard(false); setShowTournamentsPage(true); window.history.pushState({}, '', '/tournaments'); }} />;
  }

  // Admin page
  if (showAdminPage && isAdmin) {
    const levelLabel = { beg: 'Beginner', int: 'Intermediate', adv: 'Advanced' };
    const activeRegistrations = adminRegistrations.filter(r => r.payment_status !== 'admin_removed');
    const completedSeasonIds  = new Set(adminSeasons.filter(s => s.status === 'completed').map(s => s.id));
    console.log('[admin] adminSeasons:', adminSeasons.map(s => ({ id: s.id, status: s.status })));
    console.log('[admin] completedSeasonIds:', [...completedSeasonIds]);
    console.log('[admin] tournament seasons:', [...new Set(adminTournaments.map(t => t.season))]);
    const activeTournaments   = adminTournaments.filter(t => t.payment_status !== 'admin_removed' && !completedSeasonIds.has(t.season));
    const archivedTournaments = adminTournaments.filter(t => t.payment_status !== 'admin_removed' && completedSeasonIds.has(t.season));
    const activeFreeAgents    = adminFreeAgents.filter(a => a.payment_status !== 'admin_removed' && a.payment_status !== 'refunded' && a.payment_status !== 'user_cancelled');
    const byLevel = { beg: 0, int: 0, adv: 0 };
    activeRegistrations.forEach(r => { if (byLevel[r.level] !== undefined) byLevel[r.level]++; });
    const tabBtn = (id, label) => (
      <button key={id} onClick={() => { setAdminTab(id); setAdminSearch(''); }} style={{ background: 'transparent', border: 'none', borderBottom: adminTab === id ? '2px solid #9D1515' : '2px solid transparent', color: adminTab === id ? '#fff' : '#555', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 18, letterSpacing: '0.08em', padding: '10px 20px', cursor: 'pointer', transition: 'color 0.15s' }}>{label}</button>
    );

    return (
      <AdminMfaGate>
      <>
      <div style={{ background: '#0a0a0a', minHeight: '100vh', overflowY: 'auto', overflowX: 'hidden', color: '#fff', fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(10,10,10,0.96)', borderBottom: '1px solid #1a1a1a', padding: '16px 32px', display: 'flex', alignItems: 'center', backdropFilter: 'blur(12px)' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}
            onClick={handleLogoClick}
          >
            <img src={'/ovr-logo.png'} alt="OVR" style={{ height: 36 }} />
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', color: '#9D1515', textTransform: 'uppercase' }}>Admin Panel</div>
              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, letterSpacing: '0.05em', lineHeight: 1 }}>OVR REGISTRATIONS</div>
            </div>
          </div>
          <button
            onClick={refreshAdminData}
            disabled={adminLoading}
            style={{ marginLeft: 'auto', marginRight: 70, background: 'transparent', border: '1px solid #2a2a2a', color: adminLoading ? '#333' : '#666', fontFamily: 'Inter, sans-serif', fontSize: 11, letterSpacing: '0.1em', padding: '8px 16px', cursor: adminLoading ? 'not-allowed' : 'pointer', textTransform: 'uppercase', transition: 'color 0.15s, border-color 0.15s' }}
            onMouseEnter={e => { if (!adminLoading) e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = adminLoading ? '#333' : '#666'; }}
          >
            {adminLoading ? 'LOADING...' : '↻ REFRESH'}
          </button>
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        <div style={{ padding: isMobile ? '24px 16px' : '32px 40px', maxWidth: 1200, margin: '0 auto' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #1e1e1e', marginBottom: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
            {tabBtn('dropins', `DROP-INS (${adminRegistrations.filter(r => r.payment_status !== 'admin_removed').length})`)}
            {tabBtn('tournaments', `TOURNAMENTS (${activeTournaments.length})`)}
            {tabBtn('freeagents', `FREE AGENTS (${activeFreeAgents.length})`)}
            {tabBtn('blitz', `BLITZ (${adminBlitz.length})`)}
            {tabBtn('merch', `MERCH (${adminMerch.length})`)}
            {tabBtn('waitlist', `WAITLIST (${adminWaitlist.filter(w => w.status === 'waiting').length})`)}
            {tabBtn('events', 'EVENTS')}
            {tabBtn('scoreboard', 'LIVE SCOREBOARD')}
            {(() => { const rc = adminRegistrations.filter(r => r.payment_status === 'admin_removed' || r.payment_status === 'user_cancelled').length + adminTournaments.filter(t => t.payment_status === 'admin_removed' || t.payment_status === 'user_cancelled').length + adminFreeAgents.filter(a => a.payment_status === 'admin_removed' || a.payment_status === 'refunded' || a.payment_status === 'user_cancelled').length; return rc > 0 ? tabBtn('refunds', `REFUNDS (${rc})`) : null; })()}
          </div>

          {adminLoading ? (
            <div style={{ textAlign: 'center', color: '#444', padding: 60, letterSpacing: '0.18em', fontSize: 12 }}>LOADING...</div>
          ) : adminTab === 'dropins' ? (
            <>
              {/* Drop-in stats */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
                {[{ label: 'Total', value: activeRegistrations.length, color: '#9D1515' }, { label: 'Beginner', value: byLevel.beg, color: '#555' }, { label: 'Intermediate', value: byLevel.int, color: '#555' }, { label: 'Advanced', value: byLevel.adv, color: '#555' }].map(s => (
                  <div key={s.label} style={{ background: '#111', border: '1px solid #1e1e1e', borderTop: `2px solid ${s.color}`, padding: '18px 20px 14px' }}>
                    <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 38, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <DropInRosterExport registrations={adminRegistrations} />
              {/* Filters */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Level pills */}
                {[{ key: '', label: 'All Levels' }, { key: 'beg', label: 'Beginner' }, { key: 'int', label: 'Intermediate' }, { key: 'adv', label: 'Advanced' }].map(({ key, label }) => (
                  <button key={key} onClick={() => setAdminFilterLevel(key)}
                    style={{ background: adminFilterLevel === key ? '#9D1515' : '#111', border: `1px solid ${adminFilterLevel === key ? '#9D1515' : '#2a2a2a'}`, color: adminFilterLevel === key ? '#fff' : '#888', fontFamily: "'Bebas Neue',sans-serif", fontSize: 12, letterSpacing: '0.12em', padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {label}
                  </button>
                ))}
                {/* Session date dropdown */}
                <select
                  value={adminFilterSession}
                  onChange={e => setAdminFilterSession(e.target.value)}
                  style={{ background: adminFilterSession ? '#9D1515' : '#111', border: `1px solid ${adminFilterSession ? '#9D1515' : '#2a2a2a'}`, color: adminFilterSession ? '#fff' : '#888', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '6px 12px', cursor: 'pointer', outline: 'none' }}>
                  <option value="">All Dates</option>
                  {[...new Set(adminRegistrations.filter(r => r.session_label).map(r => r.session_label))].sort().map(label => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
                {(adminFilterLevel || adminFilterSession) && (
                  <button onClick={() => { setAdminFilterLevel(''); setAdminFilterSession(''); }}
                    style={{ background: 'transparent', border: '1px solid #333', color: '#555', fontFamily: "'Bebas Neue',sans-serif", fontSize: 11, letterSpacing: '0.1em', padding: '6px 12px', cursor: 'pointer' }}>
                    CLEAR
                  </button>
                )}
              </div>
              <input
                value={adminSearch} onChange={e => setAdminSearch(e.target.value)}
                placeholder="Search by name, email, or confirmation #…"
                style={{ width: '100%', background: '#111', border: '1px solid #2a2a2a', color: '#e8e8e8', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '10px 14px', outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
              />
              {adminRegistrations.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#333', padding: 80 }}>
                  <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 32, marginBottom: 8 }}>NO DROP-INS YET</div>
                  <div style={{ fontSize: 13, color: '#444' }}>Registrations will appear here once players sign up.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                      {['Conf #', 'Name', 'Email', 'Level', 'Session', 'Time', 'Location', 'Position', 'Payment', 'Registered'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {adminRegistrations.filter(r => {
                        const q = adminSearch.toLowerCase();
                        return r.payment_status !== 'admin_removed'
                          && (!adminFilterLevel || r.level === adminFilterLevel)
                          && (!adminFilterSession || r.session_label === adminFilterSession)
                          && (!q || (r.user_name||'').toLowerCase().includes(q) || (r.user_email||'').toLowerCase().includes(q) || (r.confirmation_number||'').toLowerCase().includes(q));
                      }).map((r, i) => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #161616', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(157,21,21,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}>
                          <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}><span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#9D1515' }}>{r.confirmation_number || '-'}</span></td>
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: '#e8e8e8', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 26, height: 26, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {adminAvatarMap[r.user_id]
                                  ? <img src={adminAvatarMap[r.user_id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                                }
                              </div>
                              {r.user_name || '-'}
                            </div>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#888', fontSize: 12 }}>{r.user_email}</td>
                          <td style={{ padding: '12px 14px' }}><span style={{ background: r.level === 'adv' ? 'rgba(157,21,21,0.2)' : r.level === 'int' ? 'rgba(255,165,0,0.1)' : 'rgba(255,255,255,0.06)', color: r.level === 'adv' ? '#e05555' : r.level === 'int' ? '#c8860a' : '#888', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '3px 8px', textTransform: 'uppercase' }}>{levelLabel[r.level] || r.level}</span></td>
                          <td style={{ padding: '12px 14px', color: '#ccc', whiteSpace: 'nowrap' }}>{r.session_label}</td>
                          <td style={{ padding: '12px 14px', color: '#777', fontSize: 12, whiteSpace: 'nowrap' }}>{r.session_time}</td>
                          <td style={{ padding: '12px 14px', color: '#777', fontSize: 12 }}>{r.session_location}</td>
                          <td style={{ padding: '12px 14px', color: '#9D1515', fontWeight: 600, textTransform: 'uppercase', fontSize: 12 }}>{r.position || '-'}</td>
                          <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                            {(() => { const s = r.payment_status || (r.paid ? 'paid' : 'pending'); return <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '3px 8px', textTransform: 'uppercase', background: s === 'paid' ? 'rgba(74,222,128,0.1)' : 'rgba(255,165,0,0.1)', color: s === 'paid' ? '#4ade80' : '#c8860a' }}>{s}</span>; })()}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#555', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <button onClick={() => { const rIsPaid = r.payment_status === 'paid' || r.paid === true; setAdminConfirm({ type: 'dropin', label: r.user_name || r.user_email || 'this registration', isPaid: rIsPaid, onConfirm: async () => {
                              try {
                                if (rIsPaid) { await adminRemoveWithRefund('drop_in_registrations', r.id); }
                                else { const { data: upd, error } = await supabase.from('drop_in_registrations').update({ payment_status: 'admin_removed' }).eq('id', r.id).select('id'); if (error || !upd?.length) throw new Error(error?.message || 'Remove blocked - check RLS.'); }
                              } catch (err) { alert(err.message); setAdminConfirm(null); return; }
                              setAdminConfirm(null);
                              notifyWaitlist(r.session_id);
                              await refreshAdminData();
                            }}); }} style={{ background: 'transparent', border: '1px solid #3a3a3a', color: '#888', fontFamily: "'Bebas Neue',sans-serif", fontSize: 11, letterSpacing: '0.1em', padding: '5px 10px', cursor: 'pointer' }}>
                              REMOVE
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : adminTab === 'tournaments' ? (
            (() => {
              const posPill = (pos) => {
                const c = { S:['#7c5cff','#b9a8ff'], OH:['#d8a64a','#ecc77a'], MB:['#6bd6a3','#94e7c0'], OPP:['#f0686b','#ffa3a5'], L:['#58b6e7','#8fd1f1'] }[pos] || ['#3a3a3a','#8a8a8a'];
                return { display:'inline-flex', alignItems:'center', justifyContent:'center', fontFamily:"'Bebas Neue',sans-serif", fontSize:12, letterSpacing:'0.08em', padding:'3px 8px', border:`1px solid ${c[0]}`, color:c[1], minWidth:36 };
              };
              const gPill = (g) => ({ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, fontFamily:"'JetBrains Mono',monospace", fontSize:10, border: g==='M'?'1px solid rgba(88,182,231,0.4)':g==='F'?'1px solid rgba(240,104,107,0.4)':'1px solid #3a3a3a', color: g==='M'?'#58b6e7':g==='F'?'#f0686b':'#8a8a8a', background:'#131313' });
              return (
                <>
                  {/* Stat strip */}
                  <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr 1fr':'repeat(4,1fr)', gap:1, background:'#2a2a2a', border:'1px solid #2a2a2a', marginBottom:24 }}>
                    {[
                      { label:'Total Teams', value: activeTournaments.length, color:'#9D1515' },
                      { label:'Total Players', value: activeTournaments.reduce((s,t)=>s+(t.num_players||0),0), color:'' },
                      { label:'Paid', value: activeTournaments.filter(t=>t.paid).length, color:'#6bd6a3' },
                      { label:'Unpaid', value: activeTournaments.filter(t=>!t.paid).length, color:'#d8a64a' },
                    ].map(s=>(
                      <div key={s.label} style={{ background:'#0a0a0a', padding:'18px 20px' }}>
                        <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:40, color: s.color||'#fff', lineHeight:1 }}>{s.value}</div>
                        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.22em', color:'#555', textTransform:'uppercase', marginTop:6 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Active / Archive toggle */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                    <input
                      value={adminSearch} onChange={e => setAdminSearch(e.target.value)}
                      placeholder="Search by team name, captain, or confirmation #…"
                      style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#e8e8e8', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '10px 14px', outline: 'none', boxSizing: 'border-box' }}
                    />
                    {archivedTournaments.length > 0 && (
                      <button
                        onClick={() => setTournArchiveView(v => !v)}
                        style={{ background: tournArchiveView ? '#1a1a0a' : 'transparent', border: `1px solid ${tournArchiveView ? '#d8a64a' : '#333'}`, color: tournArchiveView ? '#d8a64a' : '#666', fontFamily: "'Bebas Neue',sans-serif", fontSize: 12, letterSpacing: '0.1em', padding: '10px 16px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        📁 ARCHIVE ({archivedTournaments.length})
                      </button>
                    )}
                  </div>
                  {(() => {
                    const viewList = tournArchiveView ? archivedTournaments : activeTournaments;
                    const seasonLabel = tournArchiveView && archivedTournaments.length > 0
                      ? adminSeasons.find(s => s.id === archivedTournaments[0]?.season)?.name
                      : null;
                    return viewList.length === 0 ? (
                    <div style={{ textAlign:'center', color:'#333', padding:80 }}>
                      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, marginBottom:8 }}>{tournArchiveView ? 'NO ARCHIVED TEAMS' : 'NO TEAMS YET'}</div>
                      <div style={{ fontSize:13, color:'#444' }}>{tournArchiveView ? 'Ended tournaments will appear here.' : 'Tournament registrations will appear here.'}</div>
                    </div>
                  ) : (
                    <>
                      {tournArchiveView && seasonLabel && (
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#d8a64a', letterSpacing: '0.2em', marginBottom: 12 }}>ARCHIVED — {seasonLabel.toUpperCase()}</div>
                      )}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(400px,1fr))', gap:14 }}>
                      {viewList.filter(t => {
                        const q = adminSearch.toLowerCase();
                        return !q || (t.team_name||'').toLowerCase().includes(q) || (t.captain_name||'').toLowerCase().includes(q) || (t.captain_email||'').toLowerCase().includes(q) || (t.confirmation_number||'').toLowerCase().includes(q);
                      }).map(team => {
                        const players = Array.isArray(team.players) ? team.players : [];
                        const tournamentFAs = adminFreeAgents.filter(fa => fa.tournament === team.season);
                        const availableFAs = tournamentFAs.filter(fa => fa.payment_status === 'paid' && !fa.assigned_team_id && !players.some(p => p.fa_id === fa.id));
                        const saving = adminSaving[team.id];
                        return (
                          <div key={team.id} style={{ background:'#131313', border:'1px solid #2a2a2a', padding:22 }}>
                            {/* Head */}
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                              <div>
                                <div style={{ fontSize:10, letterSpacing:'0.18em', textTransform:'uppercase', color:'#9D1515', fontWeight:700, marginBottom:4 }}>{adminSeasons.find(s => s.id === team.season)?.name || team.season?.toUpperCase()}</div>
                                <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, letterSpacing:'0.02em', lineHeight:1 }}>{team.team_name}</div>
                                {team.payment_status === 'user_cancelled' && <span style={{ display:'inline-block', fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase', color:'#d8a64a', border:'1px solid rgba(216,166,74,0.4)', padding:'2px 8px', marginTop:4 }}>CANCELLED BY PLAYER</span>}
                                <div style={{ fontSize:12, color:'#888', marginTop:5 }}>{team.captain_name}</div>
                                <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{team.captain_email}{team.phone ? ` · ${team.phone}` : ''}</div>
                              </div>
                              <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
                                <button onClick={()=>toggleTeamPaid(team)} disabled={saving}
                                  style={{ background: team.paid?'rgba(107,214,163,0.1)':'transparent', border:`1px solid ${team.paid?'#6bd6a3':'#3a3a3a'}`, color: team.paid?'#6bd6a3':'#888', fontFamily:"'Bebas Neue',sans-serif", fontSize:12, letterSpacing:'0.12em', padding:'6px 12px', cursor:'pointer', opacity: saving?0.5:1 }}>
                                  {saving ? '...' : team.paid ? '✓ PAID' : 'MARK PAID'}
                                </button>
                                <button onClick={()=>deleteTeam(team)}
                                  style={{ background:'transparent', border:'1px solid #3a1a1a', color:'#8a3a3a', fontFamily:"'Bebas Neue',sans-serif", fontSize:12, letterSpacing:'0.12em', padding:'6px 12px', cursor:'pointer' }}
                                  onMouseEnter={e=>{e.currentTarget.style.borderColor='#c0392b';e.currentTarget.style.color='#e74c3c';}}
                                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#3a1a1a';e.currentTarget.style.color='#8a3a3a';}}>
                                  DELETE TEAM
                                </button>
                              </div>
                            </div>

                            {/* Roster */}
                            <div style={{ borderTop:'1px solid #2a2a2a', paddingTop:14 }}>
                              <div style={{ fontSize:10, letterSpacing:'0.18em', textTransform:'uppercase', color:'#555', fontWeight:700, marginBottom:10 }}>
                                ROSTER · {players.length} PLAYER{players.length!==1?'S':''}
                              </div>
                              {players.map((p, pi) => (
                                <div key={pi} style={{ display:'grid', gridTemplateColumns:'20px 1fr auto auto auto', gap:8, alignItems:'center', padding:'7px 0', borderTop:'1px solid #1a1a1a' }}>
                                  <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:'#555' }}>{String(pi+1).padStart(2,'0')}</span>
                                  <span style={{ fontSize:13, color: pi===0?'#fff':'#ccc', fontWeight: pi===0?600:400 }}>
                                    {`${p.firstName||''} ${p.lastName||''}`.trim()||'-'}{pi===0?' · CAPT':''}
                                  </span>
                                  <span style={gPill(p.gender)}>{p.gender||'?'}</span>
                                  {p.position ? <span style={posPill(p.position)}>{p.position}</span> : <span/>}
                                  <button onClick={()=>removePlayerWithConfirm(team,pi)}
                                    disabled={pi===0}
                                    title={pi===0?'Cannot remove captain':'Remove player'}
                                    style={{ background:'transparent', border:'1px solid #2a2a2a', color: pi===0?'#333':'#555', width:22, height:22, display:'grid', placeItems:'center', fontSize:13, cursor: pi===0?'not-allowed':'pointer', padding:0, opacity: pi===0?0.35:1 }}
                                    onMouseEnter={e=>{if(pi!==0){e.currentTarget.style.color='#f0686b';e.currentTarget.style.borderColor='#f0686b';}}}
                                    onMouseLeave={e=>{if(pi!==0){e.currentTarget.style.color='#555';e.currentTarget.style.borderColor='#2a2a2a';}}}>×</button>
                                </div>
                              ))}

                              {players.length < 8 && (
                                <button onClick={()=>setAdminFaPickerTeam(adminFaPickerTeam===team.id?null:team.id)}
                                  style={{ width:'100%', background:'transparent', border:'1px dashed #3a3a3a', color:'#555', fontFamily:"'Bebas Neue',sans-serif", fontSize:12, letterSpacing:'0.12em', padding:'10px', marginTop:10, cursor:'pointer' }}
                                  onMouseEnter={e=>{e.currentTarget.style.color='white';e.currentTarget.style.borderColor='#9D1515';}}
                                  onMouseLeave={e=>{e.currentTarget.style.color='#555';e.currentTarget.style.borderColor='#3a3a3a';}}>
                                  {adminFaPickerTeam===team.id?'▲ CLOSE FA PICKER':`+ ADD FROM FA POOL (${availableFAs.length} AVAILABLE)`}
                                </button>
                              )}

                              {adminFaPickerTeam === team.id && (
                                <div style={{ marginTop:10, background:'#0d0d0d', border:'1px solid #3a3a3a', padding:14 }}>
                                  <div style={{ fontSize:10, letterSpacing:'0.18em', textTransform:'uppercase', color:'#9D1515', fontWeight:700, marginBottom:10 }}>SELECT FREE AGENT</div>
                                  {availableFAs.length===0 ? (
                                    <div style={{ fontSize:12, color:'#555' }}>No available free agents for this tournament.</div>
                                  ) : availableFAs.map(fa=>(
                                    <div key={fa.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderTop:'1px solid #1a1a1a', gap:10 }}>
                                      <div style={{ flex:1, display:'flex', alignItems:'center', gap:8 }}>
                                        <div style={{ width:28, height:28, borderRadius:'50%', overflow:'hidden', flexShrink:0, background:'rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                          {adminAvatarMap[fa.user_id]
                                            ? <img src={adminAvatarMap[fa.user_id]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                                            : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                                          }
                                        </div>
                                        <div>
                                        <span style={{ fontSize:13, color:'#e8e8e8', fontWeight:600 }}>{fa.name}</span>
                                        <span style={{ fontSize:11, color:'#555', marginLeft:8 }}>{fa.email}</span>
                                        </div>
                                      </div>
                                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                        {fa.gender&&<span style={gPill(fa.gender)}>{fa.gender}</span>}
                                        {fa.position&&<span style={posPill(fa.position)}>{fa.position}</span>}
                                      </div>
                                      <button onClick={()=>addFaToAdminTeam(team,fa)}
                                        style={{ background:'#9D1515', border:'none', color:'#fff', fontFamily:"'Bebas Neue',sans-serif", fontSize:12, letterSpacing:'0.1em', padding:'5px 12px', cursor:'pointer' }}>ADD</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Footer */}
                            <div style={{ marginTop:14, paddingTop:12, borderTop:'1px solid #2a2a2a' }}>
                              {team.confirmation_number && (
                                <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:'#9D1515', marginBottom:6 }}>{team.confirmation_number}</div>
                              )}
                              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#555' }}>
                                <span>{new Date(team.created_at).toLocaleDateString('en-CA',{month:'short',day:'numeric',year:'numeric'})}</span>
                                <span style={{ color: team.paid?'#6bd6a3':'#d8a64a' }}>{team.paid?'✓ PAYMENT RECEIVED':'⏳ AWAITING PAYMENT'}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    </>
                  );
                  })()}
                </>
              );
            })()
          ) : adminTab === 'freeagents' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
                {[{ label: 'Active', value: activeFreeAgents.length, color: '#9D1515' },
                  { label: 'Refunded', value: adminFreeAgents.filter(a => a.payment_status === 'refunded').length, color: '#4ade80' },
                  { label: 'Unassigned', value: activeFreeAgents.filter(a => a.payment_status === 'paid' && !a.assigned_team_id).length, color: '#d8a64a' }].map(s => (
                  <div key={s.label} style={{ background: '#111', border: '1px solid #1e1e1e', borderTop: `2px solid ${s.color}`, padding: '18px 20px 14px' }}>
                    <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 38, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {(() => {
                const unassignedByTournament = activeFreeAgents
                  .filter(a => a.payment_status === 'paid' && !a.assigned_team_id)
                  .reduce((acc, a) => { const k = a.tournament || 'unknown'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
                const tournaments = Object.entries(unassignedByTournament);
                if (!tournaments.length) return null;
                return (
                  <div style={{ marginBottom: 20, padding: '14px 18px', background: 'rgba(74,222,128,0.04)', border: '1px solid rgba(74,222,128,0.15)', borderLeft: '3px solid #4ade80' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', color: '#4ade80', textTransform: 'uppercase', marginBottom: 10 }}>Unassigned Paid FAs - Issue Refunds</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {tournaments.map(([tournKey, count]) => (
                        <button key={tournKey}
                          disabled={adminRefunding === tournKey}
                          onClick={() => {
                            setAdminConfirm({
                              type: 'batch_refund',
                              label: `${count} unassigned paid free agent${count !== 1 ? 's' : ''} for "${tournKey.toUpperCase()}"`,
                              isPaid: true,
                              onConfirm: async () => {
                                setAdminConfirm(null);
                                setAdminRefunding(tournKey);
                                try {
                                  const { data: { session: s } } = await supabase.auth.getSession();
                                  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/refund-unassigned-fas`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s?.access_token}`, 'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY },
                                    body: JSON.stringify({ tournament: tournKey }),
                                  });
                                  const json = await res.json();
                                  if (!res.ok) throw new Error(json.error || 'Refund failed');
                                  await refreshAdminData();
                                } catch (err) {
                                  alert(`Refund failed: ${err.message}`);
                                }
                                setAdminRefunding(null);
                              },
                            });
                          }}
                          style={{ background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80', fontFamily: "'Bebas Neue',sans-serif", fontSize: 12, letterSpacing: '0.1em', padding: '7px 16px', cursor: 'pointer', opacity: adminRefunding === tournKey ? 0.5 : 1, transition: 'all 0.15s' }}
                          onMouseEnter={e => { if (adminRefunding !== tournKey) { e.currentTarget.style.background = 'rgba(74,222,128,0.14)'; e.currentTarget.style.borderColor = 'rgba(74,222,128,0.5)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(74,222,128,0.07)'; e.currentTarget.style.borderColor = 'rgba(74,222,128,0.25)'; }}>
                          {adminRefunding === tournKey ? 'REFUNDING...' : `REFUND ${count} × ${tournKey.toUpperCase()} →`}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <input
                value={adminSearch} onChange={e => setAdminSearch(e.target.value)}
                placeholder="Search by name, email, or confirmation #…"
                style={{ width: '100%', background: '#111', border: '1px solid #2a2a2a', color: '#e8e8e8', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '10px 14px', outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
              />
              {adminFreeAgents.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#333', padding: 80 }}>
                  <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 32, marginBottom: 8 }}>NO FREE AGENTS YET</div>
                  <div style={{ fontSize: 13, color: '#444' }}>Free agent registrations will appear here.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                      {['Conf #', 'Name', 'Email', 'Phone', 'Position', 'Gender', 'Tournament', 'Status', 'Registered', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {adminFreeAgents.filter(a => {
                        const q = adminSearch.toLowerCase();
                        return a.payment_status !== 'admin_removed' && a.payment_status !== 'refunded' && a.payment_status !== 'user_cancelled' && (!q || (a.name||'').toLowerCase().includes(q) || (a.email||'').toLowerCase().includes(q) || (a.confirmation_number||'').toLowerCase().includes(q));
                      }).map((a, i) => {
                        return (
                        <tr key={a.id} style={{ borderBottom: '1px solid #161616', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(157,21,21,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}>
                          <td style={{ padding: '12px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: a.confirmation_number ? '#9D1515' : '#333', whiteSpace: 'nowrap' }}>{a.confirmation_number || '-'}</td>
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: '#e8e8e8', whiteSpace: 'nowrap' }}>{a.name}</td>
                          <td style={{ padding: '12px 14px', color: '#888', fontSize: 12 }}>{a.email}</td>
                          <td style={{ padding: '12px 14px', color: '#777', fontSize: 12 }}>{a.phone}</td>
                          <td style={{ padding: '12px 14px' }}>{(() => { const c={S:['#7c5cff','#b9a8ff'],OH:['#d8a64a','#ecc77a'],MB:['#6bd6a3','#94e7c0'],OPP:['#f0686b','#ffa3a5'],L:['#58b6e7','#8fd1f1']}[a.position]||['#3a3a3a','#8a8a8a']; return <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',fontFamily:"'Bebas Neue',sans-serif",fontSize:12,letterSpacing:'0.08em',padding:'3px 8px',border:`1px solid ${c[0]}`,color:c[1],minWidth:36}}>{a.position}</span>; })()}</td>
                          <td style={{ padding: '12px 14px' }}><span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:22,height:22,fontFamily:"'JetBrains Mono',monospace",fontSize:10,border:a.gender==='M'?'1px solid rgba(88,182,231,0.4)':a.gender==='F'?'1px solid rgba(240,104,107,0.4)':'1px solid #3a3a3a',color:a.gender==='M'?'#58b6e7':a.gender==='F'?'#f0686b':'#8a8a8a',background:'#131313'}}>{a.gender}</span></td>
                          <td style={{ padding: '12px 14px' }}><span style={{ background: 'rgba(255,255,255,0.06)', color: '#888', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px' }}>{a.tournament?.toUpperCase()}</span></td>
                          <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '3px 8px', textTransform: 'uppercase', background: a.payment_status === 'paid' ? 'rgba(74,222,128,0.1)' : 'rgba(255,165,0,0.1)', color: a.payment_status === 'paid' ? '#4ade80' : '#c8860a' }}>{a.payment_status === 'paid' ? 'PAID' : 'PENDING'}</span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#555', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <button onClick={() => removeFreeAgent(a)} style={{ background: 'transparent', border: '1px solid #3a3a3a', color: '#888', fontFamily: "'Bebas Neue',sans-serif", fontSize: 11, letterSpacing: '0.1em', padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor='#f0686b'; e.currentTarget.style.color='#f0686b'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor='#3a3a3a'; e.currentTarget.style.color='#888'; }}>
                              REMOVE
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : adminTab === 'blitz' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(2, 1fr)', gap: 12, marginBottom: 28 }}>
                {[{ label: 'Registered', value: adminBlitz.length, color: '#9D1515' }, { label: 'Coming Soon', value: '-', color: '#555' }].map(s => (
                  <div key={s.label} style={{ background: '#111', border: '1px solid #1e1e1e', borderTop: `2px solid ${s.color}`, padding: '18px 20px 14px' }}>
                    <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 38, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {adminBlitz.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#333', padding: 80 }}>
                  <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 32, marginBottom: 8 }}>NO BLITZ SIGN-UPS YET</div>
                  <div style={{ fontSize: 13, color: '#444' }}>OVR Blitz registrations will appear here once the flow is live.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                      {['Name', 'Email', 'Team', 'Registered'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {adminBlitz.map((r, i) => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #161616', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(157,21,21,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}>
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: '#e8e8e8' }}>{r.user_name || '-'}</td>
                          <td style={{ padding: '12px 14px', color: '#888', fontSize: 12 }}>{r.user_email}</td>
                          <td style={{ padding: '12px 14px', color: '#ccc' }}>{r.team_name || '-'}</td>
                          <td style={{ padding: '12px 14px', color: '#555', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : adminTab === 'waitlist' ? (
            <>
              {adminWaitlist.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                  <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 36, color: '#222', letterSpacing: '0.06em', marginBottom: 10 }}>NOBODY ON THE WAITLIST</div>
                  <div style={{ fontSize: 13, color: '#3a3a3a' }}>When a drop-in sells out, players can join a waitlist here.</div>
                </div>
              ) : (
                <>
                  {(() => {
                    // Derive level label from session_id prefix (s-b* = Beginner, s-i* = Intermediate, s-a* = Advanced)
                    const levelFromId = (sid) => {
                      if (!sid) return null;
                      if (sid.startsWith('s-b')) return { label: 'BEGINNER', color: '#4ade80' };
                      if (sid.startsWith('s-i')) return { label: 'INTERMEDIATE', color: '#facc15' };
                      if (sid.startsWith('s-a')) return { label: 'ADVANCED', color: '#9D1515' };
                      return null;
                    };
                    // Group by session
                    const bySession = {};
                    adminWaitlist.forEach(w => {
                      if (!bySession[w.session_id]) bySession[w.session_id] = { label: w.session_label || w.session_id, items: [], level: levelFromId(w.session_id) };
                      bySession[w.session_id].items.push(w);
                    });
                    // Sort groups: waiting count desc
                    const sorted = Object.entries(bySession).sort((a, b) => b[1].items.filter(i => i.status === 'waiting').length - a[1].items.filter(i => i.status === 'waiting').length);
                    return sorted.map(([sid, group]) => {
                      const waiting = group.items.filter(i => i.status === 'waiting').length;
                      const notified = group.items.filter(i => i.status === 'notified').length;
                      return (
                        <div key={sid} style={{ marginBottom: 32, background: '#0d0d0d', border: '1px solid #1e1e1e', borderTop: `2px solid ${group.level?.color || '#9D1515'}` }}>
                          {/* Session header */}
                          <div style={{ padding: '18px 24px', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', color: '#555', textTransform: 'uppercase' }}>Drop-In Session</span>
                                {group.level && (
                                  <span style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 10, letterSpacing: '0.18em', padding: '2px 8px', background: `${group.level.color}18`, color: group.level.color, border: `1px solid ${group.level.color}40` }}>
                                    {group.level.label}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 26, color: '#fff', letterSpacing: '0.06em', lineHeight: 1 }}>{group.label}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 28, color: '#c8860a', lineHeight: 1 }}>{waiting}</div>
                                <div style={{ fontSize: 9, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase' }}>Waiting</div>
                              </div>
                              <div style={{ width: 1, height: 32, background: '#2a2a2a' }} />
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 28, color: '#4285F4', lineHeight: 1 }}>{notified}</div>
                                <div style={{ fontSize: 9, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase' }}>Notified</div>
                              </div>
                              <div style={{ width: 1, height: 32, background: '#2a2a2a' }} />
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 28, color: '#fff', lineHeight: 1 }}>{group.items.length}</div>
                                <div style={{ fontSize: 9, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase' }}>Total</div>
                              </div>
                            </div>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead><tr style={{ borderBottom: '1px solid #1e1e1e', background: 'rgba(255,255,255,0.02)' }}>
                                {['#', 'Name', 'Email', 'Phone', 'Joined', 'Status', 'Actions'].map(h => (
                                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', color: '#444', textTransform: 'uppercase' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {group.items.map((w, i) => (
                                  <tr key={w.id} style={{ borderBottom: '1px solid #141414', background: w.status === 'waiting' ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(157,21,21,0.05)'}
                                    onMouseLeave={e => e.currentTarget.style.background = w.status === 'waiting' ? 'transparent' : 'rgba(255,255,255,0.01)'}
                                  >
                                    <td style={{ padding: '12px 14px', color: '#444', fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{i + 1}</td>
                                    <td style={{ padding: '12px 14px', color: '#fff', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{w.name}</td>
                                    <td style={{ padding: '12px 14px' }}>
                                      <a href={`mailto:${w.email}?subject=A%20spot%20opened%20up%20for%20${encodeURIComponent(group.label)}%20-%20OVR%20Drop-In`} style={{ color: '#9D1515', textDecoration: 'none', fontSize: 12, fontFamily: 'Inter, sans-serif' }}>{w.email}</a>
                                    </td>
                                    <td style={{ padding: '12px 14px', color: '#666', fontSize: 12, fontFamily: 'Inter, sans-serif' }}>{w.phone || '-'}</td>
                                    <td style={{ padding: '12px 14px', color: '#444', fontSize: 11, whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}>{new Date(w.joined_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                    <td style={{ padding: '12px 14px' }}>
                                      <span style={{
                                        fontFamily: "'Bebas Neue','Oswald',sans-serif",
                                        fontSize: 10, letterSpacing: '0.14em', padding: '3px 8px', textTransform: 'uppercase',
                                        background: w.status === 'waiting' ? 'rgba(200,134,10,0.12)' : w.status === 'notified' ? 'rgba(66,133,244,0.12)' : w.status === 'converted' ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.04)',
                                        color: w.status === 'waiting' ? '#c8860a' : w.status === 'notified' ? '#4285F4' : w.status === 'converted' ? '#4ade80' : '#555',
                                        border: `1px solid ${w.status === 'waiting' ? 'rgba(200,134,10,0.25)' : w.status === 'notified' ? 'rgba(66,133,244,0.25)' : w.status === 'converted' ? 'rgba(74,222,128,0.25)' : 'rgba(255,255,255,0.08)'}`,
                                      }}>{w.status}</span>
                                    </td>
                                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                      {w.status === 'waiting' && (
                                        <button
                                          onClick={async () => {
                                            try {
                                              const { data: { session: s } } = await supabase.auth.getSession();
                                              const res = await fetch(
                                                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/notify-waitlist`,
                                                {
                                                  method: 'POST',
                                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s?.access_token}`, 'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY },
                                                  body: JSON.stringify({ waitlist_id: w.id }),
                                                }
                                              );
                                              const json = await res.json();
                                              if (!res.ok) throw new Error(json.error || 'Notify failed');
                                            } catch (err) { alert(`Notify failed: ${err.message}`); return; }
                                            await refreshAdminData();
                                          }}
                                          style={{ background: 'transparent', border: '1px solid rgba(66,133,244,0.5)', color: '#4285F4', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 10, letterSpacing: '0.12em', padding: '5px 12px', cursor: 'pointer', marginRight: 6, transition: 'background 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(66,133,244,0.1)'}
                                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                          NOTIFY
                                        </button>
                                      )}
                                      {w.status !== 'converted' && (
                                        <button
                                          onClick={() => {
                                            setAdminConfirm({ type: 'waitlist', label: w.name, isPaid: false, onConfirm: async () => {
                                              const { error } = await supabase.from('drop_in_waitlist').delete().eq('id', w.id);
                                              if (error) { alert(`Delete failed: ${error.message}`); setAdminConfirm(null); return; }
                                              setAdminConfirm(null);
                                              await refreshAdminData();
                                            }});
                                          }}
                                          style={{ background: 'transparent', border: '1px solid #2a2a2a', color: '#666', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 10, letterSpacing: '0.12em', padding: '5px 12px', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
                                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#9D1515'; e.currentTarget.style.color = '#9D1515'; }}
                                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#666'; }}
                                        >
                                          REMOVE
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>
              )}
            </>
          ) : adminTab === 'refunds' ? (
            <>
              {(() => {
                const removedDropins = adminRegistrations.filter(r => r.payment_status === 'admin_removed');
                const cancelledDropins = adminRegistrations.filter(r => r.payment_status === 'user_cancelled');
                const removedTeams   = adminTournaments.filter(t => t.payment_status === 'admin_removed');
                const cancelledTeams = adminTournaments.filter(t => t.payment_status === 'user_cancelled');
                const refundedFAs    = adminFreeAgents.filter(a => a.payment_status === 'admin_removed' || a.payment_status === 'refunded' || a.payment_status === 'user_cancelled');
                const allRefunds = [
                  ...removedDropins.map(r => ({ ...r, _rtype: 'Drop-In', _name: r.user_name || '-', _email: r.user_email || '-', _detail: r.session_label || '-', _status: 'admin_removed' })),
                  ...cancelledDropins.map(r => ({ ...r, _rtype: 'Drop-In', _name: r.user_name || '-', _email: r.user_email || '-', _detail: r.session_label || '-', _status: 'user_cancelled' })),
                  ...removedTeams.map(t => ({ ...t, _rtype: 'Team', _name: t.team_name || t.captain_name || '-', _email: t.captain_email || '-', _detail: t.season || '-', _status: 'admin_removed' })),
                  ...cancelledTeams.map(t => ({ ...t, _rtype: 'Team', _name: t.team_name || t.captain_name || '-', _email: t.captain_email || '-', _detail: t.season || '-', _status: 'user_cancelled' })),
                  ...refundedFAs.map(a => ({ ...a, _rtype: 'Free Agent', _name: a.name || '-', _email: a.email || '-', _detail: a.tournament || '-', _status: a.payment_status })),
                ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                const q = adminSearch.toLowerCase();
                const filtered = allRefunds.filter(r => !q || r._name.toLowerCase().includes(q) || r._email.toLowerCase().includes(q) || (r.confirmation_number||'').toLowerCase().includes(q));
                return (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
                      {[
                        { label: 'Drop-Ins Removed', value: removedDropins.length + cancelledDropins.length, color: '#9D1515' },
                        { label: 'Teams Removed', value: removedTeams.length + cancelledTeams.length, color: '#9D1515' },
                        { label: 'FAs Refunded / Removed', value: refundedFAs.length, color: '#4ade80' },
                      ].map(s => (
                        <div key={s.label} style={{ background: '#111', border: '1px solid #1e1e1e', borderTop: `2px solid ${s.color}`, padding: '18px 20px 14px' }}>
                          <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 38, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    <input
                      value={adminSearch} onChange={e => setAdminSearch(e.target.value)}
                      placeholder="Search by name, email, or confirmation #…"
                      style={{ width: '100%', background: '#111', border: '1px solid #2a2a2a', color: '#e8e8e8', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '10px 14px', outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
                    />
                    {filtered.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#333', padding: 80 }}>
                        <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 32, marginBottom: 8 }}>NO REFUNDS YET</div>
                        <div style={{ fontSize: 13, color: '#444' }}>Removed and refunded registrations will appear here.</div>
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                            {['Conf #', 'Type', 'Name', 'Email', 'Detail', 'Status', 'Date'].map(h => (
                              <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {filtered.map((r, i) => {
                              const isRefunded = r._status === 'refunded';
                              const isUserCancelled = r._status === 'user_cancelled';
                              const hadPayment = !!r.stripe_payment_intent_id;
                              const statusLabel = isRefunded ? 'REFUNDED' : isUserCancelled ? 'CANCELLED BY PLAYER' : hadPayment ? 'REMOVED + REFUNDED' : 'REMOVED (FREE)';
                              const statusColor = isRefunded ? '#4ade80' : isUserCancelled ? '#d8a64a' : hadPayment ? '#4ade80' : '#e05555';
                              const statusBg    = isRefunded ? 'rgba(74,222,128,0.1)' : isUserCancelled ? 'rgba(216,166,74,0.1)' : hadPayment ? 'rgba(74,222,128,0.1)' : 'rgba(157,21,21,0.1)';
                              return (
                                <tr key={`${r._rtype}-${r.id}`} style={{ borderBottom: '1px solid #161616', background: isRefunded || hadPayment ? 'rgba(74,222,128,0.03)' : 'rgba(157,21,21,0.03)' }}
                                  onMouseEnter={e => e.currentTarget.style.background = isRefunded || hadPayment ? 'rgba(74,222,128,0.07)' : 'rgba(157,21,21,0.07)'}
                                  onMouseLeave={e => e.currentTarget.style.background = isRefunded || hadPayment ? 'rgba(74,222,128,0.03)' : 'rgba(157,21,21,0.03)'}>
                                  <td style={{ padding: '12px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: r.confirmation_number ? '#9D1515' : '#333', whiteSpace: 'nowrap' }}>{r.confirmation_number || '-'}</td>
                                  <td style={{ padding: '12px 14px' }}><span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', textTransform: 'uppercase', background: 'rgba(255,255,255,0.06)', color: '#888' }}>{r._rtype}</span></td>
                                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#ccc', whiteSpace: 'nowrap' }}>{r._name}</td>
                                  <td style={{ padding: '12px 14px', color: '#888', fontSize: 12 }}>{r._email}</td>
                                  <td style={{ padding: '12px 14px' }}><span style={{ background: 'rgba(255,255,255,0.06)', color: '#888', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px' }}>{r._detail.toUpperCase()}</span></td>
                                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '3px 8px', textTransform: 'uppercase', background: statusBg, color: statusColor }}>{statusLabel}</span>
                                  </td>
                                  <td style={{ padding: '12px 14px', color: '#555', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          ) : adminTab === 'events' ? (
            <AdminEventsTab
              sessions={adminSessions}
              seasons={adminSeasons}
              onRefresh={refreshAdminData}
              supabase={supabase}
            />
          ) : adminTab === 'scoreboard' ? (
            <AdminScoreboardTab seasons={adminSeasons} supabase={supabase} onRefresh={refreshAdminData} />
          ) : (
            (() => {
              const paidOrders = adminMerch.filter(o => o.status === 'paid');
              const statusStyle = (st) => st === 'pending'
                ? { background: 'rgba(255,165,0,0.1)', color: '#c8860a' }
                : st === 'ready'
                  ? { background: 'rgba(96,165,250,0.1)', color: '#60a5fa' }
                  : { background: 'rgba(0,200,0,0.1)', color: '#4caf50' };
              const sendReadyEmails = async () => {
                setMerchNotify(m => ({ ...m, busy: true, result: null }));
                try {
                  const { data: { session: s } } = await supabase.auth.getSession();
                  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/merch-ready-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s?.access_token}`, 'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY },
                    body: JSON.stringify({}),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.error || 'Send failed');
                  setMerchNotify({ confirm: false, busy: false, result: `SENT ${data.sent} EMAIL${data.sent === 1 ? '' : 'S'}${data.failed ? ` · ${data.failed} FAILED` : ''}` });
                  await refreshAdminData();
                } catch (e) {
                  setMerchNotify({ confirm: false, busy: false, result: `ERROR: ${e.message}` });
                }
              };
              return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
                {[
                  { label: 'Paid Orders', value: paidOrders.length, color: '#9D1515' },
                  { label: 'Revenue (paid)', value: `$${adminMerch.filter(o => o.status !== 'pending').reduce((s, o) => s + (o.total || 0), 0).toFixed(2)}`, color: '#4caf50' },
                  { label: 'Notified / Ready', value: adminMerch.filter(o => o.status === 'ready').length, color: '#60a5fa' },
                  { label: 'Abandoned', value: adminMerch.filter(o => o.status === 'pending').length, color: '#555' },
                ].map(s => (
                  <div key={s.label} style={{ background: '#111', border: '1px solid #1e1e1e', borderTop: `2px solid ${s.color}`, padding: '18px 20px 14px' }}>
                    <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 38, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Ready-for-pickup blast */}
              <div style={{ background: 'rgba(107,214,163,0.04)', border: '1px solid rgba(107,214,163,0.25)', padding: '18px 20px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 20, color: '#fff', letterSpacing: '0.04em' }}>READY FOR PICKUP</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    Emails every <b style={{ color: '#4caf50' }}>paid</b> order that their merch is ready at Plant Rec, then marks it <b style={{ color: '#60a5fa' }}>ready</b>. Already-notified orders are skipped.
                  </div>
                  {merchNotify.result && (
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.1em', color: merchNotify.result.startsWith('ERROR') ? '#e05555' : '#6bd6a3', marginTop: 8 }}>{merchNotify.result}</div>
                  )}
                </div>
                {!merchNotify.confirm ? (
                  <button
                    onClick={() => setMerchNotify(m => ({ ...m, confirm: true, result: null }))}
                    disabled={paidOrders.length === 0}
                    style={{ background: paidOrders.length ? '#1a3d2b' : '#161616', border: `1px solid ${paidOrders.length ? '#6bd6a3' : '#2a2a2a'}`, color: paidOrders.length ? '#6bd6a3' : '#444', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 14, letterSpacing: '0.14em', padding: '12px 22px', cursor: paidOrders.length ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
                    EMAIL ALL ({paidOrders.length})
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={sendReadyEmails} disabled={merchNotify.busy}
                      style={{ background: '#9D1515', border: 'none', color: '#fff', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 14, letterSpacing: '0.14em', padding: '12px 22px', cursor: merchNotify.busy ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: merchNotify.busy ? 0.6 : 1 }}>
                      {merchNotify.busy ? 'SENDING…' : `CONFIRM — SEND ${paidOrders.length}`}
                    </button>
                    {!merchNotify.busy && (
                      <button onClick={() => setMerchNotify(m => ({ ...m, confirm: false }))}
                        style={{ background: 'transparent', border: '1px solid #2a2a2a', color: '#888', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 14, letterSpacing: '0.14em', padding: '12px 18px', cursor: 'pointer' }}>
                        CANCEL
                      </button>
                    )}
                  </div>
                )}
              </div>

              {adminMerch.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#333', padding: 80 }}>
                  <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 32, marginBottom: 8 }}>NO ORDERS YET</div>
                  <div style={{ fontSize: 13, color: '#444' }}>Merch orders will appear here once the store is live.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                      {['Order #', 'Name', 'Email', 'Items', 'Total', 'Status', 'Receipt', 'Ordered'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', color: '#555', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {adminMerch.map((o, i) => (
                        <tr key={o.id} style={{ borderBottom: '1px solid #161616', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(157,21,21,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}>
                          <td style={{ padding: '12px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#9D1515', fontWeight: 700, whiteSpace: 'nowrap' }}>{merchOrderCode(o.id)}</td>
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: '#e8e8e8' }}>{o.user_name || '-'}</td>
                          <td style={{ padding: '12px 14px', color: '#888', fontSize: 12 }}>{o.user_email || '-'}</td>
                          <td style={{ padding: '12px 14px', color: '#ccc', fontSize: 12 }}>{Array.isArray(o.items) ? o.items.map(it => `${it.name || it}${it.size ? ` (${String(it.size).toUpperCase()}${it.qty > 1 ? ` ×${it.qty}` : ''})` : ''}`).join(', ') : '-'}</td>
                          <td style={{ padding: '12px 14px', color: '#9D1515', fontWeight: 700, whiteSpace: 'nowrap' }}>${(o.total || 0).toFixed(2)}</td>
                          <td style={{ padding: '12px 14px' }}><span style={{ ...statusStyle(o.status), fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', textTransform: 'uppercase' }}>{o.status === 'pending' ? 'abandoned' : o.status}</span></td>
                          <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                            {o.stripe_session_id && o.status !== 'pending' ? (
                              <a href={`https://dashboard.stripe.com/payments?query=${o.stripe_session_id}`} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: 11, textDecoration: 'none', fontFamily: "'JetBrains Mono',monospace" }}>STRIPE ↗</a>
                            ) : <span style={{ color: '#333', fontSize: 11 }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#555', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(o.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
              );
            })()
          )}
        </div>

      </div>
      {adminConfirm && ReactDOM.createPortal(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
          onClick={() => setAdminConfirm(null)}>
          <div style={{ background:'#111', border:'1px solid rgba(255,255,255,0.1)', borderTop:'2px solid #9D1515', padding:'36px 32px', maxWidth:420, width:'100%' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:'Inter,sans-serif', fontSize:9, fontWeight:700, letterSpacing:'0.3em', color: adminConfirm.type === 'batch_refund' ? '#4ade80' : '#9D1515', textTransform:'uppercase', marginBottom:10 }}>
              {adminConfirm.type === 'batch_refund' ? 'ISSUE REFUNDS' : adminConfirm.type === 'team' ? 'REMOVE TEAM' : adminConfirm.type === 'player' ? 'REMOVE PLAYER' : adminConfirm.type === 'dropin' ? 'REMOVE DROP-IN' : adminConfirm.type === 'waitlist' ? 'REMOVE FROM WAITLIST' : 'REMOVE FREE AGENT'}
            </div>
            <div style={{ fontFamily:"'Bebas Neue','Oswald',sans-serif", fontSize:28, color:'#fff', letterSpacing:'0.04em', marginBottom:12 }}>
              {adminConfirm.type === 'batch_refund' ? 'CONFIRM REFUND' : 'ARE YOU SURE?'}
            </div>
            {adminConfirm.type === 'batch_refund' ? (
              <div style={{ background:'rgba(74,222,128,0.06)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:2, padding:'12px 14px', marginBottom:20, display:'flex', gap:10, alignItems:'flex-start' }}>
                <span style={{ color:'#4ade80', fontSize:16, lineHeight:1 }}>↺</span>
                <div>
                  <div style={{ color:'#4ade80', fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:3 }}>STRIPE REFUND WILL BE ISSUED</div>
                  <div style={{ color:'rgba(74,222,128,0.65)', fontSize:11, lineHeight:1.55 }}>These free agents were never placed on a team. Their full registration fees will be automatically refunded via Stripe. This cannot be undone.</div>
                </div>
              </div>
            ) : adminConfirm.isPaid ? (
              <div style={{ background:'rgba(157,21,21,0.15)', border:'1px solid rgba(157,21,21,0.4)', borderRadius:2, padding:'10px 14px', marginBottom:16, display:'flex', gap:10, alignItems:'flex-start' }}>
                <span style={{ color:'#e05555', fontSize:16, lineHeight:1 }}>⚠</span>
                <div>
                  <div style={{ color:'#e05555', fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:3 }}>THIS REGISTRATION IS PAID</div>
                  <div style={{ color:'rgba(224,85,85,0.75)', fontSize:11, lineHeight:1.5 }}>A Stripe refund will be issued automatically when you confirm.</div>
                </div>
              </div>
            ) : null}
            <p style={{ fontFamily:'Inter,sans-serif', fontSize:13, color:'rgba(255,255,255,0.45)', lineHeight:1.65, margin:'0 0 24px' }}>
              {adminConfirm.type === 'batch_refund'
                ? `Refund ${adminConfirm.label}?`
                : adminConfirm.type === 'team'
                ? `This will remove ${adminConfirm.label} from the tournament. This cannot be undone.`
                : adminConfirm.type === 'waitlist'
                ? `Remove ${adminConfirm.label} from the waitlist? This cannot be undone.`
                : `Remove ${adminConfirm.label} from this registration?`}
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={adminConfirm.onConfirm}
                style={{ flex:1, background: adminConfirm.type === 'batch_refund' ? 'rgba(74,222,128,0.15)' : '#9D1515', border: adminConfirm.type === 'batch_refund' ? '1px solid rgba(74,222,128,0.4)' : 'none', color: adminConfirm.type === 'batch_refund' ? '#4ade80' : '#fff', fontFamily:"'Bebas Neue','Oswald',sans-serif", fontSize:15, letterSpacing:'0.12em', padding:'12px 0', cursor:'pointer' }}>
                {adminConfirm.type === 'batch_refund' ? 'YES, ISSUE REFUNDS →' : 'YES, REMOVE →'}
              </button>
              <button onClick={() => setAdminConfirm(null)}
                style={{ flex:1, background:'transparent', border:'1px solid rgba(255,255,255,0.15)', color:'rgba(255,255,255,0.5)', fontFamily:"'Bebas Neue','Oswald',sans-serif", fontSize:15, letterSpacing:'0.12em', padding:'12px 0', cursor:'pointer' }}>
                CANCEL
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      </>
      </AdminMfaGate>
    );
  }

  // Payment success screen
  if (showPaymentSuccess) {
    return (
      <PaymentSuccess
        sessionId={paymentSuccessSessionId}
        onMyEvents={() => {
          setShowPaymentSuccess(false);
          if (user) {
            setMyEventsTournaments(null);
            setMyEventsFa(null);
            setMyEventsDropins(null);
            Promise.all([
              supabase.from('tournament_registrations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
              supabase.from('tournament_free_agents').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
              supabase.from('drop_in_registrations').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
            ]).then(async ([{ data: t }, { data: fa }, { data: di }]) => {
              const faData = fa || [];
              const assignedIds = [...new Set(faData.filter(f => f.assigned_team_id).map(f => f.assigned_team_id))];
              let teamMap = {};
              if (assignedIds.length > 0) {
                const { data: teams } = await supabase.from('tournament_registrations').select('id, team_name, captain_name, season, players').in('id', assignedIds);
                (teams || []).forEach(tm => { teamMap[tm.id] = tm; });
              }
              setMyEventsTournaments(t || []);
              setMyEventsFa(faData.map(f => f.assigned_team_id ? { ...f, _assigned_team: teamMap[f.assigned_team_id] || null } : f));
              setMyEventsDropins(di || []);
            }).catch(() => {
              setMyEventsTournaments([]);
              setMyEventsFa([]);
              setMyEventsDropins([]);
            });
          }
          setShowMyEvents(true);
        }}
        onHome={() => { setShowPaymentSuccess(false); setShowMenuPage(true); }}
      />
    );
  }

  // My Events page
  if (showMyEvents) {
    const loading = myEventsTournaments === null && myEventsFa === null && myEventsDropins === null && myEventsOrders === null;

    const handleCancelDropin = async (ev) => {
      const paid = ev.payment_status === 'paid' || ev.paid === true;
      let within24 = false;
      let isPast = false;
      if (ev.session_id) {
        const { data: sessRow } = await supabase
          .from('drop_in_sessions').select('starts_at').eq('id', ev.session_id).maybeSingle();
        if (sessRow?.starts_at) {
          const startsAt = new Date(sessRow.starts_at).getTime();
          isPast = Date.now() > startsAt;
          within24 = !isPast && (startsAt - Date.now()) < 24 * 60 * 60 * 1000;
        }
      }
      setCancelDropinConfirm({ ev, paid, within24, isPast });
    };

    const getFreshToken = async () => {
      try {
        const { data } = await supabase.auth.refreshSession();
        if (data?.session?.access_token) return data.session.access_token;
      } catch (_) {}
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token;
    };

    const executeCancelDropin = async () => {
      const { ev } = cancelDropinConfirm;
      setCancelDropinConfirm(null);
      try {
        const token = await getFreshToken();
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-dropin`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ registration_id: ev.id }),
          }
        );
        const json = await res.json();
        if (!res.ok) { setOvrAlert({ title: 'CANCELLATION FAILED', message: json.error || 'that did not go through. email ovrleague@gmail.com and we will handle it.' }); return; }
        notifyWaitlist(ev.session_id);
        handleHamburgerNav('MY EVENTS');
      } catch {
        setOvrAlert({ title: 'SOMETHING BROKE', message: 'cancellation did not go through on our end. email ovrleague@gmail.com and we will sort it out.' });
      }
    };

    const executeCancelReg = async () => {
      const { ev } = cancelRegConfirm;
      setCancelRegConfirm(null);
      setMyEventsReceipt(null);
      try {
        const token = await getFreshToken();
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-tournament`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ registration_id: ev.id, type: ev._type }),
          }
        );
        const json = await res.json();
        if (!res.ok) { setOvrAlert({ title: 'CANCELLATION FAILED', message: json.error || 'that did not go through. email ovrleague@gmail.com and we will handle it.' }); return; }
        if (ev._type === 'team') setMyEventsTournaments(ts => ts.map(t => t.id === ev.id ? { ...t, payment_status: 'user_cancelled' } : t));
        else if (ev._type === 'fa') setMyEventsFa(fas => fas.map(f => f.id === ev.id ? { ...f, payment_status: 'user_cancelled' } : f));
      } catch {
        setOvrAlert({ title: 'SOMETHING BROKE', message: 'cancellation did not go through on our end. email ovrleague@gmail.com and we will sort it out.' });
      }
    };

    const TOURN_PER_PLAYER_CENTS = { blitz: 2500, szn6: 4000, carleton: 4000 };
    const MAX_ROSTER = 8;

    const openRosterEdit = async (team) => {
      setRosterEditTeam(team);
      setRosterDraft(Array.isArray(team.players) ? team.players.map(p => ({ ...p })) : []);
      setRosterFaPool(null);
      setRosterShowFa(false);
      setRosterAddForm(null);
      setRosterAddForm2(null);
      setRosterClaimingFa(null);
      // Fetch the latest players list from the server so cancelled FAs are removed
      const { data: fresh } = await supabase
        .from('tournament_registrations')
        .select('players')
        .eq('id', team.id)
        .maybeSingle();
      if (fresh?.players) {
        // Filter out any FA entries whose registration has since been cancelled
        const faIds = fresh.players.filter(p => p.fa_id).map(p => p.fa_id);
        if (faIds.length > 0) {
          const { data: faRows } = await supabase
            .from('tournament_free_agents')
            .select('id, payment_status')
            .in('id', faIds);
          const cancelledIds = new Set(
            (faRows || []).filter(f => f.payment_status === 'user_cancelled' || f.payment_status === 'admin_removed').map(f => f.id)
          );
          const activePlayers = fresh.players.filter(p => !p.fa_id || !cancelledIds.has(p.fa_id));
          setRosterDraft(activePlayers.map(p => ({ ...p })));
          setRosterEditTeam(prev => ({ ...prev, players: activePlayers }));
        } else {
          setRosterDraft(fresh.players.map(p => ({ ...p })));
          setRosterEditTeam(prev => ({ ...prev, players: fresh.players }));
        }
      }
    };

    const closeRosterEdit = () => {
      setRosterEditTeam(null);
      setRosterDraft([]);
      setRosterFaPool(null);
      setRosterShowFa(false);
      setRosterAddForm(null);
      setRosterAddForm2(null);
      setRosterClaimingFa(null);
    };

    const loadRosterFaPool = async () => {
      if (!rosterEditTeam || rosterFaLoading || rosterFaPool !== null) return;
      setRosterFaLoading(true);
      const { data } = await supabase
        .from('tournament_free_agents')
        .select('id, name, gender, position, user_id')
        .eq('tournament', rosterEditTeam.season)
        .eq('payment_status', 'paid')
        .is('assigned_team_id', null);
      setRosterFaPool(data || []);
      setRosterFaLoading(false);
    };

    const saveRosterNames = async () => {
      if (!rosterEditTeam) return;
      setRosterSaving(true);
      const { error } = await supabase
        .from('tournament_registrations')
        .update({ players: rosterDraft })
        .eq('id', rosterEditTeam.id);
      if (!error) {
        setMyEventsTournaments(ts => ts.map(t =>
          t.id === rosterEditTeam.id ? { ...t, players: rosterDraft } : t
        ));
        setRosterEditTeam(prev => ({ ...prev, players: rosterDraft }));
      } else {
        setOvrAlert({ title: 'SAVE FAILED', message: 'roster did not save. check your connection and try again.' });
      }
      setRosterSaving(false);
    };

    const checkoutAddPlayers = async () => {
      if (!rosterEditTeam || !rosterAddForm) return;
      const { firstName, lastName, gender, position } = rosterAddForm;
      if (!firstName.trim() || !lastName.trim() || !gender || !position) {
        setOvrAlert({ title: 'MISSING INFO', message: 'player 1 still needs some details. fill everything in and try again.' }); return;
      }
      const newPlayers = [{ firstName: firstName.trim(), lastName: lastName.trim(), gender, position }];
      if (rosterAddForm2) {
        const { firstName: f2, lastName: l2, gender: g2, position: p2 } = rosterAddForm2;
        if (!f2.trim() || !l2.trim() || !g2 || !p2) {
          setOvrAlert({ title: 'PLAYER 2 INCOMPLETE', message: 'finish filling in player 2 or remove them from the form.' }); return;
        }
        newPlayers.push({ firstName: f2.trim(), lastName: l2.trim(), gender: g2, position: p2 });
      }
      const perPlayerCents = TOURN_PER_PLAYER_CENTS[rosterEditTeam.season] || 2500;
      const halfCents = Math.round(perPlayerCents / 2);
      const totalCents = halfCents * newPlayers.length;
      const displayPrice = `$${(totalCents / 100).toFixed(2)}`;

      setRosterCheckingOut(true);
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const origin = window.location.origin;
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/add-roster-players`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${s?.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              registrationId: rosterEditTeam.id,
              newPlayers,
              successUrl: `${origin}/?payment_success=1&session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: `${origin}/?payment_cancelled=1`,
            }),
          }
        );
        const json = await res.json();
        if (!res.ok || json.error) { setOvrAlert({ title: 'CHECKOUT BLOCKED', message: json.error || 'something stopped the checkout. try again and if it keeps happening, email us.' }); setRosterCheckingOut(false); return; }
        try {
          localStorage.setItem('ovr_pending_payment', JSON.stringify([{
            type: 'roster_add',
            label: `${rosterEditTeam.team_name} · Add ${newPlayers.length} Player${newPlayers.length > 1 ? 's' : ''}`,
            sublabel: newPlayers.map(p => `${p.firstName} ${p.lastName} · ${p.position}`).join(', '),
            fee: displayPrice,
            confNum: rosterEditTeam.confirmation_number || '',
          }]));
        } catch (e) { /* quota */ }
        window.location.href = json.url;
      } catch (err) {
        setOvrAlert({ title: 'CHECKOUT FAILED', message: 'the payment page did not load. check your connection and try again.' });
        setRosterCheckingOut(false);
      }
    };

    const claimFreeAgent = async (fa) => {
      if (rosterClaimingFa) return;
      setRosterClaimingFa(fa.id);
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/claim-free-agent`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${s?.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ registrationId: rosterEditTeam.id, freeAgentId: fa.id }),
          }
        );
        const json = await res.json();
        if (!res.ok || json.error) { setOvrAlert({ message: json.error || 'Failed to claim free agent.' }); setRosterClaimingFa(null); return; }
        const newPlayer = json.newPlayer || {
          firstName: (fa.name || '').split(' ')[0] || '',
          lastName: (fa.name || '').split(' ').slice(1).join(' ') || '',
          gender: fa.gender || '',
          position: fa.position || '',
          fa_id: fa.id,
        };
        setRosterDraft(d => [...d, newPlayer]);
        setRosterEditTeam(prev => ({ ...prev, players: [...(prev.players || []), newPlayer] }));
        setMyEventsTournaments(ts => ts.map(t => t.id === rosterEditTeam.id ? { ...t, players: [...(t.players || []), newPlayer] } : t));
        setRosterFaPool(pool => pool ? pool.filter(f => f.id !== fa.id) : pool);
      } catch (err) {
        setOvrAlert({ title: 'FREE AGENT TAKEN', message: 'looks like someone else got there first. refresh and see who is still available.' });
      }
      setRosterClaimingFa(null);
    };

    const posPillStyle = (pos) => {
      const c = { S: ['#7c5cff','#b9a8ff'], OH: ['#d8a64a','#ecc77a'], MB: ['#6bd6a3','#94e7c0'], OPP: ['#f0686b','#ffa3a5'], L: ['#58b6e7','#8fd1f1'] }[pos] || ['#3a3a3a','#8a8a8a'];
      return { display:'inline-flex', alignItems:'center', justifyContent:'center', fontFamily:"'Bebas Neue',sans-serif", fontSize:11, letterSpacing:'0.08em', padding:'2px 8px', border:`1px solid ${c[0]}`, color:c[1], minWidth:32 };
    };
    const gPillStyle = (g) => ({
      display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22,
      fontFamily:"'JetBrains Mono',monospace", fontSize:10,
      border: g==='M'?'1px solid rgba(88,182,231,0.4)':g==='F'?'1px solid rgba(240,104,107,0.4)':'1px solid #3a3a3a',
      color: g==='M'?'#58b6e7':g==='F'?'#f0686b':'#8a8a8a', background:'#0e0e0e',
    });

    // Merge all event types + merch orders into one sorted timeline
    const allEvents = loading ? [] : [
      ...(myEventsDropins||[]).map(d => ({ ...d, _type: 'dropin' })),
      ...(myEventsTournaments||[]).map(t => ({ ...t, _type: 'team' })),
      ...(myEventsFa||[]).map(fa => ({ ...fa, _type: 'fa' })),
      ...(myEventsOrders||[]).map(o => ({ ...o, _type: 'merch', confirmation_number: merchOrderCode(o.id), amount_paid_cents: Math.round((o.total || 0) * 100) })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const levelLabel = { adv: 'Advanced', int: 'Intermediate', beg: 'Beginner' };
    const merchItemsSummary = (items) => (Array.isArray(items) ? items : [])
      .map(it => `${it.name || it.id}${it.size ? ` (${String(it.size).toUpperCase()})` : ''}${it.qty > 1 ? ` ×${it.qty}` : ''}`)
      .join(', ');

    const eventTitle = (ev) => {
      if (ev._type === 'dropin') return ev.session_label || 'Drop-In Session';
      if (ev._type === 'team')   return ev.team_name || ev.captain_name || 'Tournament Team';
      if (ev._type === 'fa')     return ev.tournament || 'Free Agent';
      if (ev._type === 'merch')  return 'Merch Order';
      return '-';
    };
    const eventSubtitle = (ev) => {
      if (ev._type === 'dropin') return [levelLabel[ev.level] || ev.level, ev.session_location].filter(Boolean).join(' · ');
      if (ev._type === 'team')   return [ev.season, ev.division].filter(Boolean).join(' · ');
      if (ev._type === 'fa')     return [ev.position, ev.gender].filter(Boolean).join(' · ');
      if (ev._type === 'merch')  return merchItemsSummary(ev.items);
      return '';
    };
    const typeLabel = { dropin: 'DROP-IN', team: 'TEAM REG', fa: 'FREE AGENT', merch: 'MERCH ORDER' };
    const typeCls   = { dropin: 'myev-card__type--dropin', team: 'myev-card__type--team', fa: 'myev-card__type--fa', merch: 'myev-card__type--team' };

    return (
      <div className="app" style={{ background: '#0a0a0a', minHeight: '100vh', width: '100vw', overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="logo-container-small" onClick={handleLogoClick} style={{ cursor: 'pointer', position: 'fixed', top: '20px', left: '20px', zIndex: 20 }}>
          <img src={'/ovr-logo.png'} alt="Ottawa Volleyball Revival" className="logo-small" />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}
        {myEventsReceipt && <ReceiptModal item={myEventsReceipt} onClose={() => setMyEventsReceipt(null)} onRequestCancel={ev => { setMyEventsReceipt(null); if (ev._type === 'dropin') { handleCancelDropin(ev); } else { setCancelRegConfirm({ ev, paid: ev.payment_status === 'paid' }); } }} />}

        {/* OVR alert — replaces native browser alert() */}
        {ovrAlert && (
          <div onClick={() => setOvrAlert(null)} style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#0e0e0e', border: '1px solid #2a2a2a', borderTop: '2px solid #9D1515', maxWidth: 400, width: '100%', padding: '28px 28px 24px' }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', color: '#9D1515', textTransform: 'uppercase', marginBottom: 10 }}>
                {ovrAlert.title || 'Hold up'}
              </div>
              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.04em', lineHeight: 1.15, marginBottom: 16 }}>
                {ovrAlert.message}
              </div>
              <button
                onClick={() => setOvrAlert(null)}
                style={{ width: '100%', background: '#9D1515', border: 'none', color: '#fff', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 14, letterSpacing: '0.14em', padding: '14px 0', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#c41e1e'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#9D1515'; }}
              >
                GOT IT
              </button>
            </div>
          </div>
        )}

        {/* OVR-styled cancel registration modal — team / FA */}
        {cancelRegConfirm && (
          <div onClick={() => setCancelRegConfirm(null)} style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#0e0e0e', border: '1px solid #2a2a2a', borderTop: '2px solid #9D1515', maxWidth: 420, width: '100%', padding: '28px 28px 24px' }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', color: '#9D1515', textTransform: 'uppercase', marginBottom: 10 }}>
                Cancel Registration
              </div>
              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 26, color: '#fff', letterSpacing: '0.04em', marginBottom: 16, lineHeight: 1 }}>
                {cancelRegConfirm.ev._type === 'team' ? (cancelRegConfirm.ev.team_name || 'Your team') : `Free agent · ${(cancelRegConfirm.ev.tournament || '').toUpperCase()}`}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, marginBottom: 20 }}>
                {cancelRegConfirm.paid
                  ? cancelRegConfirm.ev._type === 'team'
                    ? 'You will receive a full refund to your original payment method within 5–10 business days if cancelled at least 5 days before the tournament.'
                    : 'You will receive a full refund to your original payment method within 5–10 business days if cancelled at least 2 days before the tournament.'
                  : 'No payment was made so no refund applies.'
                }
              </div>
              <div style={{ background: 'rgba(157,21,21,0.08)', border: '1px solid rgba(157,21,21,0.25)', padding: '10px 14px', marginBottom: 22, fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>
                ⚠ This cannot be undone. {cancelRegConfirm.ev._type === 'team' ? 'Any free agents on your roster will be released back to the pool.' : ''}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setCancelRegConfirm(null)}
                  style={{ flex: 1, background: 'transparent', border: '1px solid #2a2a2a', color: 'rgba(255,255,255,0.55)', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 13, letterSpacing: '0.14em', padding: '12px 0', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#555'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#2a2a2a'}
                >
                  KEEP MY SPOT
                </button>
                <button
                  onClick={executeCancelReg}
                  style={{ flex: 1, background: '#9D1515', border: '1px solid #9D1515', color: '#fff', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 13, letterSpacing: '0.14em', padding: '12px 0', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#c41e1e'}
                  onMouseLeave={e => e.currentTarget.style.background = '#9D1515'}
                >
                  YES, CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        {/* OVR-styled cancel confirmation modal */}
        {cancelDropinConfirm && (
          <div onClick={() => setCancelDropinConfirm(null)} style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: '#0e0e0e', border: '1px solid #2a2a2a', borderTop: '2px solid #9D1515',
              maxWidth: 420, width: '100%', padding: '28px 28px 24px',
            }}>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', color: '#9D1515', textTransform: 'uppercase', marginBottom: 10 }}>
                Cancel Registration
              </div>
              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 26, color: '#fff', letterSpacing: '0.04em', marginBottom: 16, lineHeight: 1 }}>
                {cancelDropinConfirm.ev.session_label}
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, marginBottom: 20 }}>
                {cancelDropinConfirm.paid && cancelDropinConfirm.isPast
                  ? 'This session has already taken place. No refund will be issued.'
                  : cancelDropinConfirm.paid && cancelDropinConfirm.within24
                    ? 'You are within 24 hours of the session start. No refund per our cancellation policy.'
                    : cancelDropinConfirm.paid
                      ? 'You will receive a full refund to your original payment method within 5–10 business days.'
                      : 'No payment was made so no refund applies.'
                }
                {' '}Your spot will be released.
              </div>
              <div style={{ background: (cancelDropinConfirm.within24 || cancelDropinConfirm.isPast) ? 'rgba(157,21,21,0.15)' : 'rgba(157,21,21,0.08)', border: `1px solid ${(cancelDropinConfirm.within24 || cancelDropinConfirm.isPast) ? 'rgba(157,21,21,0.5)' : 'rgba(157,21,21,0.25)'}`, padding: '10px 14px', marginBottom: 22, fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>
                {cancelDropinConfirm.isPast
                  ? '⚠ This cannot be undone. Session already occurred. No refund applies.'
                  : cancelDropinConfirm.within24
                    ? '⚠ This cannot be undone. No refund will be issued. Cancellation was within 24 hours of start.'
                    : '⚠ This cannot be undone. Cancellation is free more than 24 hours before the session.'
                }
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setCancelDropinConfirm(null)}
                  style={{
                    flex: 1, background: 'transparent', border: '1px solid #2a2a2a', color: 'rgba(255,255,255,0.55)',
                    fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 13, letterSpacing: '0.14em',
                    padding: '12px 0', cursor: 'pointer', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#555'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#2a2a2a'}
                >
                  KEEP MY SPOT
                </button>
                <button
                  onClick={executeCancelDropin}
                  style={{
                    flex: 1, background: '#9D1515', border: '1px solid #9D1515', color: '#fff',
                    fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 13, letterSpacing: '0.14em',
                    padding: '12px 0', cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#c41e1e'}
                  onMouseLeave={e => e.currentTarget.style.background = '#9D1515'}
                >
                  YES, CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Roster edit panel */}
        {rosterEditTeam && (() => {
          const currentCount = (rosterEditTeam.players || []).length;
          const slotsLeft = Math.max(0, MAX_ROSTER - currentCount);
          const perPlayerCents = TOURN_PER_PLAYER_CENTS[rosterEditTeam.season] || 2500;
          const halfCents = Math.round(perPlayerCents / 2);
          const draftChanged = JSON.stringify(rosterDraft) !== JSON.stringify(rosterEditTeam.players);
          return (
            <>
              <div onClick={closeRosterEdit} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.65)' }} />
              <div style={{
                position: 'fixed', zIndex: 501, overflowY: 'auto',
                ...(isMobile
                  ? { bottom: 0, left: 0, right: 0, height: '56vh', borderTop: '2px solid #9D1515' }
                  : { top: 0, right: 0, bottom: 0, width: 400, borderLeft: '1px solid #2a2a2a' }),
                background: '#0e0e0e',
                display: 'flex', flexDirection: 'column',
              }}>
                {/* Panel header */}
                <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #1e1e1e', flexShrink: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#9D1515', marginBottom: 4 }}>EDIT ROSTER</div>
                      <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.04em', lineHeight: 1 }}>{rosterEditTeam.team_name}</div>
                      <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>{currentCount}/{MAX_ROSTER} players · {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} left</div>
                    </div>
                    <button onClick={closeRosterEdit} style={{ background: 'none', border: 'none', color: '#555', fontSize: 20, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}>✕</button>
                  </div>
                </div>

                {/* Roster rows */}
                <div style={{ padding: '14px 20px', flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#555', marginBottom: 10 }}>PLAYER NAMES</div>
                  {rosterDraft.map((p, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#3a3a3a', width: 20, flexShrink: 0 }}>{String(i+1).padStart(2,'0')}</span>
                      {p.fa_id ? (
                        <>
                          <span style={{ flex: 1, background: '#0e0e0e', border: '1px solid #1e1e1e', color: '#555', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '7px 9px', cursor: 'not-allowed', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.firstName || ''}</span>
                          <span style={{ flex: 1, background: '#0e0e0e', border: '1px solid #1e1e1e', color: '#555', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '7px 9px', cursor: 'not-allowed', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.lastName || ''}</span>
                        </>
                      ) : (
                        <>
                          <input
                            value={p.firstName || ''}
                            onChange={e => setRosterDraft(d => d.map((r, j) => j === i ? { ...r, firstName: e.target.value } : r))}
                            placeholder="First"
                            style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '7px 9px', outline: 'none' }}
                          />
                          <input
                            value={p.lastName || ''}
                            onChange={e => setRosterDraft(d => d.map((r, j) => j === i ? { ...r, lastName: e.target.value } : r))}
                            placeholder="Last"
                            style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '7px 9px', outline: 'none' }}
                          />
                        </>
                      )}
                      {p.gender && <span style={{ ...gPillStyle(p.gender), opacity: p.fa_id ? 0.45 : 1 }}>{p.gender}</span>}
                      {p.position && <span style={{ ...posPillStyle(p.position), opacity: p.fa_id ? 0.45 : 1 }}>{p.position}</span>}
                      {i === 0 && <span style={{ fontSize: 9, letterSpacing: '0.12em', color: '#9D1515', fontWeight: 700, flexShrink: 0 }}>CAPT</span>}
                      {p.fa_id && <span style={{ fontSize: 9, letterSpacing: '0.12em', color: '#6bd6a3', fontWeight: 700, flexShrink: 0 }}>FA</span>}
                    </div>
                  ))}
                  {draftChanged && (
                    <button
                      onClick={saveRosterNames}
                      disabled={rosterSaving}
                      style={{ width: '100%', marginTop: 10, background: '#9D1515', border: 'none', color: '#fff', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 13, letterSpacing: '0.16em', padding: '11px 0', cursor: rosterSaving ? 'default' : 'pointer', opacity: rosterSaving ? 0.6 : 1, transition: 'background 0.15s' }}
                      onMouseEnter={e => { if (!rosterSaving) e.currentTarget.style.background = '#c41e1e'; }}
                      onMouseLeave={e => { if (!rosterSaving) e.currentTarget.style.background = '#9D1515'; }}
                    >
                      {rosterSaving ? 'SAVING…' : 'SAVE NAMES'}
                    </button>
                  )}
                </div>

                {/* Add player */}
                {slotsLeft > 0 && (
                  <div style={{ padding: '14px 20px', borderTop: '1px solid #1e1e1e', flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#555', marginBottom: 10 }}>
                      ADD PLAYER <span style={{ color: '#3a3a3a' }}>· ${(halfCents/100).toFixed(2)}/player (half price)</span>
                    </div>
                    {rosterAddForm === null ? (
                      <button
                        onClick={() => setRosterAddForm({ firstName: '', lastName: '', gender: '', position: '' })}
                        style={{ width: '100%', background: 'transparent', border: '1px dashed #3a3a3a', color: '#555', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em', padding: '10px 0', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#9D1515'; e.currentTarget.style.color = '#e8e8e8'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#3a3a3a'; e.currentTarget.style.color = '#555'; }}
                      >+ ADD PLAYER</button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* Player 1 */}
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#555' }}>PLAYER 1</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input value={rosterAddForm.firstName} onChange={e => setRosterAddForm(f => ({ ...f, firstName: e.target.value }))} placeholder="First name" style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 9px', outline: 'none' }} />
                          <input value={rosterAddForm.lastName} onChange={e => setRosterAddForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Last name" style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 9px', outline: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {['M','F'].map(g => (
                            <button key={g} type="button" onClick={() => setRosterAddForm(f => ({ ...f, gender: f.gender === g ? '' : g }))}
                              style={{ flex: 1, background: rosterAddForm.gender === g ? (g === 'M' ? 'rgba(88,182,231,0.15)' : 'rgba(240,104,107,0.15)') : 'transparent', border: `1px solid ${rosterAddForm.gender === g ? (g === 'M' ? '#58b6e7' : '#f0686b') : '#2a2a2a'}`, color: rosterAddForm.gender === g ? (g === 'M' ? '#58b6e7' : '#f0686b') : '#555', fontFamily: "'JetBrains Mono',monospace", fontSize: 12, padding: '8px 0', cursor: 'pointer' }}>
                              {g === 'M' ? 'Male' : 'Female'}
                            </button>
                          ))}
                          <select value={rosterAddForm.position} onChange={e => setRosterAddForm(f => ({ ...f, position: e.target.value }))}
                            style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: rosterAddForm.position ? '#fff' : '#555', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 6px', outline: 'none' }}>
                            <option value="">Position</option>
                            {['S','OH','MB','OPP','L'].map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>

                        {/* Player 2 — optional, only if there's room */}
                        {slotsLeft > 1 && rosterAddForm2 === null && (
                          <button
                            onClick={() => setRosterAddForm2({ firstName: '', lastName: '', gender: '', position: '' })}
                            style={{ width: '100%', background: 'transparent', border: '1px dashed #2a2a2a', color: '#3a3a3a', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 11, letterSpacing: '0.12em', padding: '7px 0', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#888'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#3a3a3a'; }}
                          >+ ADD SECOND PLAYER</button>
                        )}
                        {rosterAddForm2 !== null && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#555' }}>PLAYER 2</div>
                              <button onClick={() => setRosterAddForm2(null)} style={{ background: 'none', border: 'none', color: '#555', fontSize: 11, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>✕ remove</button>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input value={rosterAddForm2.firstName} onChange={e => setRosterAddForm2(f => ({ ...f, firstName: e.target.value }))} placeholder="First name" style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 9px', outline: 'none' }} />
                              <input value={rosterAddForm2.lastName} onChange={e => setRosterAddForm2(f => ({ ...f, lastName: e.target.value }))} placeholder="Last name" style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 9px', outline: 'none' }} />
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {['M','F'].map(g => (
                                <button key={g} type="button" onClick={() => setRosterAddForm2(f => ({ ...f, gender: f.gender === g ? '' : g }))}
                                  style={{ flex: 1, background: rosterAddForm2.gender === g ? (g === 'M' ? 'rgba(88,182,231,0.15)' : 'rgba(240,104,107,0.15)') : 'transparent', border: `1px solid ${rosterAddForm2.gender === g ? (g === 'M' ? '#58b6e7' : '#f0686b') : '#2a2a2a'}`, color: rosterAddForm2.gender === g ? (g === 'M' ? '#58b6e7' : '#f0686b') : '#555', fontFamily: "'JetBrains Mono',monospace", fontSize: 12, padding: '8px 0', cursor: 'pointer' }}>
                                  {g === 'M' ? 'Male' : 'Female'}
                                </button>
                              ))}
                              <select value={rosterAddForm2.position} onChange={e => setRosterAddForm2(f => ({ ...f, position: e.target.value }))}
                                style={{ flex: 1, background: '#111', border: '1px solid #2a2a2a', color: rosterAddForm2.position ? '#fff' : '#555', fontFamily: 'Inter, sans-serif', fontSize: 12, padding: '8px 6px', outline: 'none' }}>
                                <option value="">Position</option>
                                {['S','OH','MB','OPP','L'].map(r => <option key={r} value={r}>{r}</option>)}
                              </select>
                            </div>
                          </>
                        )}

                        {/* Action row */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => { setRosterAddForm(null); setRosterAddForm2(null); }} style={{ flex: 1, background: 'transparent', border: '1px solid #2a2a2a', color: '#555', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.12em', padding: '9px 0', cursor: 'pointer' }}>CANCEL</button>
                          <button
                            onClick={checkoutAddPlayers}
                            disabled={rosterCheckingOut}
                            style={{ flex: 2, background: '#9D1515', border: 'none', color: '#fff', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em', padding: '9px 0', cursor: rosterCheckingOut ? 'default' : 'pointer', opacity: rosterCheckingOut ? 0.6 : 1 }}
                          >
                            {rosterCheckingOut ? 'REDIRECTING…' : `CHECKOUT → $${((halfCents * (rosterAddForm2 ? 2 : 1))/100).toFixed(2)}`}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Browse FA pool */}
                <div style={{ padding: '14px 20px', borderTop: '1px solid #1e1e1e', flexShrink: 0 }}>
                  <button
                    onClick={() => { setRosterShowFa(v => !v); if (!rosterShowFa) loadRosterFaPool(); }}
                    style={{ width: '100%', background: 'transparent', border: '1px solid #2a2a2a', color: '#888', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em', padding: '10px 0', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#9D1515'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
                  >
                    {rosterShowFa ? '▲ HIDE FA POOL' : '▼ BROWSE FREE AGENTS'}
                  </button>
                  {rosterShowFa && (
                    <div style={{ marginTop: 10 }}>
                      {rosterDraft.length >= MAX_ROSTER ? (
                        <div style={{ background: '#131313', border: '1px solid #2a2a2a', borderLeft: '3px solid #9D1515', padding: '20px 22px', marginTop: 4 }}>
                          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#9D1515', fontWeight: 700, marginBottom: 10 }}>ROSTER LOCKED</div>
                          <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 28, letterSpacing: '0.04em', color: '#fff', lineHeight: 1, marginBottom: 8 }}>8 / 8 · FULL</div>
                          <div style={{ fontSize: 12, color: '#555', lineHeight: 1.65 }}>Your roster is at capacity. The maximum is 8 players per team. To add someone from the free agent pool, remove an existing player first.</div>
                        </div>
                      ) : rosterFaLoading ? (
                        <div style={{ fontSize: 11, color: '#555', padding: '10px 0', letterSpacing: '0.12em' }}>LOADING…</div>
                      ) : !rosterFaPool || rosterFaPool.filter(fa => !rosterDraft.some(p => p.fa_id === fa.id)).length === 0 ? (
                        <div style={{ fontSize: 12, color: '#3a3a3a', padding: '10px 0' }}>No free agents available for this tournament.</div>
                      ) : rosterFaPool.filter(fa => !rosterDraft.some(p => p.fa_id === fa.id)).map(fa => {
                        const parts = (fa.name || '').split(' '); // eslint-disable-line no-unused-vars
                        return (
                          <div key={fa.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid #1a1a1a' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: '#e8e8e8', fontWeight: 600 }}>{fa.name}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              {fa.gender && <span style={gPillStyle(fa.gender)}>{fa.gender}</span>}
                              {fa.position && <span style={posPillStyle(fa.position)}>{fa.position}</span>}
                            </div>
                            <button
                              onClick={() => claimFreeAgent(fa)}
                              disabled={!!rosterClaimingFa}
                              style={{ background: rosterClaimingFa === fa.id ? 'rgba(107,214,163,0.12)' : 'transparent', border: `1px solid ${rosterClaimingFa === fa.id ? '#6bd6a3' : '#3a3a3a'}`, color: rosterClaimingFa === fa.id ? '#6bd6a3' : '#9D1515', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 11, letterSpacing: '0.1em', padding: '4px 10px', cursor: rosterClaimingFa ? 'default' : 'pointer', opacity: rosterClaimingFa && rosterClaimingFa !== fa.id ? 0.4 : 1 }}
                              onMouseEnter={e => { if (!rosterClaimingFa) { e.currentTarget.style.background = 'rgba(157,21,21,0.12)'; e.currentTarget.style.borderColor = '#9D1515'; } }}
                              onMouseLeave={e => { if (!rosterClaimingFa) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#3a3a3a'; } }}
                            >{rosterClaimingFa === fa.id ? 'ADDING…' : 'ADD +'}</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          );
        })()}

        <div className="myev-page">
          {/* Header */}
          <div className="myev-header">
            <div className="myev-eyebrow">YOUR PROFILE</div>
            <h1 className="myev-title">MY EVENTS &amp; ORDERS</h1>
            {user && (
              <div className="myev-header__meta">
                <span className="myev-header__name">{user.user_metadata?.full_name || (user.email ? user.email.split('@')[0] : 'PLAYER')}</span>
                <span className="myev-header__email">{user.email || ''}</span>
                {!loading && <span className="myev-header__count">{allEvents.length} item{allEvents.length !== 1 ? 's' : ''}</span>}
                <button onClick={() => { setMyEventsTournaments(null); setMyEventsFa(null); setMyEventsDropins(null); setMyEventsOrders(null); setMyEventsRefreshKey(k => k + 1); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter,sans-serif', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', padding: '2px 0', marginTop: 4 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}>
                  ↻ Refresh
                </button>
              </div>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="myev-loading">
              <div className="myev-loading__bar" />
              <div style={{ marginTop: 14, fontSize: 11, letterSpacing: '0.18em', color: '#444', fontFamily: "'Inter',sans-serif", textTransform: 'uppercase' }}>Loading your events…</div>
            </div>
          )}

          {/* Empty */}
          {!loading && allEvents.length === 0 && (
            <div className="myev-empty">
              <div className="myev-empty__title">NOTHING HERE YET</div>
              <div className="myev-empty__sub">Sign up for a tournament, join a drop-in, or grab some merch and it'll show up here with your receipt.</div>
              <div className="myev-empty__actions">
                <button className="myev-empty__cta" onClick={() => handleHamburgerNav('TOURNAMENT_REGISTER')}>BROWSE TOURNAMENTS →</button>
                <button className="myev-empty__cta myev-empty__cta--ghost" onClick={() => handleHamburgerNav('DROP INS')}>SIGN UP FOR DROP-IN →</button>
              </div>
            </div>
          )}

          {/* Event cards */}
          {!loading && allEvents.length > 0 && (
            <div className="myev-list">
              {allEvents.map(ev => {
                const adminRemoved = ev.payment_status === 'admin_removed';
                const faRefunded   = ev.payment_status === 'refunded';
                const paid = ev._type === 'merch'
                  ? (ev.status === 'paid' || ev.status === 'ready')
                  : !adminRemoved && !faRefunded && (ev.payment_status === 'paid' || ev.paid === true);
                const confNum = ev.confirmation_number;
                const regDate = new Date(ev.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });

                // Location + time line
                let locationLine = null;
                if (ev._type === 'dropin') {
                  const parts = [ev.session_time, ev.session_location].filter(Boolean);
                  if (parts.length) locationLine = parts.join(' · ');
                }

                // Player list for team cards
                const playerNames = ev._type === 'team' && Array.isArray(ev.players)
                  ? ev.players.map(p => typeof p === 'string' ? p : [p.firstName, p.lastName].filter(Boolean).join(' ')).filter(Boolean)
                  : [];

                return (
                  <div key={`${ev._type}-${ev.id}`} className="myev-card" style={adminRemoved ? { borderColor: ev.stripe_payment_intent_id ? 'rgba(74,222,128,0.25)' : 'rgba(157,21,21,0.4)', opacity: 0.9 } : faRefunded ? { borderColor: 'rgba(74,222,128,0.25)', opacity: 0.9 } : {}}>
                    {/* Admin removed banner */}
                    {adminRemoved && (() => {
                      const wasRefunded = !!ev.stripe_payment_intent_id;
                      return wasRefunded ? (
                        <div style={{ background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 2, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <span style={{ color: '#4ade80', fontSize: 15, lineHeight: 1, flexShrink: 0 }}>✓</span>
                          <div>
                            <div style={{ color: '#4ade80', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>Registration Removed - Refund Issued</div>
                            <div style={{ color: 'rgba(74,222,128,0.6)', fontSize: 11, lineHeight: 1.55 }}>Your registration was removed by an admin. Your full payment has been refunded to your original payment method.</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ background: 'rgba(157,21,21,0.12)', border: '1px solid rgba(157,21,21,0.3)', borderRadius: 2, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <span style={{ color: '#e05555', fontSize: 15, lineHeight: 1, flexShrink: 0 }}>⚠</span>
                          <div>
                            <div style={{ color: '#e05555', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>Registration Removed by Admin</div>
                            <div style={{ color: 'rgba(224,85,85,0.65)', fontSize: 11, lineHeight: 1.55 }}>Your registration was removed. No payment was on file so no refund applies. Contact us if you have questions.</div>
                          </div>
                        </div>
                      );
                    })()}
                    {/* FA refunded banner */}
                    {faRefunded && (
                      <div style={{ background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 2, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{ color: '#4ade80', fontSize: 15, lineHeight: 1, flexShrink: 0 }}>✓</span>
                        <div>
                          <div style={{ color: '#4ade80', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>Registration Refunded</div>
                          <div style={{ color: 'rgba(74,222,128,0.6)', fontSize: 11, lineHeight: 1.55 }}>You were not placed on a team 24 hours before the tournament - your full registration fee has been refunded to your original payment method.</div>
                        </div>
                      </div>
                    )}
                    {/* Top row: type badge + payment badge */}
                    <div className="myev-card__topbar">
                      <span className={`myev-card__type ${typeCls[ev._type]}`}>{typeLabel[ev._type]}</span>
                      {paid && (
                        <span className="myev-card__paid myev-card__paid--yes">✓ PAID</span>
                      )}
                    </div>

                    {/* Event name */}
                    <div className="myev-card__name">{eventTitle(ev)}</div>

                    {/* FA placed on team banner + roster preview */}
                    {ev._type === 'fa' && ev.assigned_team_id && (() => {
                      const team = ev._assigned_team;
                      const players = Array.isArray(team?.players) ? team.players : [];
                      const seasonName = { blitz: 'OVR BLITZ', szn6: 'OVR CUP SZN VI', carleton: 'OVR × CARLETON FUNDRAISER' }[team?.season] || (team?.season || '').toUpperCase();
                      return (
                        <div style={{ background: 'rgba(107,214,163,0.05)', border: '1px solid rgba(107,214,163,0.2)', borderLeft: '3px solid #6bd6a3', padding: '16px 18px', marginBottom: 12 }}>
                          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6bd6a3', fontWeight: 700, marginBottom: 6 }}>Placed on a Team</div>
                          {team ? (
                            <>
                              <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 20, letterSpacing: '0.04em', color: '#fff', lineHeight: 1, marginBottom: 2 }}>{team.team_name}</div>
                              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#555', letterSpacing: '0.12em', marginBottom: 12 }}>{seasonName} · {players.length} PLAYER{players.length !== 1 ? 'S' : ''}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {players.map((p, pi) => {
                                  const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || `Player ${pi + 1}`;
                                  const isYou = !!p.fa_id && p.fa_id === ev.id;
                                  return (
                                    <div key={pi} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: isYou ? 'rgba(107,214,163,0.1)' : '#0e0e0e', border: `1px solid ${isYou ? 'rgba(107,214,163,0.3)' : '#1e1e1e'}` }}>
                                      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#3a3a3a', width: 16, flexShrink: 0 }}>{String(pi + 1).padStart(2, '0')}</span>
                                      <span style={{ flex: 1, fontSize: 12, fontWeight: isYou ? 700 : 400, color: isYou ? '#6bd6a3' : '#ccc' }}>{name}</span>
                                      {p.position && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.1em', color: isYou ? '#6bd6a3' : '#555', flexShrink: 0 }}>{p.position}</span>}
                                      {p.gender && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#3a3a3a', flexShrink: 0 }}>{p.gender}</span>}
                                      {pi === 0 && <span style={{ fontSize: 8, letterSpacing: '0.12em', color: '#9D1515', fontWeight: 700, flexShrink: 0 }}>CAPT</span>}
                                      {isYou && <span style={{ fontSize: 8, letterSpacing: '0.12em', color: '#6bd6a3', fontWeight: 700, flexShrink: 0 }}>YOU</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          ) : (
                            <div style={{ color: 'rgba(107,214,163,0.6)', fontSize: 11, lineHeight: 1.55 }}>A captain has added you to their roster. No further action needed.</div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Subtitle / FA badges */}
                    {ev._type === 'fa' ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                        {ev.position && <span style={posPillStyle(ev.position)}>{ev.position}</span>}
                        {ev.gender && <span style={gPillStyle(ev.gender)}>{ev.gender}</span>}
                        {ev.tournament && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#555', padding: '2px 0' }}>{ev.tournament.toUpperCase()}</span>}
                      </div>
                    ) : eventSubtitle(ev) ? (
                      <div className="myev-card__subtitle">{eventSubtitle(ev)}</div>
                    ) : null}

                    {/* Merch item photos */}
                    {ev._type === 'merch' && Array.isArray(ev.items) && ev.items.length > 0 && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '12px 0 4px' }}>
                        {ev.items.map((it, i) => {
                          const img = merchItemImage(it.id);
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0e0e0e', border: '1px solid #1e1e1e', padding: '6px 12px 6px 6px' }}>
                              {img ? (
                                <img src={img} alt={it.name || it.id} style={{ width: 44, height: 44, objectFit: 'cover', background: '#161616' }} />
                              ) : (
                                <div style={{ width: 44, height: 44, background: '#161616' }} />
                              )}
                              <div>
                                <div style={{ fontSize: 12, color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>{it.name || it.id}</div>
                                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#777', letterSpacing: '0.06em' }}>
                                  {it.size ? `SIZE ${String(it.size).toUpperCase()}` : ''}{it.qty > 1 ? ` · ×${it.qty}` : ''}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Location / time */}
                    {locationLine && (
                      <div className="myev-card__location">
                        <span className="myev-card__location-icon">📍</span>{locationLine}
                      </div>
                    )}

                    {/* Confirmation number - hero element */}
                    <div className="myev-card__conf-row">
                      <span className="myev-card__conf-label">CONFIRMATION #</span>
                      <span className="myev-card__conf-num">{confNum || '-'}</span>
                    </div>

                    {/* Roster preview for team registrations */}
                    {playerNames.length > 0 && (
                      <div className="myev-card__roster">
                        <span className="myev-card__roster-label">ROSTER</span>
                        <span className="myev-card__roster-names">
                          {playerNames.slice(0, 4).join(', ')}{playerNames.length > 4 ? ` +${playerNames.length - 4} more` : ''}
                        </span>
                      </div>
                    )}

                    {/* Captain total paid — shown directly on team card */}
                    {ev._type === 'team' && ev.amount_paid_cents > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #1e1e1e', marginTop: 4 }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#555' }}>TOTAL PAID</span>
                        <span style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 18, color: '#6bd6a3', letterSpacing: '0.04em' }}>${(ev.amount_paid_cents / 100).toFixed(2)} CAD</span>
                      </div>
                    )}

                    {/* Merch order — total + pickup status */}
                    {ev._type === 'merch' && (
                      <>
                        {ev.status === 'ready' && (
                          <div style={{ background: 'rgba(107,214,163,0.08)', border: '1px solid rgba(107,214,163,0.3)', padding: '10px 14px', margin: '4px 0 10px', fontSize: 12, color: '#6bd6a3', fontWeight: 600 }}>
                            ✓ READY FOR PICKUP — grab it at league play
                          </div>
                        )}
                        {ev.amount_paid_cents > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #1e1e1e', marginTop: 4 }}>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#555' }}>TOTAL PAID</span>
                            <span style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 18, color: '#6bd6a3', letterSpacing: '0.04em' }}>${(ev.amount_paid_cents / 100).toFixed(2)} CAD</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Footer: date + actions */}
                    <div className="myev-card__footer">
                      <span className="myev-card__date">{ev._type === 'merch' ? 'Ordered' : 'Registered'} {regDate}</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {!adminRemoved && !faRefunded && ev.payment_status !== 'user_cancelled' && (
                          <button className="myev-card__receipt-btn" onClick={() => setMyEventsReceipt(ev)}>
                            {ev._type === 'fa' ? 'YOUR RECEIPT →' : 'VIEW RECEIPT →'}
                          </button>
                        )}
                        {ev._type === 'team' && !adminRemoved && ev.payment_status !== 'user_cancelled' && (
                          <button
                            onClick={() => openRosterEdit(ev)}
                            style={{
                              background: '#9D1515', border: 'none',
                              color: '#fff', fontFamily: "'Bebas Neue','Oswald',sans-serif",
                              fontSize: 13, letterSpacing: '0.14em', padding: '8px 18px', cursor: 'pointer',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#b01a1a'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#9D1515'; }}
                          >
                            EDIT ROSTER
                          </button>
                        )}
                      </div>
                    </div>
                    {/* User cancelled banner */}
                    {ev.payment_status === 'user_cancelled' && (
                      <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid #2a2a2a', padding: '10px 14px', fontSize: 12, color: '#666' }}>
                        You cancelled this registration.{ev.stripe_payment_intent_id ? (ev._type === 'dropin' ? ' If cancelled outside the 24-hour window, a refund minus the Stripe processing fee was issued to your original payment method.' : ' A refund minus the Stripe processing fee was issued to your original payment method.') : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // New drop-in flow
  if (showDropInFlow && DROPIN_REGISTRATIONS_OPEN) {
    return (
      <>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        <DropInFlow onClose={() => setShowDropInFlow(false)} onMyEvents={() => { setShowDropInFlow(false); handleHamburgerNav('MY EVENTS'); }} userAvatar={userAvatar} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}
      </>
    );
  }

  // Upcoming Events Page
  if (showUpcomingEvents) {
    return (
      <div className="app" style={{ background: '#000', minHeight: '100vh', width: '100vw', position: 'relative', overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="logo-container-small" onClick={handleLogoClick} style={{ cursor: 'pointer', position: 'fixed', top: '20px', left: '20px', zIndex: 20 }}>
          <img src={'/ovr-logo.png'} alt="Ottawa Volleyball Revival" className="logo-small" />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        {/* Upcoming Events — OVR × Carleton fundraiser */}
        <div style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-start' : 'center', alignItems: 'center', padding: isMobile ? '96px 20px 60px' : '80px 40px' }}>
          <p className="ovr-anim-rise" style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.28em', color: '#9D1515', margin: '0 0 28px', textTransform: 'uppercase', alignSelf: isMobile ? 'center' : 'auto' }}>
            Upcoming Events
          </p>

          <div style={{ width: '100%', maxWidth: 980, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 32 : 56, alignItems: 'center' }}>

            {/* Poster */}
            <div className="ovr-anim-pop" style={{ flexShrink: 0, width: isMobile ? '86%' : 360, margin: isMobile ? '0 auto' : 0, animationDelay: '0.1s' }}>
              <img
                src="/ovr-x-carleton.jpg"
                alt="OVR × Carleton co-ed volleyball tournament fundraiser, October 25, 1–9 PM, Norm Fenn Gym"
                className="ovr-lift"
                style={{ width: '100%', display: 'block', borderRadius: 6, boxShadow: '0 12px 48px rgba(157,21,21,0.3)' }}
              />
            </div>

            {/* Details */}
            <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
              <p className="ovr-anim-rise" style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.24em', color: '#9D1515', margin: '0 0 10px', textTransform: 'uppercase', animationDelay: '0.2s' }}>Co-Ed Volleyball Tournament · Fundraiser</p>
              <h2 className="ovr-anim-rise" style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 56 : 80, color: '#fff', margin: '0 0 2px', lineHeight: 0.88, letterSpacing: '0.02em', animationDelay: '0.28s' }}>
                OVR <span style={{ color: 'rgba(255,255,255,0.5)' }}>×</span> <span style={{ color: '#9D1515', fontStyle: 'italic' }}>CARLETON</span>
              </h2>
              <p className="ovr-anim-rise" style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 16, color: 'rgba(255,255,255,0.35)', margin: '8px 0 32px', letterSpacing: '0.14em', animationDelay: '0.34s' }}>SAVE THE DATE · OVR THE REST</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36, alignItems: isMobile ? 'center' : 'flex-start' }}>
                {[
                  { label: 'Date',   value: 'Sunday, October 25  ·  1 – 9 PM' },
                  { label: 'Venue',  value: 'Norm Fenn Gymnasium · Carleton University' },
                  { label: 'Format', value: '10 Teams · 2 Pools · Playoff Bracket' },
                  { label: 'Prizes', value: 'Prizes available for the winners' },
                  { label: 'Price',  value: '$40 / player  ·  Free agents $40' },
                ].map(({ label, value }, i) => (
                  <div key={label} className="ovr-anim-slide" style={{ display: 'flex', gap: 16, alignItems: 'baseline', animationDelay: `${0.42 + i * 0.07}s` }}>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, fontWeight: 700, color: '#9D1515', letterSpacing: '0.2em', textTransform: 'uppercase', minWidth: 52, textAlign: 'left' }}>{label}</span>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>

              <div className="ovr-anim-rise" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start', animationDelay: '0.8s' }}>
                <button
                  className="ovr-shine"
                  onClick={() => { setShowUpcomingEvents(false); setShowTournamentTeamForm(true); }}
                  style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 15, letterSpacing: '0.2em', background: '#9D1515', color: '#fff', border: 'none', padding: '15px 38px', cursor: 'pointer', transition: 'background 0.2s, transform 0.25s, box-shadow 0.25s', borderRadius: 3 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#c41e1e'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 26px rgba(157,21,21,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#9D1515'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  SIGN UP NOW →
                </button>
                <button
                  onClick={() => setShowCupInfo(true)}
                  style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 14, letterSpacing: '0.18em', background: 'transparent', color: 'rgba(255,255,255,0.75)', border: 'none', borderBottom: '1px solid #9D1515', padding: '6px 0', cursor: 'pointer' }}
                >
                  HOW THE TOURNAMENT WORKS
                </button>
              </div>
            </div>
          </div>
        </div>
        <BlitzInfoModal open={showBlitzInfo} onClose={() => setShowBlitzInfo(false)} />
        <OvrCupInfoModal open={showCupInfo} event={CUP_INFO.carleton} onClose={() => setShowCupInfo(false)} />
      </div>
    );
  }

  // About Us Page
  if (showAboutUs) {
    return (
      <div ref={aboutUsPageRef} key="about-page" className="app about-page">
        <div
          className="logo-container-small about-back-button"
          onClick={handleLogoClick}
        >
          <img
            src={'/ovr-logo.png'}
            alt="Ottawa Volleyball Revival"
            className="logo-small"
          />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        <div className="about-split">
          <Reveal variant="left" className="about-section about-section--divider">
            <img
              src={'/ovr-logo.png'}
              alt="OVR League"
              className="about-logo"
            />
            <h2 className="about-heading">OVR LEAGUE</h2>
            <p className="about-tagline">Reigniting Volleyball in Ottawa</p>
            <p className="about-text">
              OVR is dedicated to bringing back the volleyball hype by hosting the most exciting drop-ins and competitive tournaments across the city.
            </p>
            <p className="about-text">
              We create spaces where athletes, students, and friends come together for real competition, real memories, and real growth.
            </p>
            <p className="about-highlight">
              Our goal: make volleyball fun, accessible, and alive again in Ottawa.
            </p>
          </Reveal>

          <Reveal variant="right" delay={120} className="about-section">
            <img
              src={'/horizon_events_transparent.png'}
              alt="Horizon Events Official"
              className="about-logo"
            />
            <h2 className="about-heading">HORIZON EVENTS OFFICIAL</h2>
            <p className="about-tagline">Unforgettable Experiences Beyond the Court</p>
            <p className="about-text">
              Horizon Events focuses on seasonal and travel-based experiences, like out-of-city volleyball trips and community get-togethers.
            </p>
            <p className="about-text">
              We go beyond the game, curating moments that bring people together, from tournaments to travel adventures that stay with you for life.
            </p>
            <p className="about-highlight">
              Currently: Horizon is working on its first official trip in collaboration with OVR, launching within the next couple of weeks. An experience designed to take volleyball to new places and new heights.
            </p>
          </Reveal>

        </div>

        <Reveal variant="scale" className="about-mission">
          <div className="about-mission__eyebrow">THE MISSION</div>
          <h2 className="about-mission__heading">ONE COMMUNITY.<br />TWO FORCES.</h2>
          <p className="about-mission__body">OVR and Horizon share the same vision: to revive the love of volleyball and push it to the highest level Ottawa has ever seen.</p>
          <p className="about-mission__body">Together, we're building a connected community that plays hard, laughs harder, and always looks forward to the next event.</p>
        </Reveal>

        {/* Stats */}
        <AboutStats scrollRef={aboutUsPageRef} />

        {/* Community Reviews */}
        <AnimatedTestimonials
          title="WHAT PEOPLE ARE SAYING"
          subtitle="Real reviews from the OVR community"
          testimonials={[
            { id: 1,  handle: 'Sahar Mankal',           avatar: '🏐', source: 'google',    content: 'I think every sports league needs to have organizers like the two guys that run OVR. The level of care they put into every event is unmatched.', rating: 5 },
            { id: 2,  handle: 'Gajapriyan Vigneswaran', avatar: '⚡', source: 'google',    content: 'Great volleyball events with a fun, organized atmosphere and a strong competitive environment.', rating: 5 },
            { id: 3,  handle: 'Amin Galuta',            avatar: '🌅', source: 'google',    content: 'Super organized and level of competitiveness is really good. The only volleyball hosts that care about their players.', rating: 5 },
            { id: 4,  handle: 'Youssef Ghoneim',        avatar: '🔥', source: 'google',    content: 'This is the best volleyball league in the city! Hosts awesome events, always vibes and just a good time. (Also competitive)', rating: 5 },
            { id: 5,  handle: 'Kareem Hassan',          avatar: '👑', source: 'google',    content: 'Loved the service! Just 2 young future-millionaires with a dream 😁❤️', rating: 5 },
            { id: 6,  handle: 'Lane 9',                 avatar: '📸', source: 'google',    content: 'Staff were super friendly, had an amazing time at their drop ins and felt very welcomed 👌', rating: 5 },
            { id: 7,  handle: 'Mohammed Hassan',        avatar: '🎯', source: 'google',    content: 'Organized an awesome volleyball event and had so much fun.', rating: 5 },
            { id: 8,  handle: 'Bilal Barmaki',          avatar: '✨', source: 'google',    content: 'Great tournaments with great turnouts 🔥', rating: 5 },
            { id: 9,  handle: '@jasmineT.vball',       avatar: '🏐', source: 'instagram', content: 'Best drop-in I have been to in Ottawa, no contest. Competitive but super welcoming at the same time. Already booked my spot for the next one.', rating: 5 },
            { id: 10, handle: '@nadia.serves',         avatar: '🌟', source: 'instagram', content: 'Came to a drop-in not knowing anyone and left with a whole squad. That is the OVR effect. Signed up for the next one before I even got home.', rating: 5 },
            { id: 11, handle: '@priya_spikes',         avatar: '💪', source: 'instagram', content: 'Finally a volleyball event in Ottawa that actually delivers on the hype. Registration was smooth, the court setup was great, and the refs actually knew what they were doing.', rating: 5 },
            { id: 12, handle: '@kevinott.22',          avatar: '🎉', source: 'instagram', content: 'Season 4 was absolutely insane. The pool play format kept the energy going all day and the gym was loud from start to finish.', rating: 5 },
          ]}
        />

        {/* Review prompt */}
        <Reveal className="about-review-bar">
          <span className="about-review-bar__stars">★★★★★</span>
          <span className="about-review-bar__text">
            Played with OVR? Drop an honest Google review. Every review helps the community grow and brings more players to the court.
          </span>
          <a
            href="https://g.page/r/CY18uJzb1rm9EAE/review"
            target="_blank"
            rel="noopener noreferrer"
            className="about-review-bar__link"
          >
            Leave a Google Review ↗
          </a>
        </Reveal>

        {/* Contact CTA */}
        <Reveal style={{ background: '#0a0a0a', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '72px 24px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.26em', color: '#9D1515', textTransform: 'uppercase', margin: '0 0 16px' }}>
            Get In Touch
          </p>
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 'clamp(36px, 6vw, 64px)', color: '#fff', letterSpacing: '0.04em', margin: '0 0 14px', lineHeight: 1 }}>
            QUESTIONS? WE'VE GOT YOU.
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.75, maxWidth: 420, margin: '0 auto 28px' }}>
            Registration questions, refunds, sponsorships, or just want to say hi. Reach out and we'll get back to you within 24 hours.
          </p>
          <a
            href="/contact"
            style={{
              display: 'inline-block',
              background: '#9D1515', color: '#fff', textDecoration: 'none',
              fontFamily: "'Bebas Neue','Oswald',sans-serif",
              fontSize: 14, letterSpacing: '0.18em',
              padding: '14px 32px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#c41e1e'}
            onMouseLeave={e => e.currentTarget.style.background = '#9D1515'}
          >
            CONTACT OVR →
          </a>
        </Reveal>

        {/* FAQ */}
        <FAQ />

        {/* Site Footer */}
        <SiteFooter onNavigate={(section) => {
          setShowAboutUs(false);
          if (section === 'UPCOMING EVENTS') setShowUpcomingEvents(true);
          else if (section === 'TOURNAMENTS') setShowTournamentsPage(true);
          else if (section === 'GALLERY') setShowGallery(true);
          else if (section === 'SPONSORS') setShowSponsors(true);
          else if (section === 'ABOUT US') { /* already here */ }
        }} />
      </div>
    );
  }

  // Gallery Page
  if (showGallery) {
    return (
      <div ref={galleryScrollRef} style={{
        background: '#080808',
        width: '100vw',
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
      }}>
        {/* Fixed nav */}
        <div
          className="logo-container-small"
          onClick={handleLogoClick}
          style={{ cursor: 'pointer', position: 'fixed', top: '20px', left: '20px', zIndex: 100 }}
        >
          <img
            src={'/ovr-logo.png'}
            alt="Ottawa Volleyball Revival"
            className="logo-small"
          />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        {/* Hero heading */}
        <div style={{
          paddingTop: '120px',
          paddingBottom: '0',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
        }}>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#9D1515',
            margin: '0 0 12px 0',
          }}>
            Ottawa Volleyball Revival
          </p>
          <h1 style={{
            fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
            fontSize: isMobile ? 52 : 'clamp(64px, 9vw, 110px)',
            fontWeight: 900,
            color: '#fff',
            letterSpacing: '0.05em',
            lineHeight: 0.95,
            margin: 0,
          }}>
            THE GALLERY
          </h1>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            color: 'rgba(255,255,255,0.35)',
            marginTop: 16,
            letterSpacing: '0.04em',
          }}>
            Season photos dropping soon
          </p>
        </div>

        {/* Coming soon state */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 120px', textAlign: 'center' }}>
          <div style={{ width: 48, height: 1, background: 'rgba(157,21,21,0.6)', marginBottom: 40 }} />
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 38 : 52, color: 'rgba(255,255,255,0.12)', letterSpacing: '0.1em', margin: '0 0 16px' }}>
            MORE COMING SOON
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.2)', maxWidth: 360, lineHeight: 1.6, margin: 0 }}>
            We're putting together the full photo archive from every season. Check back after June 28th.
          </p>
          <div style={{ width: 48, height: 1, background: 'rgba(157,21,21,0.6)', marginTop: 40 }} />
        </div>
      </div>
    );
  }

  // Sponsors Page
  if (showSponsors) {
    const sponsors = [
      { id: 'fluxcanada',       name: 'Flux Canada',       handle: 'fluxcanada',       initials: 'FC',  color: '#FFD700', desc: 'Premium energy drink brand fueling Ottawa athletes and creators. Flux Canada brings the energy to every OVR event.' },
      { id: 'garliconion',      name: 'Garlic & Onion',    handle: 'garliconion',      initials: 'G&O', color: '#C8A46E', desc: 'Bold, flavourful street food born in Ottawa. Garlic & Onion brings the heat to every bite.' },
      { id: 'zaytounpizza',     name: 'Zaytoun Pizza',     handle: 'zaytounpizza',     initials: 'ZP',  color: '#E63946', desc: "Ottawa's go-to spot for authentic, wood-fired pizza with a Middle Eastern twist." },
      { id: 'spoonmii',         name: 'Spoonmii',          handle: 'spoonmii',         initials: 'SM',  color: '#F4A261', desc: 'Fresh, made-to-order bowls and comfort food in Ottawa. Spoonmii keeps the community fueled.' },
      { id: 'sushipokego',      name: 'Sushi Poke Go',     handle: 'sushipokego',      initials: 'SPG', color: '#2A9D8F', desc: "Ottawa's favourite poke and sushi destination. Fresh ingredients, bold flavours, every time." },
      { id: 'mexishuntclub',    name: "Mexi's Hunt Club",  handle: 'mexishuntclub',    initials: 'MHC', color: '#E9C46A', desc: 'Authentic Mexican flavours in the heart of Hunt Club. Tacos, burritos, and good vibes.' },
      { id: 'futureautosales',  name: 'Future Auto Sales', handle: 'futureautosales',  initials: 'FA',  color: '#457B9D', desc: "Ottawa's trusted pre-owned dealership. Honest deals, quality vehicles, and community first." },
      { id: 'healthyplanet',    name: 'Healthy Planet',    handle: 'healthyplanet',    initials: 'HP',  color: '#52B788', desc: "Canada's leading natural health retailer. Supplements, wellness products, and clean living essentials." },
      { id: 'glowhourmatcha',   name: 'Glow Hour Matcha',  handle: 'glowhourmatcha',   initials: 'GH',  color: '#74C69D', desc: 'Premium ceremonial matcha crafted for the modern lifestyle. Glow from the inside out.' },
      { id: 'kubomatcha',       name: 'Kubo Matcha',       handle: 'kubomatcha',       initials: 'KM',  color: '#40916C', desc: 'Specialty matcha brand bringing Japanese tea culture to Ottawa with every cup.' },
      { id: 'sahararestaurant', name: "Sa'hara Restaurant", handle: 'saharaottawa',     initials: 'SR',  color: '#D4A373', desc: "Ottawa's home for authentic Somali cuisine and more. Warm hospitality and bold East African flavours every visit." },
      { id: 'saharasupplement', name: 'Sahara Supplements', handle: 'saharasupplements', initials: 'SS',  color: '#E76F51', desc: 'Premium supplements and nutrition products to fuel your performance and recovery.' },
      { id: 'keemcandles',      name: 'Keem Candles',      handle: 'keemcandles',      initials: 'KC',  color: '#CDB4DB', desc: 'Handcrafted luxury candles made in Ottawa. Every scent tells a story.' },
      { id: 'genuinehealth',    name: 'Genuine Health',    handle: 'genuinehealth',    initials: 'GE',  color: '#80B918', desc: 'Science-backed supplements rooted in whole food nutrition. Genuine Health supports peak performance.' },
      { id: 'ayjmsa',           name: 'AYJ MSA',           handle: 'ayjmsa',           initials: 'AYJ', color: '#9B5DE5', desc: 'A vibrant student association building community, culture, and connection across Ottawa.' },
      { id: 'majesticdominion', name: 'Majestic Dominion', handle: 'majestic.domin1on', initials: 'MD',  color: '#F15BB5', desc: 'Ottawa-based brand representing excellence, ambition, and community pride.' },
      { id: 'maqamcanada',      name: 'Maqam',             handle: 'maqamumrah',        initials: 'MQ',  color: '#00BBF9', desc: 'A platform celebrating culture, faith, and community. Connecting Ottawa through shared experiences.' },
    ];

    const SponsorLogo = ({ s, size = 36 }) => {
      const [stage, setStage] = React.useState(0); // 0=local file, 1=unavatar, 2=initials
      const localSrc  = `/sponsor_logos/${s.id}.png`;
      const igSrc     = `https://unavatar.io/instagram/${s.handle}`;
      const imgStyle  = { width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 };
      if (stage === 0) return <img src={localSrc} alt={s.name} onError={() => setStage(1)} style={imgStyle} />;
      if (stage === 1) return <img src={igSrc}    alt={s.name} onError={() => setStage(2)} style={imgStyle} />;
      return (
        <div style={{
          width: size, height: size, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${s.color}55, ${s.color}22)`,
          border: `1.5px solid ${s.color}88`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: size * 0.28, fontWeight: 700,
          fontFamily: 'Inter, sans-serif', color: s.color
        }}>{s.initials}</div>
      );
    };

    return (
      <div style={{ position: 'relative', height: '100dvh', overflowY: 'auto', overflowX: 'hidden' }}>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        {/* Three-panel photo wall - top 58% of screen */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '58%', display: 'flex', gap: '3px', zIndex: 0, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <img src={'/IMG_9116.JPG'} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 8%' }} />
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <img src={'/IMG_9201.JPG'} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 8%' }} />
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <img src={'/IMG_9100.jpg'} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }} />
          </div>
        </div>

        {/* Solid dark fill for bottom 45% - no black void */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: '#0d0d0d', zIndex: 0 }} />

        {/* Unified gradient - ties photos into the dark content area */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.85) 68%, #0d0d0d 82%)'
        }} />

        {/* Red brand glow behind content */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, width: '45%', height: '35%', zIndex: 2,
          background: 'radial-gradient(ellipse at bottom left, rgba(157,21,21,0.15) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        {/* Nav */}
        <div className="logo-container-small" onClick={handleLogoClick}
          style={{ cursor: 'pointer', position: 'absolute', top: '20px', left: '20px', zIndex: 20 }}>
          <img src={'/ovr-logo.png'} alt="OVR" className="logo-small" />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />

        {/* Page content */}
        <style>{`
          @keyframes marqueeL { from { transform: translateX(0) } to { transform: translateX(-50%) } }
          @keyframes marqueeR { from { transform: translateX(-50%) } to { transform: translateX(0) } }
          .spg-row { overflow: hidden; width: 100%; }
          .spg-track { display: flex; width: max-content; gap: 10px; }
          .spg-pill { display: flex; align-items: center; gap: 10px; padding: 10px 18px 10px 10px;
            background: rgba(12,12,12,0.8); border: 1px solid rgba(255,255,255,0.1);
            border-radius: 50px; cursor: pointer; flex-shrink: 0;
            backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
            transition: border-color 0.2s ease, box-shadow 0.2s ease; }
          .spg-pill:hover { box-shadow: 0 0 20px rgba(157,21,21,0.3); border-color: rgba(157,21,21,0.5); }
          .spg-pill--feat { padding: 13px 22px 13px 12px; background: rgba(18,18,18,0.88); border-color: rgba(255,255,255,0.14); }
        `}</style>

        {/* Heading + marquee - stacked in document flow so nothing overlaps */}
        <div className="ovr-stagger" style={{
          position: 'relative', zIndex: 10,
          marginTop: isMobile ? '60vh' : '52vh',
          padding: isMobile ? '0 20px 20px' : '0 52px 32px',
        }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>[ Partners ]</p>
          <h1 style={{ fontFamily: "'Bebas Neue', Oswald, sans-serif", fontSize: isMobile ? '34px' : '54px', letterSpacing: '0.06em', color: '#fff', lineHeight: 1.0, margin: '0 0 4px' }}>
            THE BRANDS BEHIND OVR
          </h1>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', margin: '0 0 16px' }}>
            Ottawa businesses investing in the community. Click any to learn more.
          </p>
          <button
            onClick={() => setShowAllSponsorsList(true)}
            style={{
              fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 700,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              color: '#fff', background: 'rgba(157,21,21,0.85)',
              border: '1px solid rgba(157,21,21,0.6)',
              borderRadius: '8px', padding: '10px 22px',
              cursor: 'pointer', backdropFilter: 'blur(8px)',
            }}
          >
            View All Sponsors
          </button>
        </div>

        {/* Marquee rows */}
        <div className="ovr-anim-fade" style={{ animationDelay: '0.5s', animationDuration: '0.9s', position: 'relative', zIndex: 10, paddingTop: isMobile ? '20px' : '8px', paddingBottom: isMobile ? '32px' : '32px' }}>
          {(() => {
            const row1 = sponsors.slice(0, 9);
            const featIds = new Set(['fluxcanada', 'healthyplanet', 'genuinehealth', 'garliconion']);
            const Pill = ({ s }) => {
              const feat = featIds.has(s.id);
              return (
                <div className={`spg-pill${feat ? ' spg-pill--feat' : ''}`} onClick={() => setActiveSponsor(s)}>
                  <SponsorLogo s={s} size={feat ? 40 : 32} />
                  <div>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: feat ? '14px' : '12px', fontWeight: feat ? 700 : 600, color: '#fff', whiteSpace: 'nowrap', display: 'block' }}>{s.name}</span>
                    {feat && <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '9px', color: s.color, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Partner</span>}
                  </div>
                </div>
              );
            };
            return (
              <>
                <div className="spg-row" style={{ marginBottom: '10px' }}>
                  <div className="spg-track" style={{ animation: 'marqueeL 28s linear infinite', paddingLeft: '10px' }}>
                    {[...row1, ...row1].map((s, i) => <Pill key={i} s={s} />)}
                  </div>
                </div>
                <div className="spg-row">
                  <div className="spg-track" style={{ animation: 'marqueeR 32s linear infinite', paddingLeft: '10px' }}>
                    {[...sponsors.slice(9), ...sponsors.slice(9)].map((s, i) => <Pill key={i} s={s} />)}
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* All sponsors list overlay */}
        {showAllSponsorsList && (
          <div onClick={() => setShowAllSponsorsList(false)} style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.82)',
            backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'rgba(12,12,12,0.98)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: isMobile ? '28px 20px' : '40px 44px',
              maxWidth: '520px', width: '100%',
              maxHeight: '80vh', overflowY: 'auto',
              boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
            }}>
              <h2 style={{ fontFamily: "'Bebas Neue', Oswald, sans-serif", fontSize: '32px', letterSpacing: '0.08em', color: '#fff', margin: '0 0 6px' }}>ALL SPONSORS</h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', margin: '0 0 24px' }}>{sponsors.length} PARTNERS</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {sponsors.map((s, i) => (
                  <button key={s.id} onClick={() => { setShowAllSponsorsList(false); setActiveSponsor(s); }} style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px', padding: '12px 16px', cursor: 'pointer',
                    transition: 'border-color 0.2s', textAlign: 'left', width: '100%',
                  }}>
                    <SponsorLogo s={s} size={38} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '2px' }}>{s.name}</div>
                      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: s.color, letterSpacing: '0.08em' }}>@{s.handle}</div>
                    </div>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.25)' }}>→</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setShowAllSponsorsList(false)} style={{
                display: 'block', margin: '24px auto 0', background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '12px', cursor: 'pointer',
              }}>Close</button>
            </div>
          </div>
        )}

        {/* Sponsor detail modal */}
        {activeSponsor && (
          <div onClick={() => setActiveSponsor(null)} style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.78)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px'
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'rgba(14,14,14,0.98)',
              border: `1px solid ${activeSponsor.color}55`,
              borderRadius: '22px',
              padding: isMobile ? '32px 24px' : '44px',
              maxWidth: '420px', width: '100%',
              boxShadow: `0 32px 80px rgba(0,0,0,0.6)`,
              textAlign: 'center'
            }}>
              <div style={{ margin: '0 auto 18px', width: '80px', height: '80px' }}>
                <SponsorLogo s={activeSponsor} size={80} />
              </div>
              <h2 style={{ fontFamily: "'Bebas Neue', Oswald, sans-serif", fontSize: '34px', letterSpacing: '0.08em', color: '#fff', margin: '0 0 4px' }}>{activeSponsor.name}</h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: activeSponsor.color, letterSpacing: '0.15em', marginBottom: '18px' }}>@{activeSponsor.handle}</p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: 1.75, color: 'rgba(255,255,255,0.65)', marginBottom: '28px' }}>{activeSponsor.desc}</p>
              <a href={`https://www.instagram.com/${activeSponsor.handle}`} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600, color: '#fff', background: activeSponsor.color, borderRadius: '8px', padding: '11px 28px', textDecoration: 'none' }}>
                View on Instagram
              </a>
              <br />
              <button onClick={() => setActiveSponsor(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '12px', cursor: 'pointer', marginTop: '14px' }}>Dismiss</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // OVR+ Membership page
  if (showOVRPlus) {
    return (
      <div style={{ position: 'relative', height: '100dvh', overflowY: 'auto', overflowX: 'hidden' }}>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}
        <OVRPlus onNavigate={handleHamburgerNav} />
      </div>
    );
  }

  if (showMerch) {
    return (
      <div style={{ height: '100dvh', overflowY: 'auto', overflowX: 'hidden', background: '#e9e5dc' }}>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} hideCart />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}
        <MerchPage onBack={() => { setShowMerch(false); setShowMenuPage(true); }} supabase={supabase} />
      </div>
    );
  }

  // Menu Page
  if (showMenuPage) {
    const pub = '';
    const menuItems = [
      { link: '#', text: 'UPCOMING EVENTS', image: pub + '/background-removed-background-removed.png',  onClick: () => handleMenuClick('UPCOMING EVENTS'), isNew: true },
      { link: '#', text: 'TOURNAMENTS',     image: pub + '/background-removed-background-removed-2.png', onClick: () => handleMenuClick('TOURNAMENTS'), isLive: pubLiveTournIds.length > 0, isNew: pubLiveTournIds.length === 0 },
      { link: '#', text: 'DROP INS',        image: pub + '/hf_20260512_173251_0e58e0ae-cbf1-4f14-8707-09dce0580fcf-removebg-preview.png', onClick: () => handleMenuClick('DROP INS') },
      { link: '#', text: 'GALLERY',         image: pub + '/background-removed-background-removed-3.png', onClick: () => handleMenuClick('GALLERY') },
      { link: '#', text: 'ABOUT US',        image: pub + '/hf_20260512_172122_89848c9e-dec9-4420-bb5b-5097bb866734-Photoroom.png', onClick: () => handleMenuClick('ABOUT US') },
      { link: '#', text: 'SPONSORS',        image: pub + '/background-removed-background-removed-4.png', onClick: () => { setShowMenuPage(false); setShowSponsors(true); } },
      { link: '#', text: 'MERCH',           image: pub + '/background-removed-background-removed-5.png', onClick: () => handleMenuClick('MERCH'), isNew: true },
    ];

    return (
      <div className="app menu-page" style={{ background: '#fff', height: '100dvh', position: 'relative', overflow: 'hidden' }}>
        {/* Video hero background */}
        <video autoPlay muted loop playsInline preload="metadata" className="menu-video-bg"
          src={'/hero.mp4'} />
        <div className="menu-video-overlay" />

        {/* Ambient orbs */}
        <div className="menu-orb menu-orb--1" />
        <div className="menu-orb menu-orb--2" />
        <div className="menu-orb menu-orb--3" />

        {/* Logo */}
        <div className="logo-container-small" onClick={handleLogoClick}
          style={{ cursor: 'pointer', position: 'absolute', top: '20px', left: '20px', zIndex: 20 }}>
          <img src={'/ovr-logo.png'} alt="Ottawa Volleyball Revival" className="logo-small" />
        </div>

        <HamburgerMenu onNavigate={handleHamburgerNav} onOpen={() => setShowAccountDropdown(false)} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        {/* Header icon buttons - desktop only */}
        {!isMobile && <div style={{ position: 'fixed', top: '20px', right: '128px', zIndex: 1300, display: 'flex', gap: '8px' }}>
          {/* Person icon + account dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => user ? setShowAccountDropdown(d => !d) : openAuthModal(null, 'signup')}
              aria-label={user ? 'Account' : 'Sign in'}
              style={{
                width: 44, height: 44, padding: 0, overflow: 'hidden',
                background: user ? 'rgba(157,21,21,0.18)' : 'rgba(8,8,8,0.75)',
                border: `1px solid ${user ? 'rgba(157,21,21,0.6)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: userAvatar ? '50%' : 10,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(12px)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(157,21,21,0.6)'; if (!userAvatar) e.currentTarget.style.background = 'rgba(157,21,21,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = user ? 'rgba(157,21,21,0.6)' : 'rgba(255,255,255,0.1)'; if (!userAvatar) e.currentTarget.style.background = user ? 'rgba(157,21,21,0.18)' : 'rgba(8,8,8,0.75)'; }}
            >
              {userAvatar ? (
                <img src={userAvatar} alt="Profile" style={{ width: 44, height: 44, objectFit: 'cover', display: 'block' }} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={user ? '#9D1515' : '#fff'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
              )}
            </button>

            {/* Account dropdown - always rendered, animated via CSS class */}
            {user && (
              <>
                {showAccountDropdown && <div onClick={() => setShowAccountDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 1098 }} />}
                <div className={`account-dropdown${showAccountDropdown ? ' account-dropdown--open' : ''}`}>
                  {/* Avatar section */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, cursor: 'pointer' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                      background: 'rgba(157,21,21,0.15)', border: '2px solid rgba(157,21,21,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
                    }}>
                      {userAvatar
                        ? <img src={userAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9D1515" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                      }
                      <div className="avatar-edit-overlay">
                        {avatarUploading
                          ? <span style={{ fontSize: 11, color: '#fff' }}>...</span>
                          : <span className="avatar-edit-overlay__icon">
                              <svg className="avatar-edit-overlay__plus" width="18" height="18" viewBox="0 0 18 18" fill="none"><line x1="9" y1="3" x2="9" y2="15" stroke="#000" strokeWidth="2.2" strokeLinecap="round"/><line x1="3" y1="9" x2="15" y2="9" stroke="#000" strokeWidth="2.2" strokeLinecap="round"/></svg>
                              <svg className="avatar-edit-overlay__pencil" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </span>
                        }
                      </div>
                    </div>
                    <div>
                      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', color: '#9D1515', marginBottom: 2 }}>WELCOME BACK</div>
                      <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 20, color: '#fff', letterSpacing: '0.05em', lineHeight: 1 }}>
                        {(user.user_metadata?.full_name || (user.email ? user.email.split('@')[0] : 'PLAYER')).toUpperCase()}
                      </div>
                      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 10, color: '#555', marginTop: 2 }}>{user.email || ''}</div>
                    </div>
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleAvatarUpload(e.target.files[0]); }} />
                  </label>

                  <button onClick={() => { setShowAccountDropdown(false); handleHamburgerNav('MY EVENTS'); }} style={{
                    background: 'rgba(157,21,21,0.1)', border: '1px solid rgba(157,21,21,0.35)', color: '#9D1515',
                    fontFamily: 'Inter, sans-serif', fontSize: 11, letterSpacing: '0.1em',
                    padding: '7px 14px', cursor: 'pointer', textTransform: 'uppercase',
                    transition: 'color 0.15s, border-color 0.15s, background 0.15s', width: '100%', marginBottom: 8,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(157,21,21,0.2)'; e.currentTarget.style.borderColor = '#9D1515'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(157,21,21,0.1)'; e.currentTarget.style.borderColor = 'rgba(157,21,21,0.35)'; }}
                  >MY EVENTS &amp; ORDERS →</button>
                  <button onClick={() => handleSignOutGuarded()} style={{
                    background: 'transparent', border: '1px solid #2a2a2a', color: '#888',
                    fontFamily: 'Inter, sans-serif', fontSize: 11, letterSpacing: '0.1em',
                    padding: '7px 14px', cursor: 'pointer', textTransform: 'uppercase',
                    transition: 'color 0.15s, border-color 0.15s', width: '100%',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#555'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
                  >SIGN OUT</button>
                </div>
              </>
            )}
          </div>
        </div>}

        {/* FlowingMenu + ticker in a flex column that fills the remaining space */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 6, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <FlowingMenu items={menuItems} />
          </div>
          <div className="menu-ticker" aria-hidden="true">
            <div className="menu-ticker__inner">
              {['OVR LEAGUE', 'OTTAWA VOLLEYBALL REVIVAL', 'OVR THE REST', 'DROP INS', 'TOURNAMENTS', 'COMMUNITY', 'OVR LEAGUE', 'OTTAWA VOLLEYBALL REVIVAL', 'OVR THE REST', 'DROP INS', 'TOURNAMENTS', 'COMMUNITY'].map((t, i) => (
                <span key={i} className="menu-ticker__item">{t}<span className="menu-ticker__sep"> ✦ </span></span>
              ))}
            </div>
          </div>
        </div>
        {/* Legal links — visible on homepage so Google OAuth verification can find them */}
        <style>{`
          @media (max-width: 600px) {
            .home-legal { bottom: 54px !important; left: 16px !important; right: 16px !important; justify-content: space-between !important; }
          }
        `}</style>
        <div className="home-legal" style={{
          position: 'fixed', bottom: 8, right: 16, zIndex: 10,
          display: 'flex', gap: 14,
          fontFamily: 'Inter, sans-serif', fontSize: 10,
          letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>
          <a href="/privacy" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Privacy</a>
          <a href="/terms" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Terms</a>
        </div>

        {/* Draft recovery banner */}
        {regDraft && (
          <div className="draft-banner-overlay">
            <div className="draft-banner-card">
              <div className="draft-banner-eyebrow">DRAFT SAVED</div>
              <div className="draft-banner-title">Continue where you left off?</div>
              <div className="draft-banner-desc">Looks like you have an unfinished team registration.</div>
              <div className="draft-banner-actions">
                <button onClick={() => { setRestoreDraft(true); setShowMenuPage(false); setShowTournamentTeamForm(true); setRegDraft(null); }}>CONTINUE →</button>
                <button onClick={() => { localStorage.removeItem('ovrRegDraft'); setRegDraft(null); }}>DISMISS</button>
              </div>
            </div>
          </div>
        )}

        {/* Live tournament toast */}
        {liveToastInfo && !liveToastDismissed && !regDraft && (
          <div className="draft-banner-overlay" style={{ bottom: regDraft ? 220 : 32 }}>
            <div className="draft-banner-card" style={{ borderLeft: '3px solid #9D1515', borderColor: 'rgba(157,21,21,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#9D1515', display: 'inline-block', animation: 'pulse 1.2s ease-in-out infinite', flexShrink: 0 }} />
                <div className="draft-banner-eyebrow" style={{ margin: 0 }}>HAPPENING NOW</div>
              </div>
              <div className="draft-banner-title">OVR {liveToastInfo.name} IS LIVE</div>
              <div className="draft-banner-desc">wanna see the score?</div>
              <div className="draft-banner-actions">
                <button onClick={() => { setLiveToastDismissed(true); setShowMenuPage(false); setShowScoreboard(true); window.history.pushState({}, '', `/scoreboard/${liveToastInfo.id}`); }}>SEE SCORE →</button>
                <button onClick={() => setLiveToastDismissed(true)}>DISMISS</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Tournaments Page
  if (showTournamentsPage) {
    const seasons = [
      { id: 'szn1', label: 'Season I',   cardTitle: 'OVR CUP SZN I',   winners: 'Champions crowned at the first ever OVR Cup.',             src: '/szn1-winners.jpg',    pos: 'center 35%' },
      { id: 'szn2', label: 'Season II',  cardTitle: 'OVR CUP SZN II',  winners: 'Back-to-back energy. The level was serious from day one.',  src: '/szn2-winners.jpg',    pos: 'center 43%' },
      { id: 'szn3', label: 'Season III', cardTitle: 'OVR CUP SZN III', winners: 'Biggest field yet. Hardest fought final in OVR history.',   src: '/szniiiwinners.png',   pos: 'center 30%' },
      { id: 'szn4', label: 'Season IV',  cardTitle: 'OVR CUP SZN IV',  winners: 'The standard was raised. The level has never been higher.', scoreboardId: 'szn4', src: '/ovrsznivwinners.jpg', pos: 'center 45%' },
      { id: 'szn5', label: 'Season V',   cardTitle: 'OVR CUP SZN V',   winners: 'The most competitive OVR Cup to date.',                     scoreboardId: 'szn5', src: '/IMG_3043.jpg',        pos: 'center 42%' },
      { id: 'szn6', label: 'Season VI',  cardTitle: 'OVR CUP SZN VI',  winners: 'Sameer and Friends go the distance — champions in 3 sets.', scoreboardId: 'szn6', src: '/szn6-winners.jpg',    pos: 'center 40%' },
    ];

    return (
      <div ref={tournScrollRef} onScroll={scrubTrophyVideo} style={{ background: '#24211c', height: '100dvh', overflowY: 'scroll', overflowX: 'hidden', width: '100%', position: 'relative', WebkitOverflowScrolling: 'touch' }}>

        {/* Fixed logo + nav */}
        <div className="logo-container-small" onClick={handleLogoClick} style={{ cursor: 'pointer', position: 'fixed', top: '20px', left: '20px', zIndex: 200 }}>
          <img src={'/ovr-logo.png'} alt="OVR" className="logo-small" />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        {/* ── SECTION 1: HERO ── */}
        <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
          <img src={'/HOWITWORKS.jpg'} alt="OVR live action"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 25%' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.1) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 45%)' }} />
          <div className="ovr-stagger" style={{ position: 'absolute', bottom: isMobile ? 80 : 110, left: isMobile ? 24 : 64, right: isMobile ? 24 : 'auto', zIndex: 10, maxWidth: 620 }}>
            <button
              onClick={() => { setShowTournamentsPage(false); setShowUpcomingEvents(true); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: '0 0 18px 0', padding: '8px 14px', background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(157,21,21,0.6)', borderRadius: 999, color: '#fff', cursor: 'pointer', backdropFilter: 'blur(6px)', fontFamily: "'JetBrains Mono',monospace", fontSize: isMobile ? 9 : 10, letterSpacing: '0.16em', textTransform: 'uppercase', transition: 'background 0.2s, border-color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(157,21,21,0.35)'; e.currentTarget.style.borderColor = '#9D1515'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; e.currentTarget.style.borderColor = 'rgba(157,21,21,0.6)'; }}
            >
              <span className="ovr-live-dot" />
              Next up · OVR × Carleton · Sun Oct 25 →
            </button>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#9D1515', margin: '0 0 14px 0' }}>Ottawa Volleyball Revival</p>
            <h1 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 72 : 'clamp(80px,9vw,130px)', fontWeight: 900, color: '#fff', lineHeight: 0.9, margin: '0 0 6px 0', letterSpacing: '0.02em' }}>THE OVR</h1>
            <h1 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 72 : 'clamp(80px,9vw,130px)', fontWeight: 900, color: '#9D1515', fontStyle: 'italic', lineHeight: 0.9, margin: '0 0 28px 0', letterSpacing: '0.02em' }}>CUP</h1>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, margin: '0 0 32px 0', maxWidth: 440 }}>
              Ottawa's most competitive volleyball tournament. Five seasons of elite matchups, unforgettable finals, and a community built around the game.
            </p>
            {TOURNAMENT_REGISTRATIONS_OPEN ? (
              <button className="ovr-shine" onClick={() => { setShowTournamentsPage(false); setShowTournamentTeamForm(true); }}
                style={{ fontFamily: 'Inter,sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', background: '#9D1515', border: 'none', borderRadius: 4, padding: '14px 36px', cursor: 'pointer' }}
                onMouseEnter={e => e.target.style.background='#b91c1c'} onMouseLeave={e => e.target.style.background='#9D1515'}>
                Register Now
              </button>
            ) : (
              <div style={{ fontFamily: 'Inter,sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, padding: '14px 36px', display: 'inline-block' }}>
                Registrations Closed
              </div>
            )}
          </div>
          {!isMobile && (
            <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: 0.35 }}>
              <span style={{ fontFamily: 'Inter,sans-serif', fontSize: 9, letterSpacing: '0.2em', color: '#fff', textTransform: 'uppercase' }}>Scroll</span>
              <div style={{ width: 1, height: 36, background: '#fff' }} />
            </div>
          )}
        </div>

        {/* ── SECTION 2: SCROLL-DRIVEN TROPHY ── 300vh tall so scroll has travel distance */}
        <div ref={trophySectionRef} style={{ height: '300vh', position: 'relative', background: '#000' }}>
          <div style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {/* Ambient glow */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(157,21,21,0.15) 0%, transparent 65%)', pointerEvents: 'none' }} />
            <p style={{ fontFamily: 'Inter,sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#9D1515', margin: '0 0 12px 0', position: 'relative', zIndex: 2 }}>The Prize</p>
            <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 38 : 60, fontWeight: 900, color: '#fff', letterSpacing: '0.05em', margin: '0 0 40px 0', textAlign: 'center', position: 'relative', zIndex: 2 }}>OVR CUP TROPHY</h2>
            {/* Trophy video - frameless floating look */}
            <div style={{ position: 'relative', zIndex: 2, width: isMobile ? 340 : 680, margin: '0 auto' }}>
              {/* Soft gold light pool under trophy */}
              <div style={{
                position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)',
                width: '70%', height: 48,
                background: 'radial-gradient(ellipse, rgba(200,150,20,0.4) 0%, transparent 70%)',
                filter: 'blur(16px)', borderRadius: '50%', zIndex: 1,
              }} />
              <video
                ref={trophyVideoRef}
                src={'/trophy.mp4'}
                muted playsInline preload="metadata"
                onLoadedMetadata={() => scrubTrophyVideo()}
                onLoadedData={() => scrubTrophyVideo()}
                style={{
                  width: '100%', display: 'block',
                  mixBlendMode: 'screen',
                  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 88%, transparent 100%)',
                  maskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 88%, transparent 100%)',
                }}
              />
            </div>
            <p style={{ fontFamily: 'Inter,sans-serif', fontSize: isMobile ? 13 : 15, color: 'rgba(255,255,255,0.45)', textAlign: 'center', maxWidth: 420, margin: '28px 0 0 0', lineHeight: 1.7, position: 'relative', zIndex: 2 }}>
              Forged for Ottawa's finest. Every season one team lifts the OVR Cup.
            </p>
          </div>
        </div>

        {/* ── SECTION 3: MAIN VIDEO ── */}
        <div style={{ position: 'relative', width: '100%', background: '#24211c' }}>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
            <video
              ref={sectionVideoRef}
              src={'/section.mp4'}
              autoPlay muted loop playsInline preload="none"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 40%, transparent 70%)' }} />

            {/* Text overlay */}
            <div style={{ position: 'absolute', bottom: isMobile ? 24 : 44, left: isMobile ? 24 : 52, zIndex: 5 }}>
              <p style={{ fontFamily: 'Inter,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', margin: '0 0 6px 0' }}>The OVR Experience</p>
              <h3 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 26 : 52, fontWeight: 900, color: '#fff', letterSpacing: '0.04em', margin: '0 0 8px 0', lineHeight: 1 }}>5 SEASONS OF ELITE VOLLEYBALL</h3>
              <p style={{ fontFamily: 'Inter,sans-serif', fontSize: isMobile ? 12 : 14, color: 'rgba(255,255,255,0.5)', margin: 0 }}>Competing at the highest level Ottawa has to offer</p>
            </div>

            {/* Mute / unmute button */}
            <button
              onClick={() => {
                const v = sectionVideoRef.current;
                if (!v) return;
                const next = !sectionVideoMuted;
                v.muted = next;
                setSectionVideoMuted(next);
              }}
              style={{
                position: 'absolute',
                bottom: isMobile ? 24 : 44,
                right: isMobile ? 16 : 52,
                zIndex: 10,
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '50%',
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                transition: 'background 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(157,21,21,0.7)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
              aria-label={sectionVideoMuted ? 'Unmute video' : 'Mute video'}
            >
              {sectionVideoMuted ? (
                /* Muted icon */
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                /* Unmuted icon */
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ── SECTION 4: CHAMPIONS GALLERY (photos, grey until clicked) ── */}
        <div style={{ background: '#24211c', padding: isMobile ? '56px 0 80px' : '80px 0 100px' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
            <Reveal style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 60 }}>
              <p style={{ fontFamily: 'Inter,sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#9D1515', margin: '0 0 12px 0' }}>Season by Season</p>
              <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 34 : 56, fontWeight: 900, color: '#fff', letterSpacing: '0.04em', margin: 0 }}>OVR CUP CHAMPIONS</h2>
              <p style={{ fontFamily: 'Inter,sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 10 }}>Click a season to reveal the winners</p>
            </Reveal>

            {/* Stacked season rows - photos, grey until clicked */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {seasons.map((s, i) => {
                const active = featuredSeason === s.id;
                return (
                  <Reveal key={s.id} variant={i % 2 ? 'right' : 'left'} onClick={() => setFeaturedSeason(active ? null : s.id)}
                    style={{ position: 'relative', cursor: 'pointer', overflow: 'hidden', borderLeft: active ? '4px solid #9D1515' : '4px solid transparent', transition: 'border-color 0.3s, opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)' }}>
                    {/* Photo background */}
                    <div style={{ position: 'relative', width: '100%', height: isMobile ? 180 : 300, overflow: 'hidden' }}>
                      <img src={s.src} alt={s.label}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: s.pos,
                          filter: active ? 'brightness(0.75)' : 'grayscale(100%) brightness(0.35)',
                          transition: 'filter 0.45s ease, transform 0.45s ease',
                          transform: active ? 'scale(1.02)' : 'scale(1)' }} />
                      {/* Gradient */}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)' }} />

                      {/* Text */}
                      <div style={{ position: 'absolute', bottom: isMobile ? 18 : 28, left: isMobile ? 18 : 36, zIndex: 5 }}>
                        <p style={{ fontFamily: 'Inter,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: active ? '#9D1515' : 'rgba(255,255,255,0.3)', margin: '0 0 4px 0', transition: 'color 0.3s' }}>{s.label}</p>
                        <h3 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 22 : 34, fontWeight: 900, color: active ? '#fff' : 'rgba(255,255,255,0.3)', letterSpacing: '0.05em', margin: '0 0 4px 0', transition: 'color 0.3s' }}>{s.cardTitle || 'OVR CUP WINNERS'}</h3>
                        {active && s.champion && <p style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 18 : 26, color: '#d8a64a', letterSpacing: '0.06em', margin: '0 0 4px 0' }}>🏆 {s.champion}</p>}
                        {active && <p style={{ fontFamily: 'Inter,sans-serif', fontSize: isMobile ? 12 : 14, color: 'rgba(255,255,255,0.6)', margin: 0, maxWidth: 380 }}>{s.winners}</p>}
                        {active && s.scoreboardId && (
                          <button onClick={e => { e.stopPropagation(); setShowTournamentsPage(false); setShowScoreboard(true); window.history.pushState({}, '', `/scoreboard/${s.scoreboardId}`); }}
                            style={{ display: 'inline-block', marginTop: 12, fontFamily: 'Inter,sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', padding: '7px 16px', cursor: 'pointer' }}>
                            View Results →
                          </button>
                        )}
                      </div>

                      {/* Season number watermark */}
                      <div style={{ position: 'absolute', top: isMobile ? 12 : 20, right: isMobile ? 12 : 28, zIndex: 5 }}>
                        <span style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 52 : 80, fontWeight: 900, color: active ? 'rgba(157,21,21,0.18)' : 'rgba(255,255,255,0.06)', lineHeight: 1, transition: 'color 0.3s' }}>0{i + 1}</span>
                      </div>

                      {/* Reveal badge when inactive */}
                      {!active && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6 }}>
                          <span style={{ fontFamily: 'Inter,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, padding: '6px 14px', backdropFilter: 'blur(4px)' }}>Click to reveal</span>
                        </div>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>

          </div>
        </div>

        {/* ── BOTTOM CTA ── */}
        <Reveal style={{ background: '#000', padding: isMobile ? '56px 24px 72px' : '72px 24px 96px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'Inter,sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#9D1515', margin: '0 0 14px 0' }}>Ready to compete?</p>
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: isMobile ? 42 : 60, fontWeight: 900, color: '#fff', letterSpacing: '0.04em', margin: '0 0 28px 0', lineHeight: 1 }}>JOIN THE NEXT OVR CUP</h2>
          {TOURNAMENT_REGISTRATIONS_OPEN ? (
            <button
              onClick={() => { setShowTournamentsPage(false); setShowTournamentTeamForm(true); }}
              style={{ fontFamily: 'Inter,sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', background: '#9D1515', border: 'none', borderRadius: 4, padding: '16px 44px', cursor: 'pointer' }}
              onMouseEnter={e => e.target.style.background = '#b91c1c'}
              onMouseLeave={e => e.target.style.background = '#9D1515'}
            >
              Register Now →
            </button>
          ) : (
            <div style={{ fontFamily: 'Inter,sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4, padding: '16px 44px', display: 'inline-block' }}>
              Registrations Closed
            </div>
          )}
        </Reveal>
      </div>
    );
  }

  // Tournament Sign Up Page
  // Tournament Details Page
  if (showTournamentDetails) {
    const poolATeams = [];
    const poolBTeams = [];
    const quarters = [];
    const semis = [];
    const finals = [];

    const stages = ['pool', 'quarters', 'semis', 'finals'];
    const stageNames = {
      pool: 'Pool Play',
      quarters: 'Quarterfinals',
      semis: 'Semifinals',
      finals: 'Finals'
    };

    const handleStageChange = (direction) => {
      const currentIndex = stages.indexOf(tournamentStage);
      if (direction === 'next' && currentIndex < stages.length - 1) {
        setTournamentStage(stages[currentIndex + 1]);
      } else if (direction === 'prev' && currentIndex > 0) {
        setTournamentStage(stages[currentIndex - 1]);
      }
    };

    // Touch/swipe handlers
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;
      if (isLeftSwipe) handleStageChange('next');
      if (isRightSwipe) handleStageChange('prev');
    };

    return (
      <div 
        style={{ 
          background: '#000', 
          minHeight: '100vh', 
          height: 'auto',
          overflowY: 'scroll', 
          overflowX: 'hidden',
          position: 'relative',
          width: '100%',
          paddingTop: 0
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Logo - Fixed position */}
        <div 
          className="logo-container-small"
          onClick={() => {
            setShowTournamentDetails(false);
            setShowTournamentsPage(true);
          }}
          style={{ cursor: 'pointer', position: 'fixed', top: '20px', left: '20px', zIndex: 100 }}
        >
          <img 
            src={'/ovr-logo.png'} 
            alt="Ottawa Volleyball Revival"
            className="logo-small"
          />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} onSignOut={handleSignOutGuarded} isAdmin={isAdmin} />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}

        {/* Title - FuzzyText */}
        <div style={{ 
          paddingTop: isMobile ? '60px' : '80px',
          paddingBottom: isMobile ? '20px' : '40px',
          paddingLeft: isMobile ? '10px' : '0',
          paddingRight: isMobile ? '10px' : '0',
          textAlign: 'center',
          width: '100%',
          pointerEvents: 'none'
        }}>
          <FuzzyText
            fontSize={isMobile ? 22 : 60}
            fontWeight={900}
            color="#fff"
            enableHover={true}
            baseIntensity={0.05}
            hoverIntensity={0.15}
          >
            OVR CUP  SZN IV (Comp/Elite) Tourney
          </FuzzyText>
        </div>

        {/* Picture Banner */}
        <div style={{
          width: '100%',
          height: '400px',
          marginBottom: '40px',
          overflow: 'hidden'
        }}>
          <img 
            src={'/HOWITWORKS.jpg'} 
            alt="Tournament Banner"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 50%'
            }}
          />
        </div>

        {/* Stage Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '30px',
          padding: '0 20px'
        }}>
          <button
            onClick={() => handleStageChange('prev')}
            disabled={tournamentStage === 'pool'}
            style={{
              padding: '10px 20px',
              background: tournamentStage === 'pool' ? '#333' : '#9D1515',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: tournamentStage === 'pool' ? 'not-allowed' : 'pointer',
              fontFamily: 'Courier New, monospace',
              fontSize: '14px',
              opacity: tournamentStage === 'pool' ? 0.5 : 1
            }}
          >
            ← Prev
          </button>
          
          <div style={{
            color: '#fff',
            fontFamily: 'Courier New, monospace',
            fontSize: '18px',
            fontWeight: 'bold',
            minWidth: '200px',
            textAlign: 'center'
          }}>
            {stageNames[tournamentStage]}
          </div>
          
          <button
            onClick={() => handleStageChange('next')}
            disabled={tournamentStage === 'finals'}
            style={{
              padding: '10px 20px',
              background: tournamentStage === 'finals' ? '#333' : '#9D1515',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: tournamentStage === 'finals' ? 'not-allowed' : 'pointer',
              fontFamily: 'Courier New, monospace',
              fontSize: '14px',
              opacity: tournamentStage === 'finals' ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>

        {/* Stage Content */}
        <div style={{ 
          padding: '0 20px 60px', 
          maxWidth: '1200px', 
          margin: '0 auto',
          color: '#fff',
          fontFamily: 'Courier New, monospace'
        }}>
          {tournamentStage === 'pool' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                marginBottom: '30px',
                textAlign: 'center',
                color: '#9D1515'
              }}>
                Pool Play Standings
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '30px',
                marginBottom: '40px'
              }}>
                {/* Pool A */}
                <div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    color: '#fff',
                    textAlign: 'center'
                  }}>
                    POOL A
                  </h3>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    background: 'rgba(157, 21, 21, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid #9D1515'
                  }}>
                    <thead>
                      <tr style={{ background: '#9D1515' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Team</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>W</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poolATeams.map((team, index) => (
                        <tr 
                          key={index}
                          style={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: team.position === 5 ? 0.5 : 1
                          }}
                        >
                          <td style={{ 
                            padding: '12px',
                            textDecoration: team.position === 5 ? 'line-through' : 'none',
                            color: team.position === 5 ? '#999' : '#fff'
                          }}>
                            {team.name}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{team.wins}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{team.losses}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pool B */}
                <div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    color: '#fff',
                    textAlign: 'center'
                  }}>
                    POOL B
                  </h3>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    background: 'rgba(157, 21, 21, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid #9D1515'
                  }}>
                    <thead>
                      <tr style={{ background: '#9D1515' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Team</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>W</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poolBTeams.map((team, index) => (
                        <tr 
                          key={index}
                          style={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: team.position === 5 ? 0.5 : 1
                          }}
                        >
                          <td style={{ 
                            padding: '12px',
                            textDecoration: team.position === 5 ? 'line-through' : 'none',
                            color: team.position === 5 ? '#999' : '#fff'
                          }}>
                            {team.name}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{team.wins}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{team.losses}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tournamentStage === 'quarters' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                marginBottom: '30px',
                textAlign: 'center',
                color: '#9D1515'
              }}>
                Quarterfinals
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                {quarters.map((match, index) => (
                  <div key={index} style={{
                    background: 'rgba(157, 21, 21, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '2px solid #9D1515'
                  }}>
                    <div style={{
                      marginBottom: '10px',
                      padding: '10px',
                      background: match.winner === match.team1 ? '#9D1515' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      textDecoration: match.winner !== match.team1 ? 'line-through' : 'none',
                      opacity: match.winner !== match.team1 ? 0.5 : 1
                    }}>
                      {match.team1}
                    </div>
                    <div style={{ textAlign: 'center', margin: '10px 0', fontSize: '20px' }}>VS</div>
                    <div style={{
                      padding: '10px',
                      background: match.winner === match.team2 ? '#9D1515' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      textDecoration: match.winner !== match.team2 ? 'line-through' : 'none',
                      opacity: match.winner !== match.team2 ? 0.5 : 1
                    }}>
                      {match.team2}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tournamentStage === 'semis' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                marginBottom: '30px',
                textAlign: 'center',
                color: '#9D1515'
              }}>
                Semifinals
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '30px',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                {semis.map((match, index) => (
                  <div key={index} style={{
                    background: 'rgba(157, 21, 21, 0.1)',
                    padding: '30px',
                    borderRadius: '8px',
                    border: '2px solid #9D1515'
                  }}>
                    <div style={{
                      marginBottom: '15px',
                      padding: '15px',
                      background: match.winner === match.team1 ? '#9D1515' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      textDecoration: match.winner !== match.team1 ? 'line-through' : 'none',
                      opacity: match.winner !== match.team1 ? 0.5 : 1,
                      fontSize: '18px'
                    }}>
                      {match.team1}
                    </div>
                    <div style={{ textAlign: 'center', margin: '15px 0', fontSize: '24px' }}>VS</div>
                    <div style={{
                      padding: '15px',
                      background: match.winner === match.team2 ? '#9D1515' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      textDecoration: match.winner !== match.team2 ? 'line-through' : 'none',
                      opacity: match.winner !== match.team2 ? 0.5 : 1,
                      fontSize: '18px'
                    }}>
                      {match.team2}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tournamentStage === 'finals' && (
            <div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                marginBottom: '30px',
                textAlign: 'center',
                color: '#9D1515'
              }}>
                Finals
              </h2>
              <div style={{
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                {finals.map((match, index) => (
                  <div key={index} style={{
                    background: 'rgba(157, 21, 21, 0.1)',
                    padding: '40px',
                    borderRadius: '8px',
                    border: '2px solid #9D1515'
                  }}>
                    <div style={{
                      marginBottom: '20px',
                      padding: '20px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      fontSize: '20px',
                      textAlign: 'center'
                    }}>
                      {match.team1}
                    </div>
                    <div style={{ textAlign: 'center', margin: '20px 0', fontSize: '28px', fontWeight: 'bold' }}>VS</div>
                    <div style={{
                      padding: '20px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      fontSize: '20px',
                      textAlign: 'center'
                    }}>
                      {match.team2}
                    </div>
                    {match.winner && (
                      <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        background: '#9D1515',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold'
                      }}>
                        Winner: {match.winner}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Description - Stage Specific */}
        <div style={{ 
          padding: '0 20px 60px', 
          maxWidth: '800px', 
          margin: '0 auto',
          color: '#fff',
          fontFamily: 'Courier New, monospace',
          fontSize: '16px',
          lineHeight: '1.8'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: '#9D1515'
          }}>
            🏐 How the OVR Tournament Works
          </h2>
          
          {tournamentStage === 'pool' && (
            <>
              <p style={{ marginBottom: '20px' }}>
                Our tournaments follow the OVR format: fast-paced, competitive, and built for maximum playtime and non-stop energy all day long.
              </p>
              <p style={{ marginBottom: '20px' }}>
                Each event brings together 10 teams, split into two pools of five: <strong>Pool A and Pool B</strong>.
              </p>
              <div style={{
                background: 'rgba(157, 21, 21, 0.15)',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #9D1515',
                marginBottom: '30px',
                marginTop: '20px'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '10px',
                  color: '#9D1515'
                }}>
                  ⚡ Co-Ed Tournament Requirements
                </h3>
                <p style={{ marginBottom: '0' }}>
                  This is a <strong>co-ed tournament</strong>. Teams must have <strong>at least 2 female players on the court at all times</strong> during gameplay. This ensures fair and inclusive competition throughout the tournament.
                </p>
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                🔹 Pool Play (Round Robin Stage)
              </h3>
              <p style={{ marginBottom: '15px' }}>
                Each pool has its own court.
              </p>
              <p style={{ marginBottom: '15px' }}>
                Every team plays four matches, facing every other team in its pool once.
              </p>
              <p style={{ marginBottom: '15px' }}>
                All matches are <strong>1 set to 25 points</strong>, with a <strong>25-minute time cap</strong> (no win-by-2).
              </p>
              <p style={{ marginBottom: '15px' }}>
                Teams rotate between playing and reffing to keep matches running smoothly.
              </p>
              <p style={{ marginBottom: '15px' }}>
                <strong>Example – Pool A (Court 1):</strong>
              </p>
              <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                <li>A1 plays A2, A3, A4, and A5</li>
                <li>A2 plays A1, A3, A4, and A5</li>
                <li>… and so on until everyone has 4 matches</li>
              </ul>
              <p style={{ marginBottom: '15px' }}>
                At the end of pool play, each team will have a record like "3–1" or "2–2" based on sets won and lost.
              </p>
              <p style={{ marginBottom: '15px' }}>
                Teams are then ranked 1st–5th in their pool using:
              </p>
              <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                <li>Match wins</li>
                <li>Sets won</li>
                <li>Point differential (if tied)</li>
              </ul>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                🔸 Playoff Qualification
              </h3>
              <p style={{ marginBottom: '20px' }}>
                After pool play, the <strong>top 4 teams from each pool</strong> move on to the playoffs (8 teams total).
              </p>
              <p>
                The bottom team (5th place in each pool) is eliminated after round robin but still gets 4 full matches from the morning stage.
              </p>
            </>
          )}

          {tournamentStage === 'quarters' && (
            <>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                🔥 Playoffs (Single-Elimination Bracket)
              </h3>
              <p style={{ marginBottom: '15px' }}>
                This is where it gets intense.
              </p>
              <p style={{ marginBottom: '15px' }}>
                Quarter Finals are <strong>single set to 25</strong>. Semis and Final are <strong>best-of-three (25/25/15)</strong> — win by 2, capped at 30.
              </p>
              <p style={{ marginBottom: '15px' }}>
                To keep things balanced, the playoff bracket crosses the pools, meaning top seeds from Pool A play lower seeds from Pool B.
              </p>
              <p style={{ marginBottom: '20px' }}>
                That way, the strongest teams don't meet too early.
              </p>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                Quarterfinal Matchups:
              </h3>
              <div style={{
                background: 'rgba(157, 21, 21, 0.1)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #9D1515',
                marginBottom: '20px'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '14px' }}>Match</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '14px' }}>Teams</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '14px' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <td style={{ padding: '10px' }}>QF1</td>
                      <td style={{ padding: '10px' }}>A1 🆚 B4</td>
                      <td style={{ padding: '10px' }}>1st in Pool A plays 4th in Pool B</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <td style={{ padding: '10px' }}>QF2</td>
                      <td style={{ padding: '10px' }}>A2 🆚 B3</td>
                      <td style={{ padding: '10px' }}>2nd in Pool A plays 3rd in Pool B</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <td style={{ padding: '10px' }}>QF3</td>
                      <td style={{ padding: '10px' }}>B1 🆚 A4</td>
                      <td style={{ padding: '10px' }}>1st in Pool B plays 4th in Pool A</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px' }}>QF4</td>
                      <td style={{ padding: '10px' }}>B2 🆚 A3</td>
                      <td style={{ padding: '10px' }}>2nd in Pool B plays 3rd in Pool A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                This ensures fair balance. If you dominated your pool, you earn an easier first playoff matchup.
              </p>
            </>
          )}

          {tournamentStage === 'semis' && (
            <>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                ⚡ Semifinals (SA & SB)
              </h3>
              <p style={{ marginBottom: '20px' }}>
                The winners move on to the semifinals:
              </p>
              <div style={{
                background: 'rgba(157, 21, 21, 0.1)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #9D1515',
                marginBottom: '20px'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '14px' }}>Match</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '14px' }}>Teams</th>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '14px' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <td style={{ padding: '10px' }}>SA</td>
                      <td style={{ padding: '10px' }}>W(QF1) 🆚 W(QF2)</td>
                      <td style={{ padding: '10px' }}>Winner from the Pool A side</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px' }}>SB</td>
                      <td style={{ padding: '10px' }}>W(QF3) 🆚 W(QF4)</td>
                      <td style={{ padding: '10px' }}>Winner from the Pool B side</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                Now, each semifinal produces one finalist.
              </p>
            </>
          )}

          {tournamentStage === 'finals' && (
            <>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                🏆 Finals
              </h3>
              <p style={{ marginBottom: '20px' }}>
                The two semifinal winners, W(SA) and W(SB), face off in the <strong>OVR Championship Final</strong>, a best-of-three showdown (25 / 25 / 15).
              </p>
              <p style={{ marginBottom: '30px' }}>
                The winner takes home the title, medals, and bragging rights.
              </p>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '30px',
                color: '#9D1515'
              }}>
                💥 Why This Format Works
              </h3>
              <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                <li><strong>Fair:</strong> Top teams don't face each other too early.</li>
                <li><strong>Fast:</strong> Games are time-capped at 35 minutes. No long delays.</li>
                <li><strong>Fun:</strong> Every team gets 4 matches minimum, with more if they advance.</li>
                <li><strong>Hype:</strong> Playoffs build momentum. Every point, every set matters.</li>
              </ul>
              <p style={{ marginBottom: '20px' }}>
                In short:
              </p>
              <p style={{ marginBottom: '20px', fontStyle: 'italic' }}>
                Every team gets lots of play, top seeds are rewarded, and every rally counts toward the OVR Championship.
              </p>
              <p>
                That's the OVR format: competitive, fast, and unforgettable.
              </p>
            </>
          )}
        </div>

        {/* Sign Up Button */}
        <div style={{ padding: '40px 20px 80px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {TOURNAMENT_REGISTRATIONS_OPEN ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowTournamentDetails(false);
                setShowTournamentSignup(false);
                setShowTournamentTeamForm(true);
                setTournamentTeamFormStep(1);
                setTournamentTeamData({ numPlayers: '', teamName: '', captainName: '', captainEmail: '', phoneNumber: '', players: [] });
              }}
              style={{ background: '#9D1515', border: 'none', color: '#fff', padding: '15px 40px', fontSize: '18px', fontWeight: 'bold', fontFamily: 'Courier New, monospace', textTransform: 'uppercase', letterSpacing: '1px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.3s ease', pointerEvents: 'auto' }}
              onMouseEnter={(e) => { e.target.style.background = '#ff0000'; e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 4px 12px rgba(157, 21, 21, 0.5)'; }}
              onMouseLeave={(e) => { e.target.style.background = '#9D1515'; e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
            >
              SIGN UP NOW
            </button>
          ) : (
            <div style={{ fontFamily: 'Courier New, monospace', fontSize: '16px', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '15px 40px' }}>
              REGISTRATIONS CLOSED
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tournament Registration Flow (new)
  if (showTournamentTeamForm && TOURNAMENT_REGISTRATIONS_OPEN) {
    return (
      <>
        <TournamentFlow
          pub={''}
          isMobile={isMobile}
          onBack={() => { setShowTournamentTeamForm(false); setShowTournamentsPage(true); }}
          restoreDraft={restoreDraft}
          onDraftRestored={() => setRestoreDraft(false)}
          onCartAdded={() => {
            setRegDraft(null);
            // If this was an edit of an existing cart item, remove the old one
            if (editCartItemIdRef.current) {
              cartRemoveItem(editCartItemIdRef.current);
              editCartItemIdRef.current = null;
            }
          }}
          onNavigate={handleHamburgerNav}
          onSignOut={handleSignOutGuarded}
          isAdmin={isAdmin}
          userAvatar={userAvatar}
        />
        {showFaWarning && <FaWarnModal onStay={() => setShowFaWarning(false)} onLeave={() => { faWarningPendingRef.current?.(); setShowFaWarning(false); }} />}
      </>
    );
  }

  // DEAD CODE BELOW - kept temporarily then to be removed
  if (false && showTournamentTeamForm) {
    return (
      <div className="app" style={{ background: '#000', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
          <LaserFlow
            horizontalBeamOffset={0.0}
            verticalBeamOffset={0.1}
            color="#9D1515"
            wispDensity={0.8}
            wispIntensity={4.0}
            fogIntensity={0.4}
            flowStrength={0.2}
          />
        </div>

        <div 
          className="logo-container-small"
          onClick={() => {
            setShowTournamentTeamForm(false);
            setShowTournamentSignup(true);
            setTournamentTeamFormStep(1);
          }}
          style={{ cursor: 'pointer', zIndex: 20 }}
        >
          <img
            src={'/ovr-logo.png'}
            alt="Ottawa Volleyball Revival"
            className="logo-small"
          />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} isAdmin={isAdmin} />

        <div className="team-form-container">
          {/* Step 1: Number of Players */}
          {tournamentTeamFormStep === 1 && (
            <div className="team-form-step">
              <div className="fuzzy-text-wrapper-form">
                <FuzzyText
                  fontSize={50}
                  fontWeight={900}
                  color="#fff"
                  enableHover={true}
                  baseIntensity={0.05}
                  hoverIntensity={0.15}
                >
                  HOW MANY PLAYERS?
                </FuzzyText>
              </div>
              <p className="team-form-subtitle-large">{selectedTournament === 'carleton' ? 'OVR × CARLETON • October 25th' : selectedTournament === 'szn6' ? 'OVR CUP SZN VI • June 28th' : selectedTournament === 'szn5' ? 'OVR CUP SZN V • March 1st' : 'OVR CUP SZN IV • February 1st'}</p>
              <form onSubmit={handleTournamentNumPlayersSubmit} className="team-form">
                <input
                  type="number"
                  min="6"
                  max="8"
                  value={tournamentTeamData.numPlayers}
                  onChange={(e) => setTournamentTeamData({ ...tournamentTeamData, numPlayers: e.target.value })}
                  placeholder="Enter number (6-8 players)"
                  className="team-form-input"
                  required
                />
                <button type="submit" className="team-form-btn">
                  NEXT
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Team Name */}
          {tournamentTeamFormStep === 2 && (
            <div className="team-form-step">
              <div className="fuzzy-text-wrapper-form">
                <FuzzyText
                  fontSize={50}
                  fontWeight={900}
                  color="#fff"
                  enableHover={true}
                  baseIntensity={0.05}
                  hoverIntensity={0.15}
                >
                  TEAM NAME?
                </FuzzyText>
              </div>
              <p className="team-form-subtitle-large">{selectedTournament === 'carleton' ? 'OVR × CARLETON • October 25th' : selectedTournament === 'szn6' ? 'OVR CUP SZN VI • June 28th' : selectedTournament === 'szn5' ? 'OVR CUP SZN V • March 1st' : 'OVR CUP SZN IV • February 1st'}</p>
              <form onSubmit={handleTournamentTeamNameSubmit} className="team-form">
                <input
                  type="text"
                  value={tournamentTeamData.teamName}
                  onChange={(e) => setTournamentTeamData({ ...tournamentTeamData, teamName: e.target.value })}
                  placeholder="Enter team name"
                  className="team-form-input"
                  required
                />
                <button type="submit" className="team-form-btn">
                  NEXT
                </button>
              </form>
            </div>
          )}

          {/* Step 3: Team Captain Info */}
          {tournamentTeamFormStep === 3 && (
            <div className="team-form-step">
              <div className="fuzzy-text-wrapper-form">
                <FuzzyText
                  fontSize={50}
                  fontWeight={900}
                  color="#fff"
                  enableHover={true}
                  baseIntensity={0.05}
                  hoverIntensity={0.15}
                >
                  TEAM CAPTAIN
                </FuzzyText>
              </div>
              <p className="team-form-subtitle-large">{selectedTournament === 'carleton' ? 'OVR × CARLETON • October 25th' : selectedTournament === 'szn6' ? 'OVR CUP SZN VI • June 28th' : selectedTournament === 'szn5' ? 'OVR CUP SZN V • March 1st' : 'OVR CUP SZN IV • February 1st'}</p>
              <p className="team-form-subtitle">Team: {tournamentTeamData.teamName}</p>
              <form onSubmit={handleTournamentCaptainSubmit} className="team-form">
                <input
                  type="text"
                  value={tournamentTeamData.captainName}
                  onChange={(e) => setTournamentTeamData({ ...tournamentTeamData, captainName: e.target.value })}
                  placeholder="Captain name"
                  className="team-form-input"
                  required
                />
                <input
                  type="email"
                  value={tournamentTeamData.captainEmail}
                  onChange={(e) => setTournamentTeamData({ ...tournamentTeamData, captainEmail: e.target.value })}
                  placeholder="Captain email"
                  className="team-form-input"
                  required
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  value={tournamentTeamData.phoneNumber}
                  onChange={(e) => {
                    // Allow only numbers and limit to 10 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setTournamentTeamData({ ...tournamentTeamData, phoneNumber: value });
                  }}
                  placeholder="10-digit phone number (e.g., 6135551234)"
                  className="team-form-input"
                  required
                />
                {tournamentTeamData.phoneNumber && tournamentTeamData.phoneNumber.length !== 10 && (
                  <p style={{
                    color: '#ff6b6b',
                    fontSize: '12px',
                    marginTop: '5px',
                    fontFamily: 'Courier New, monospace'
                  }}>
                    Phone number must be exactly 10 digits
                  </p>
                )}
                <button 
                  type="submit" 
                  className="team-form-btn"
                  disabled={tournamentTeamData.phoneNumber.length > 0 && tournamentTeamData.phoneNumber.length !== 10}
                  style={{
                    opacity: (tournamentTeamData.phoneNumber.length > 0 && tournamentTeamData.phoneNumber.length !== 10) ? 0.5 : 1,
                    cursor: (tournamentTeamData.phoneNumber.length > 0 && tournamentTeamData.phoneNumber.length !== 10) ? 'not-allowed' : 'pointer'
                  }}
                >
                  NEXT
                </button>
              </form>
            </div>
          )}

          {/* Step 4: Player Details */}
          {tournamentTeamFormStep === 4 && (
            <div className="team-form-step">
              <div className="fuzzy-text-wrapper-form">
                <FuzzyText
                  fontSize={50}
                  fontWeight={900}
                  color="#fff"
                  enableHover={true}
                  baseIntensity={0.05}
                  hoverIntensity={0.15}
                >
                  PLAYER DETAILS
                </FuzzyText>
              </div>
              <p className="team-form-subtitle-large">{selectedTournament === 'carleton' ? 'OVR × CARLETON • October 25th' : selectedTournament === 'szn6' ? 'OVR CUP SZN VI • June 28th' : selectedTournament === 'szn5' ? 'OVR CUP SZN V • March 1st' : 'OVR CUP SZN IV • February 1st'}</p>
              <p className="team-form-subtitle">Team: {tournamentTeamData.teamName}</p>
              <form onSubmit={handleTournamentFormComplete} className="team-form players-form">
                {tournamentTeamData.players.map((player, index) => (
                  <div key={index} className="player-input-group">
                    <h3 className="player-number">Player {index + 1}</h3>
                    <input
                      type="text"
                      value={player.name || ''}
                      onChange={(e) => handleTournamentPlayerInfoChange(index, 'name', e.target.value)}
                      placeholder="Player name"
                      className="team-form-input player-input"
                      required
                    />
                    <input
                      type="email"
                      value={player.email || ''}
                      onChange={(e) => handleTournamentPlayerInfoChange(index, 'email', e.target.value)}
                      placeholder="Player email"
                      className="team-form-input player-input"
                      required
                    />
                  </div>
                ))}
                <button 
                  type="submit" 
                  className="team-form-btn"
                  style={{
                    pointerEvents: 'auto',
                    zIndex: 100,
                    position: 'relative',
                    minHeight: isMobile ? '48px' : '44px',
                    touchAction: 'manipulation'
                  }}
                >
                  COMPLETE REGISTRATION
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tournament Acknowledgments Page (now handled inside TournamentFlow)
  if (false && showTournamentAcknowledgments) {
    return (
      <div className="app ack-page">
        <div className="ack-laser">
          <LaserFlow
            horizontalBeamOffset={0.0}
            verticalBeamOffset={0.1}
            color="#9D1515"
            wispDensity={0.8}
            wispIntensity={4.0}
            fogIntensity={0.4}
            flowStrength={0.2}
          />
        </div>

        <div
          className="logo-container-small ack-back-button"
          onClick={() => {
            setShowTournamentAcknowledgments(false);
            setShowTournamentTeamForm(true);
          }}
        >
          <img
            src={'/ovr-logo.png'}
            alt="Ottawa Volleyball Revival"
            className="logo-small"
          />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} isAdmin={isAdmin} />

        <div className="ack-wrapper">
          <div className="ack-card">
            <div className="fuzzy-text-wrapper-form">
              <FuzzyText
                fontSize={isMobile ? 28 : 50}
                fontWeight={900}
                color="#fff"
                enableHover={true}
                baseIntensity={0.05}
                hoverIntensity={0.15}
              >
                ACKNOWLEDGMENTS & AGREEMENT
              </FuzzyText>
            </div>
            <p className="ack-subtitle">
              Please read and acknowledge the following before completing your registration:
            </p>

            <form onSubmit={handleTournamentAcknowledgmentsSubmit} className="ack-form">
              <div className="ack-checkbox-card">
                <label className="ack-checkbox">
                  <input
                    type="checkbox"
                    className="ack-checkbox-input"
                    checked={tournamentAcknowledgments.mediaRelease || false}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setTournamentAcknowledgments(prev => ({
                        ...prev,
                        mediaRelease: newValue
                      }));
                      console.log('Media release checkbox:', newValue);
                    }}
                  />
                  <span>
                    <strong>Media Release</strong> – I understand that photos and videos may be taken during the tournament for promotional purposes. By participating, I consent to OVR League using my image and likeness in event recaps, highlight reels, and social media content (including Instagram).
                  </span>
                </label>
              </div>

              <div className="ack-checkbox-card">
                <label className="ack-checkbox">
                  <input
                    type="checkbox"
                    className="ack-checkbox-input"
                    checked={tournamentAcknowledgments.paymentConfirmation || false}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setTournamentAcknowledgments(prev => ({
                        ...prev,
                        paymentConfirmation: newValue
                      }));
                      console.log('Payment confirmation checkbox:', newValue);
                    }}
                  />
                  <span>
                    <strong>Payment & Confirmation</strong> – I understand that my team's spot is not confirmed until full payment is completed via Stripe checkout. Spots are filled on a first-come, first-served basis once payment is received.
                  </span>
                </label>
              </div>

              <p className="ack-note">
                By checking all boxes above, I confirm that I have read and agree to the terms outlined by OVR League.
              </p>

              <button
                type="submit"
                className="team-form-btn ack-submit"
                disabled={!tournamentAcknowledgments.mediaRelease || !tournamentAcknowledgments.paymentConfirmation}
                onClick={(e) => {
                  // Ensure form submission works even if disabled state is incorrect
                  if (!tournamentAcknowledgments.mediaRelease || !tournamentAcknowledgments.paymentConfirmation) {
                    e.preventDefault();
                    setOvrAlert({ title: 'TWO BOXES', message: 'both need a checkmark. we did not write the fine print for fun.' });
                    return false;
                  }
                }}
                style={{
                  pointerEvents: (!tournamentAcknowledgments.mediaRelease || !tournamentAcknowledgments.paymentConfirmation) ? 'none' : 'auto',
                  cursor: (!tournamentAcknowledgments.mediaRelease || !tournamentAcknowledgments.paymentConfirmation) ? 'not-allowed' : 'pointer',
                  zIndex: 100,
                  position: 'relative',
                  minHeight: isMobile ? '48px' : '44px',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'rgba(157, 21, 21, 0.3)'
                }}
              >
                COMPLETE REGISTRATION
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Tournament Thank You Page
  if (false && showTournamentThankYou) {
    return (
      <div className="app" style={{ background: '#000', position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
          <LaserFlow
            horizontalBeamOffset={0.0}
            verticalBeamOffset={0.1}
            color="#9D1515"
            wispDensity={0.8}
            wispIntensity={4.0}
            fogIntensity={0.4}
            flowStrength={0.2}
          />
        </div>

        <div 
          className="logo-container-small"
          onClick={handleLogoClick}
          style={{ cursor: 'pointer', zIndex: 20 }}
        >
          <img
            src={'/ovr-logo.png'}
            alt="Ottawa Volleyball Revival"
            className="logo-small"
          />
        </div>
        <HamburgerMenu onNavigate={handleHamburgerNav} isAdmin={isAdmin} />

        <div className="team-form-container" style={{ paddingTop: '100px', paddingBottom: '60px' }}>
          <div className="team-form-step" style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <div className="fuzzy-text-wrapper-form">
              <FuzzyText
                fontSize={60}
                fontWeight={900}
                color="#fff"
                enableHover={true}
                baseIntensity={0.05}
                hoverIntensity={0.15}
              >
                THANK YOU!
              </FuzzyText>
            </div>

            <div style={{
              background: 'rgba(157, 21, 21, 0.2)',
              border: '2px solid #9D1515',
              borderRadius: '12px',
              padding: '40px',
              marginTop: '40px',
              marginBottom: '30px'
            }}>
              <p style={{
                color: '#fff',
                fontFamily: 'Courier New, monospace',
                fontSize: '20px',
                lineHeight: '1.8',
                marginBottom: '30px'
              }}>
                Your tournament registration has been received!
              </p>

              <p style={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '1.7', marginBottom: '20px' }}>
                Complete payment in your cart to lock in your spot. Spots are filled on a first-come, first-served basis once payment is received.
              </p>
            </div>

            <button
              onClick={handleLogoClick}
              className="team-form-btn"
              style={{ marginTop: '20px' }}
            >
              RETURN TO HOME
            </button>
          </div>
        </div>
      </div>
    );
  }


  // COMMENTED OUT: Email entry page with galaxy background
  // return (
  //   <div className="app">
  //     {galaxyComponent}
  //     
  //     <div className="logo-container-small">
  //       <img 
  //         src={'/ovr-logo.png'} 
  //         alt="Ottawa Volleyball Revival" 
  //         className="logo-small"
  //         onClick={() => {
  //           if (email.trim()) {
  //             handleLogoClick();
  //           }
  //         }}
  //         style={{ cursor: email.trim() ? 'pointer' : 'default' }}
  //       />
  //     </div>
  //     
  //     <div className="email-container">
  //       <h1 className="email-title">ENTER YOUR EMAIL</h1>
  //       <form onSubmit={handleEmailSubmit} className="email-form">
  //         <input
  //           type="email"
  //           value={email}
  //           onChange={(e) => setEmail(e.target.value)}
  //           placeholder="email@example.com"
  //           className="email-input"
  //           required
  //         />
  //         <StarBorder
  //           as="button"
  //           type="submit"
  //           color="#9D1515"
  //           speed="5s"
  //           className="drop-in-star-border"
  //         >
  //           DROP IN
  //         </StarBorder>
  //       </form>
  //     </div>
  //   </div>
  // );

  return null;
}

export default App;
